import { computed, inject, nextTick, onActivated, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { RouteLocationNormalizedLoaded, Router } from 'vue-router'
import type {
  CareOrderListItem,

  CareOrderMetaData,
  CareTemplateMeta,
  OrderCardDTO,
  OrderServiceLineDTO,
  Technician,
} from '@/api/orders'
import {
  parseCareOrderListResponse,
  postCareOrderMeta,
  postCareOrderDetail,
  postCareOrderList,
  careOrderListItemToCardDto,
  mergeCareOrderCardPreservingLocalFields,
  countBaselineTaggedChecklistItems,
  preferredTimesFromCareOrderRow,
  isBookedScheduleDisplayLabel,
  isLikelyCareOrderBusinessNumber,
  readCareOrderNumericId,
  resolveFollowUpRowPrimaryId,
  resolveFollowUpPrimaryIdFromOrderCard,
  resolveMainOrderPrimaryIdFromCard,
  resolveScheduleOrderTargetId,
} from '@/api/orders'
import {
  displayClockToTime24,
  formatServiceDateYmd,
  gridSlotToTimeStart,
  postCareScheduleAssign,
} from '@/api/CareScheduleAssign'
import { clearPersistedScheduleForOrder } from '@/utils/confirmedOrderSchedule'
import request from '@/api/request'
import {
  currentScheduleFromContextTarget,
  deriveCurrentScheduleFromInspectorOrders,
  extractBusyInspectorIdsForDate,
  parseCareScheduleContextResponse,
  type ParsedCareScheduleContext,
  type CareScheduleCurrentSchedule,
} from '@/api/CareScheduleContext'
import {
  type ParsedCareScheduleAvailability,
} from '@/api/CareScheduleAvailability'
import { postCareScheduleUnassign } from '@/api/CareScheduleUnassign'
import { classifyCareOrderCardBucket, classifyCareSidebarSubBucket } from '@/api/hmCareOrderTypes'
import {
  isFollowUpCareLine,
  isPendingAssignmentLabel,
  lineHasConfirmedScheduleTime,
  normalizeOrderCardForDisplay,
  orderCardIsMainPendingSchedule,
  orderCardIsFuPendingSchedule,
  orderHasSchedulableFollowUpLine,
  orderPreferredTimesList,
  orderStatusIsFollowUpContext,
  preserveMainLineOnMergedFollowUpCard,
  promoteBookedScheduleOnPendingCard,
} from '@/api/careOrderLineDisplay'
import {
  buildScheduleDurationLabelForOrder,
  clockRangeFromScheduleDisplayForDate,
  formatScheduleDurationLabel,
  formatOrderScheduleLineDisplay,
  formatServiceTimeRangeLine,
} from '@/utils/orderScheduleDisplay'
import {
  applyTechnicianRefsToOrderCard,
  patchOrderCardTechnicianRefs,
  stripTechnicianNoPrefix,
  technicianNoForAssignmentName,
  technicianRefFromTechnicianNo,
  type TechnicianNoLookup,
} from '@/utils/technicianNoFromList'
import {
  getScheduleInspectorData,
} from '@/utils/scheduleInspectorCache'
import {
  fetchCareOrderListAllPages,
  invalidateCareOrderListCache,
} from '@/utils/careOrderListCache'
import imgInspectorChevron from '@/assets/三角形备份 5@2x.png'
import imgInspectorChevronBlack from '@/assets/三角形备份 5.black@2x.png'
import { TOAST_DURATION } from '@/constants/toast'
import type {
  CareScheduleFocusPayload,
  InspectorAssignment,
  InspectorRow,
  InspectorRowView,
  OrderScheduleAssignment,
  RescheduleEntrySnapshot,
  ScheduleContextInspectorOrder,
  SlotVisualState,
  UnscheduledItem,
  UserScheduleSelection,
  WeekDateView,
} from '../types'
import {
  CARE_SCHEDULE_ASSIGNMENTS_KEY,
  CARE_SCHEDULE_CLEARED_KEY,
  TIME_SLOT_GRID,
} from '../constants'
import { inspectorVisualsForIndex } from '../utils/scheduleInspectorRows'
import { contextRowEligibleForInspectorSidebar as evaluateContextSidebarRow, inspectorSidebarAssignmentClickable, inspectorSidebarBlocksNonFocusOrder } from '../utils/scheduleInspectorContextSidebar'
import {
  isDateInWeekRange,
  isSameCalendarDay,
  parseServiceCycleDate,
  resolveServiceCycleDateFromOrderCard,
  startOfWeekSunday,
  ymdFromDate,
  findFirstGreenOrBlueDateInWeek,
  findFirstDateWithPickableSlotFromToday,
} from '../utils/scheduleCalendar'
import { useCareScheduleFocus } from './useCareScheduleFocus'
import { useCareScheduleOrders } from './useCareScheduleOrders'
import { useCareScheduleDataSync } from './useCareScheduleDataSync'
import { useCareScheduleUnscheduled } from './useCareScheduleUnscheduled'
import {
  dedupeTechniciansByNameAndStaffNo,
  type ScheduleTechnician,
} from './useCareScheduleInspectors'
import { useCareScheduleReschedule } from './useCareScheduleReschedule'
import type { ScheduleBookingExclude } from '../types'


export interface CareScheduleOrchestrationProps {
  embedded?: boolean
  initialFocus?: import('../types').CareScheduleFocusPayload | null
  /** 列表页删除订单等外部事件触发 context 重拉 */
  externalRefreshNonce?: number
}

export interface CareScheduleOrchestrationEmit {
  (e: 'close', list?: import('@/api/orders').OrderCardDTO[]): void
  (e: 'orders-updated', list: import('@/api/orders').OrderCardDTO[]): void
}

export function useCareScheduleOrchestration(
  props: CareScheduleOrchestrationProps,
  emit: CareScheduleOrchestrationEmit,
  route: RouteLocationNormalizedLoaded,
  router: Router,
) {

/** 全局 toast（IndexPage/App.vue provide）：弹框关闭后提示仍可见 */
const showGlobalToast = inject<((type: 'success' | 'warning' | 'error', message: string) => void) | null>(
  'showToast',
  null,
)

// Shared session state
const selectedOrderId = ref('')
const unscheduledListRef = ref<UnscheduledItem[]>([])
let applyingRescheduleContextDepth = 0
const inspectorAvailability = ref<ParsedCareScheduleAvailability | null>(null)
/**
 * 预取 context 后：日期 → 当日有任务的检查员 id 列表。
 * week-date-card 颜色按当前选中 Inspectors 用户是否在此列表中判断。
 */
const scheduleContextBusyInspectorsByDate = ref<Record<string, string[]>>({})
const selectedDate = ref<Date | null>(null)
const selectedSlots = ref<Set<string>>(new Set())
const weekAnchor = ref(new Date())
const inspectors = ref<InspectorRow[]>([])
const selectedInspectorId = ref('')
const expandedInspectorIds = ref<Set<string>>(new Set())
const inspectorSidebarSelectedOrderId = ref('')
const inspectorSidebarSelectedLineIndex = ref<number | null>(null)
const scheduleDateLockReleased = ref(false)
const scheduleClearedByUser = ref<Set<string>>(new Set())
const rescheduleUserPickedSlots = ref(false)
const rescheduleEntrySnapshot = ref<RescheduleEntrySnapshot | null>(null)
const scheduleContextCurrentSchedule = ref<CareScheduleCurrentSchedule | null>(null)
const assignmentsByOrderId = ref<Record<string, OrderScheduleAssignment>>({})
const assignedOrdersCache = ref<OrderCardDTO[]>([])
const scheduleContextInspectorOrders = ref<ScheduleContextInspectorOrder[]>([])
const scheduleContextUnscheduledInspections = ref<OrderCardDTO[]>([])
let reloadScheduleContextForUnscheduledList: (focus: CareScheduleFocusPayload) => Promise<void> =
  async () => {}
/** 打开弹层初始化中：挡住 weekAnchor → context 连打 */
let scheduleOpenInitializing = false
/** 弹框首屏：context / inspectors 等接口未完成前展示 loading */
const schedulePageLoading = ref(true)
/** 切换日期重拉 context 时 Inspectors 侧栏 loading */
const inspectorSidebarLoading = ref(false)
let inspectorSidebarLoadSeq = 0
/** 最近一次成功 context 请求的 week_start（选中日期 YYYY-MM-DD） */
let lastLoadedContextWeekStartYmd = ''
/** 按周预取 week-date-card 颜色的序号（切周时丢弃过期结果） */
let weekDateTonePrefetchSeq = 0

function contextWeekStartYmd(): string {
  // 有选中日用选中日；打开首屏 selectedDate 尚未赋值时用今天（勿回退 weekAnchor，它常被设成当周周日）
  const date = selectedDate.value ?? new Date()
  return ymdFromDate(new Date(date.getFullYear(), date.getMonth(), date.getDate()))
}

/** 当前周内未过期日期（过去日不请求 context） */
function availableWeekDatesForContextPrefetch(): Date[] {
  return weekDates.value.filter((d) => !isPastCalendarDay(d))
}

/** 切周预取 context：相邻请求间隔，避免一次性打满 7 次 */
const WEEK_DATE_CONTEXT_PREFETCH_GAP_MS = 280

function delayMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * 按周预拉可用日期的 context，记录每日有任务的检查员。
 * week-date-card 颜色随选中 Inspectors 用户变化（有任务 → 蓝）。
 * 整周最多 7 次；已过去的日期跳过；请求之间加短延迟。
 */
async function prefetchWeekDateCardTones(focus: CareScheduleFocusPayload): Promise<void> {
  if (!focus.orderId?.trim()) return
  const days = availableWeekDatesForContextPrefetch()
  if (!days.length) return
  const seq = ++weekDateTonePrefetchSeq

  // 先清掉本周旧色，再按日错开回填
  {
    const cleared = { ...scheduleContextBusyInspectorsByDate.value }
    for (const d of weekDates.value) {
      delete cleared[ymdFromDate(d)]
    }
    scheduleContextBusyInspectorsByDate.value = cleared
  }

  for (let i = 0; i < days.length; i++) {
    if (seq !== weekDateTonePrefetchSeq) return
    if (i > 0) {
      await delayMs(WEEK_DATE_CONTEXT_PREFETCH_GAP_MS)
      if (seq !== weekDateTonePrefetchSeq) return
    }
    const ymd = ymdFromDate(days[i]!)
    const item = await dataSyncApi.fetchScheduleContextByWeekStart(focus, ymd)
    if (seq !== weekDateTonePrefetchSeq) return
    scheduleContextBusyInspectorsByDate.value = {
      ...scheduleContextBusyInspectorsByDate.value,
      [ymd]: item?.rawData ? extractBusyInspectorIdsForDate(item.rawData, ymd) : [],
    }
  }
}
/** 同 orderPrimaryId 的 detail：in-flight 去重 + 弹层内缓存，打开只打一次 */
const orderDetailByPrimaryId = new Map<number, CareOrderListItem>()
const orderDetailInflight = new Map<number, Promise<CareOrderListItem>>()

async function fetchOrderDetailOnce(orderPrimaryId: number): Promise<CareOrderListItem> {
  const key = Math.trunc(orderPrimaryId)
  const cached = orderDetailByPrimaryId.get(key)
  if (cached) return cached
  const inflight = orderDetailInflight.get(key)
  if (inflight) return inflight
  const req = postCareOrderDetail(key)
    .then((detail) => {
      orderDetailByPrimaryId.set(key, detail)
      orderDetailInflight.delete(key)
      return detail
    })
    .catch((err) => {
      orderDetailInflight.delete(key)
      throw err
    })
  orderDetailInflight.set(key, req)
  return req
}

const showCancelScheduleModal = ref(false)
const showAssignScheduleModal = ref(false)
const assignPendingSelection = ref<UserScheduleSelection | null>(null)
const assignSubmitting = ref(false)
const cancelScheduleSubmitting = ref(false)
const toast = ref({
  visible: false,
  type: 'success' as 'success' | 'warning' | 'error',
  message: '',
})
let toastTimer: ReturnType<typeof setTimeout> | null = null

let findOrderBridge: (orderId: string) => OrderCardDTO | undefined = () => undefined
let rescheduleLineIndexBridge: (orderId: string) => number = () => 0

const {
  focusPayload,
  isRescheduleMode,
  isCustomerRequestedReschedule,
  showRescheduleMetaPanel,
  resolveInitialFocus,
} = useCareScheduleFocus({
  props,
  route,
  selectedOrderId,
  findOrder: (id) => findOrderBridge(id),
  rescheduleLineIndexForOrder: (id) => rescheduleLineIndexBridge(id),
})

const ordersApi = useCareScheduleOrders({
  focusPayload,
  scheduleClearedByUser,
  unscheduled: unscheduledListRef,
  assignedOrdersCache,
  lineIndexForScheduleCard: (o, focusOrderId, focusLineIndex) =>
    lineIndexForScheduleCard(o, focusOrderId, focusLineIndex),
  ymdFromDate,
  parseServiceCycleDate,
  resolveServiceCycleDateFromOrderCard,
  scheduleTargetLineIndexForOrder: (orderId) => scheduleTargetLineIndexForOrder(orderId),
})
const {
  ordersById,
  serviceCycleByOrderId,
  careMeta,
  durationLabelsByOrderId,
  planLabelsByOrderId,
  technicianNoLookup,
  findOrder,
  registerOrderCards,
  mergeOrderCardFromBackend,
  careOrderCardsSameOrder,
  careOrderIdsReferToSameOrder,
  resolveCanonicalOrderId,
  isOrderScheduleCleared,
  upsertFocusOrderList,
  upsertLocalFocusOrderCard,
  purgeDuplicateOrdersById,
  durationLabelForOrderId,
  planDisplayNameForOrder,
  cachePlanLabelFromDetail,
  resolvePlanDisplayName,
  serviceCycleYmdForOrderId,
  upsertAssignedOrdersCache,
  purgeOrderFromAssignedCaches,
} = ordersApi
findOrderBridge = findOrder

const unscheduledApi = useCareScheduleUnscheduled(
  focusPayload,
  {
    findOrder,
    registerOrderCards,
    careOrderIdsReferToSameOrder,
    durationLabelForOrderId,
    durationLabelForTemplateId,
    lineIndexForScheduleCard,
    isFollowUpScheduleFocusSession,
    isRescheduleFollowUpFocusSession,
    isOrderEligibleForReschedule,
    orderMatchesRescheduleSession,
    isOrderInUnscheduledPool,
    schedulePoolLineIndex,
    orderCardBelongsToFuPendingSchedulePool,
    orderFuScheduleLineAwaitingAssignment,
    schedulePoolOrderIsMainPendingSchedule,
    followUpScheduleLineIndex,
    lineIndexForRescheduleCard,
    technicianNoLookup,
    assignmentsByOrderId,
    scheduleClearedByUser,
    isOrderScheduleCleared,
    selectedOrderId,
    clearInspectorSidebarSelection: () => clearInspectorSidebarSelection(),
    selectUnscheduled: (item) => selectUnscheduled(item),
    reloadScheduleContext: (focus) => reloadScheduleContextForUnscheduledList(focus),
  },
  unscheduledListRef,
)

const {
  unscheduled,
  cardFromOrder,
  finalizeUnscheduledItems,
  ensureFocusOrderSelected,
  applyScheduleUnscheduledFromContextOrders,
  loadUnscheduledList,
  refreshUnscheduledFromFocusOrderList,
  rebuildUnscheduledPoolFromFocus,
} = unscheduledApi

const weekDates = computed(() => {
  const start = startOfWeekSunday(weekAnchor.value)
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d
  })
})

let dataSyncApi!: ReturnType<typeof useCareScheduleDataSync>

function initDataSyncComposable() {
  dataSyncApi = useCareScheduleDataSync({
    focusPayload,
    weekAnchor,
    selectedInspectorId,
    selectedOrderId,
    inspectorAvailability,
    findOrder,
    resolveContextTargetForFocus,
    applyParsedScheduleContext,
    lineIndexForOrder,
    isFollowUpScheduleFocusSession,
    followUpScheduleLineIndex,
    technicianIdForAvailabilityApi,
    contextWeekStartYmd,
  })
}

function loadScheduleContext(
  focus: CareScheduleFocusPayload,
  opts?: { assignmentsOnly?: boolean; skipDayStatusUpdate?: boolean },
): Promise<boolean> {
  return dataSyncApi.loadScheduleContext(focus, opts).then((ok) => {
    if (ok) {
      lastLoadedContextWeekStartYmd = contextWeekStartYmd()
    }
    return ok
  })
}
reloadScheduleContextForUnscheduledList = async (focus) => {
  await loadScheduleContext(focus)
}
function loadInspectorAvailability(): Promise<void> {
  return dataSyncApi.loadInspectorAvailability()
}
function refreshSchedulePageData(opts?: {
  assignmentsOnly?: boolean
  skipContext?: boolean
  skipAvailability?: boolean
  skipDayStatusUpdate?: boolean
}): Promise<void> {
  const dayAtRequest = contextWeekStartYmd()
  return dataSyncApi.refreshSchedulePageData(opts).then(() => {
    // refresh 走 dataSync 内部 load，不经过上方 wrapper；成功路径后对齐 week_start 标记
    if (!opts?.skipContext) {
      lastLoadedContextWeekStartYmd = dayAtRequest
    }
  })
}
function refreshSchedulePageDataNow(opts?: {
  assignmentsOnly?: boolean
  skipContext?: boolean
  skipAvailability?: boolean
  skipDayStatusUpdate?: boolean
}): void {
  void refreshSchedulePageData(opts)
}

let captureRescheduleEntrySnapshot!: (orderId: string, opts?: { force?: boolean }) => void
let clearRescheduleEntrySnapshot!: () => void
let applyRescheduleOrderContext!: (orderId: string) => void
let applyRescheduleCalendarAnchor!: () => void

function initLateComposables() {
  initDataSyncComposable()
  const rescheduleApi = useCareScheduleReschedule({
    isRescheduleMode,
    isCustomerRequestedReschedule,
    rescheduleEntrySnapshot,
    findOrder,
    originalScheduledPartsForOrder,
    applyBookedOrderContext,
    navigateCalendarToDate,
    rescheduleAnchorDateForOrder,
    clearSelectedSlots,
    rebuildInspectorAssignmentRows: () => rebuildInspectorAssignmentRows(),
    rescheduleUserPickedSlots,
    selectedOrderId,
  })
  captureRescheduleEntrySnapshot = rescheduleApi.captureRescheduleEntrySnapshot
  clearRescheduleEntrySnapshot = rescheduleApi.clearRescheduleEntrySnapshot
  applyRescheduleOrderContext = rescheduleApi.applyRescheduleOrderContext
  applyRescheduleCalendarAnchor = rescheduleApi.applyRescheduleCalendarAnchor
}

initLateComposables()

function inspectorRowById(inspectorId: string): InspectorRow | undefined {
  const key = String(inspectorId)
  return inspectors.value.find((ins) => (ins.linkedIds ?? [ins.id]).some((id) => String(id) === key))
}

function isSameInspectorId(a: string, b: string): boolean {
  const rowA = inspectorRowById(a)
  const rowB = inspectorRowById(b)
  if (!rowA || !rowB) return String(a) === String(b)
  return rowA.id === rowB.id
}

function scheduleCardForOrder(orderId: string): UnscheduledItem | null {
  const hit = unscheduled.value.find((x) => x.orderId === orderId)
  if (hit) {
    const cachedDuration = (durationLabelsByOrderId.value[orderId] ?? '').trim()
    if (hit.durationLabel.trim() || !cachedDuration) return hit
    // context 重建左栏时常丢掉 durationLabel；标题回退缓存，避免 (Jun-Aug: …) 闪一下消失
    return { ...hit, durationLabel: cachedDuration }
  }
  const o = findOrder(orderId)
  if (!o) return null
  const focus = focusPayload.value
  const lineIndex = focus
    ? lineIndexForScheduleCard(o, focus.orderId, focus.lineIndex)
    : lineIndexForOrder(orderId)
  return cardFromOrder(o, lineIndex, undefined, durationLabelForOrderId(orderId))
}

function serviceCycleDateForOrder(orderId: string): Date | null {
  if (!orderId) return null
  const cachedYmd = serviceCycleByOrderId.value[orderId]
  if (cachedYmd) {
    const d = parseServiceCycleDate(cachedYmd)
    if (d) return d
  }
  const item = unscheduled.value.find((x) => x.orderId === orderId)
  if (item?.serviceCycleYmd) {
    const d = parseServiceCycleDate(item.serviceCycleYmd)
    if (d) return d
  }
  const focus = focusPayload.value
  if (focus?.orderId === orderId && focus.serviceCycleDate?.trim()) {
    const d = parseServiceCycleDate(focus.serviceCycleDate)
    if (d) return d
  }
  const o = findOrder(orderId)
  if (!o) return null
  const d = resolveServiceCycleDateFromOrderCard(o, scheduleTargetLineIndexForOrder(orderId))
  if (d) {
    serviceCycleByOrderId.value = { ...serviceCycleByOrderId.value, [orderId]: ymdFromDate(d) }
  }
  return d
}

const allowedScheduleDate = computed(() => serviceCycleDateForOrder(selectedOrderId.value))

/** 已废弃整页锁灰：非周期日仅通过 disabledSlots 标灰占用时段 */
const timeSlotsLockedOutsideCycleDay = computed(() => false)

function isDateSelectable(d: Date): boolean {
  return !isPastCalendarDay(d)
}

function navigateCalendarToDate(d: Date) {
  weekAnchor.value = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  selectedDate.value = new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

function originalAppointmentDateForOrder(orderId: string): Date | null {
  const assignment = assignmentForOrderId(orderId)
  if (assignment) return assignment.date
  const ctxRow = contextInspectorOrderRowFor(orderId)
  if (ctxRow) {
    const fromCtx = parseServiceCycleDate(ctxRow.dateYmd)
    if (fromCtx) return fromCtx
  }
  const o = findOrder(orderId)
  if (!o) return null
  const line = o.services[scheduleTargetLineIndexForOrder(orderId)]
  const fromLine = dateFromScheduleDisplay(line?.scheduledAt ?? '')
  if (fromLine) return fromLine
  for (const pt of orderPreferredTimesList(o)) {
    const d = dateFromScheduleDisplay(pt)
    if (d) return d
  }
  const cycle = serviceCycleDateForOrder(orderId)
  if (cycle) return cycle
  return null
}

/** 改期日历锚点：原预约日 → 首选时间 → 服务周期日 → 今天 */
function rescheduleAnchorDateForOrder(orderId: string): Date {
  return originalAppointmentDateForOrder(orderId) ?? new Date()
}

function inspectorNameForContextTechnicianId(technicianId: number | undefined): string {
  if (technicianId == null || technicianId <= 0) return ''
  const hit = inspectors.value.find((i) => technicianIdForAvailabilityApi(i.id) === technicianId)
  return hit?.name?.trim() ?? ''
}

function scheduleTimeLabelFromContext(cs: CareScheduleCurrentSchedule): string {
  if (cs.timeText?.trim()) {
    const formatted = formatOrderScheduleLineDisplay(cs.timeText.trim())
    return formatted !== '—' ? formatted : cs.timeText.trim()
  }
  if (cs.date && cs.timeStart && cs.timeEnd) {
    const line = formatServiceTimeRangeLine(cs.timeStart, cs.timeEnd, cs.date)
    const formatted = formatOrderScheduleLineDisplay(line)
    return formatted !== '—' ? formatted : line
  }
  return ''
}

function contextScheduleMatchesInspector(cs: CareScheduleCurrentSchedule, inspectorId: string): boolean {
  if (cs.technicianId == null || cs.technicianId <= 0) return true
  const techId = technicianIdForAvailabilityApi(inspectorId)
  return techId > 0 && techId === cs.technicianId
}

function gridClockPartsFromTime24(raw: string): { hm: string; ap: string } | null {
  const m = raw.trim().match(/^(\d{1,2}):(\d{2})/)
  if (!m?.[1] || !m[2]) return null
  const h24 = parseInt(m[1], 10)
  const mm = m[2]
  const ap = h24 >= 12 ? 'PM' : 'AM'
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return { hm: `${h12}:${mm}`, ap }
}

/** 从 context `current_schedule` 解析起止钟点（支持 time_start/end 或 time_text） */
function resolveContextScheduleClockRange(
  cs: CareScheduleCurrentSchedule,
): { startHm: string; startAp: string; endHm: string; endAp: string } | null {
  if (cs.timeStart?.trim() && cs.timeEnd?.trim()) {
    const start = gridClockPartsFromTime24(cs.timeStart)
    const end = gridClockPartsFromTime24(cs.timeEnd)
    if (start && end) {
      return { startHm: start.hm, startAp: start.ap, endHm: end.hm, endAp: end.ap }
    }
  }
  const text = cs.timeText?.trim()
  if (!text) return null
  const anchor = cs.date?.trim() ? parseServiceCycleDate(cs.date) : null
  if (anchor) {
    const scoped = clockRangeFromScheduleDisplayForDate(formatOrderScheduleLineDisplay(text), anchor)
    if (scoped) return scoped
    const scopedRaw = clockRangeFromScheduleDisplayForDate(text, anchor)
    if (scopedRaw) return scopedRaw
  }
  const ymdRange = text.match(
    /^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)$/i,
  )
  if (ymdRange?.[2] && ymdRange[3] && ymdRange[4] && ymdRange[5]) {
    return {
      startHm: ymdRange[2],
      startAp: ymdRange[3].toUpperCase(),
      endHm: ymdRange[4],
      endAp: ymdRange[5].toUpperCase(),
    }
  }
  return null
}

function currentScheduleFromBookedDisplayLabel(
  label: string,
  technicianId?: number,
): CareScheduleCurrentSchedule | null {
  const trimmed = label.trim()
  if (!trimmed) return null
  const formatted = formatOrderScheduleLineDisplay(trimmed)
  const date = dateFromScheduleDisplay(formatted)
  if (!date) return null
  const scoped = clockRangeFromScheduleDisplayForDate(formatted, date)
  if (!scoped) return null
  const timeStart = displayClockToTime24(`${scoped.startHm} ${scoped.startAp}`)
  const timeEnd = displayClockToTime24(`${scoped.endHm} ${scoped.endAp}`)
  return {
    technicianId,
    date: formatServiceDateYmd(date),
    timeStart,
    timeEnd,
    timeText: formatted !== '—' ? formatted : trimmed,
  }
}

/** Reschedule 原预约：后端订单行已确认时段 */
function rescheduleOriginalBookedScheduleLabelForLine(
  o: OrderCardDTO,
  lineIndex: number,
): string | null {
  return backendBookedScheduleLabelForLine(o, lineIndex)
}

/** 改期原预约浅蓝格：仅用 context，不走订单卡 scheduledAt */
function gridSlotsFromCurrentSchedule(
  cs: CareScheduleCurrentSchedule,
  anchorDate: Date,
): string[] {
  const dateYmd = cs.date?.trim()
  if (!dateYmd) return []
  const csDate = parseServiceCycleDate(dateYmd)
  if (!csDate || !isSameCalendarDay(csDate, anchorDate)) return []

  const range = resolveContextScheduleClockRange(cs)
  if (!range) return []

  return slotsInClockRangeForDisplay(range)
}

function buildAssignmentFromCurrentSchedule(
  cs: CareScheduleCurrentSchedule,
  orderId: string,
  lineIndex: number,
  inspectorId: string,
  opts?: { requireInspectorMatch?: boolean },
): OrderScheduleAssignment | null {
  const dateYmd = cs.date?.trim()
  if (!dateYmd) return null
  const date = parseServiceCycleDate(dateYmd)
  if (!date) return null
  if (opts?.requireInspectorMatch !== false && !contextScheduleMatchesInspector(cs, inspectorId)) {
    return null
  }

  const timeLabel = scheduleTimeLabelFromContext(cs)
  if (!timeLabel) return null

  const slot = slotFromScheduleDisplay(timeLabel)
  if (!slot) return null

  return {
    orderId,
    lineIndex,
    inspectorId,
    date,
    slot,
    timeLabel,
  }
}

/** 改期会话：优先 focus.lineIndex（主单+跟进行合并卡）；FU Reschedule 池固定跟进行 */
function rescheduleLineIndexForOrder(orderId: string): number {
  const focus = focusPayload.value
  const o = findOrder(orderId)
  if (focus?.mode === 'reschedule' && o && isRescheduleFollowUpFocusSession(focus)) {
    return lineIndexForRescheduleCard(o)
  }
  if (
    focus?.mode === 'reschedule' &&
    focus.orderId &&
    careOrderIdsReferToSameOrder(focus.orderId, orderId)
  ) {
    return focus.lineIndex
  }
  return lineIndexForOrder(orderId)
}
rescheduleLineIndexBridge = rescheduleLineIndexForOrder

/** Schedule / Reschedule 当前操作的服务行（合并卡跟进行改期时不读主单行） */
function scheduleTargetLineIndexForOrder(orderId: string): number {
  if (isRescheduleMode.value) return rescheduleLineIndexForOrder(orderId)
  return lineIndexForOrder(orderId)
}

/** 占用/冲突 exclude：Follow-up 排程会话以 focus.lineIndex 为准，避免推断行 index 偏差 */
function scheduleConflictExcludeLineIndex(orderId: string): number {
  const focus = focusPayload.value
  if (focus && focus.mode !== 'reschedule' && focus.orderId === orderId) {
    if (isFollowUpScheduleSessionForOrder(orderId)) {
      const o = findOrder(orderId) ?? focus.orderList?.find((x) => x.id === orderId)
      if (o) {
        const fuIdx = followUpScheduleLineIndex(o)
        if (isFollowUpCareLine(o.services[fuIdx]!)) return fuIdx
      }
    }
    const o = findOrder(orderId) ?? focus.orderList?.find((x) => x.id === orderId)
    const focusLine = o?.services[focus.lineIndex]
    if (focusLine && isFollowUpCareLine(focusLine)) return focus.lineIndex
  }
  return scheduleTargetLineIndexForOrder(orderId)
}

function readScheduledPartsFromLine(
  line: OrderServiceLineDTO | undefined,
  order?: OrderCardDTO,
): { time: string; inspector: string } {
  if (!line || !lineHasConfirmedScheduleTime(line, order)) return { time: '', inspector: '' }
  const sched = (line.scheduledAt ?? '').trim()
  if (!sched || sched === '—') return { time: '', inspector: '' }
  const time = formatOrderScheduleLineDisplay(sched)
  const assignName = (line.assignment ?? '').trim()
  const inspector = assignName && !/pending\s+assignment/i.test(assignName) ? assignName : ''
  if (time === '—') return { time: '', inspector: '' }
  return { time, inspector }
}

function rescheduleOriginalAssignmentForPicker(
  orderId: string,
  lineIndex: number,
  inspectorId: string,
  date: Date,
): OrderScheduleAssignment | null {
  const o = findOrder(orderId)
  const backendLabel = o ? backendBookedScheduleLabelForLine(o, lineIndex) : null
  if (backendLabel) {
    const lineDate = dateFromScheduleDisplay(backendLabel)
    if (lineDate && isSameCalendarDay(lineDate, date)) {
      const slot = slotFromScheduleDisplay(backendLabel)
      if (slot) {
        return {
          orderId,
          lineIndex,
          inspectorId,
          date: lineDate,
          slot,
          timeLabel: backendLabel,
        }
      }
    }
  }

  const cs = scheduleContextCurrentSchedule.value
  if (cs) {
    const csDate = cs.date ? parseServiceCycleDate(cs.date) : null
    if (csDate && isSameCalendarDay(csDate, date)) {
      const fromCtx = buildAssignmentFromCurrentSchedule(cs, orderId, lineIndex, inspectorId, {
        requireInspectorMatch: false,
      })
      if (fromCtx) return fromCtx
    }
  }

  const line = o?.services[lineIndex]
  const label = o ? rescheduleOriginalBookedScheduleLabelForLine(o, lineIndex) : null
  if (!label || !line || !lineHasConfirmedScheduleTime(line, o)) return null
  const lineDate = dateFromScheduleDisplay(label)
  if (!lineDate || !isSameCalendarDay(lineDate, date)) return null
  const slot = slotFromScheduleDisplay(label)
  if (!slot) return null

  return {
    orderId,
    lineIndex,
    inspectorId,
    date: lineDate,
    slot,
    timeLabel: label,
  }
}

function originalScheduledPartsFromContext(): { time: string; inspector: string } | null {
  const cs = scheduleContextCurrentSchedule.value
  if (!cs) return null
  const time = scheduleTimeLabelFromContext(cs)
  if (!time) return null
  const inspector =
    inspectorNameForContextTechnicianId(cs.technicianId) ||
    lineAssignmentNameForOrder(selectedOrderId.value ?? '') ||
    ''
  return { time, inspector }
}

function originalScheduledPartsForOrder(orderId: string): { time: string; inspector: string } {
  const lineIdx = scheduleTargetLineIndexForOrder(orderId)
  const o = findOrder(orderId)
  const line = o?.services[lineIdx]

  const backend = o ? backendBookedScheduleLabelForLine(o, lineIdx) : null
  if (backend) {
    const assignName = (line?.assignment ?? '').trim()
    const inspector =
      assignName && !/pending\s+assignment/i.test(assignName)
        ? assignName
        : inspectorRowById(assignmentsByOrderId.value[orderId]?.inspectorId ?? '')?.name?.trim() ||
          ''
    return { time: backend, inspector }
  }

  const fromLine = readScheduledPartsFromLine(line, o ?? undefined)
  if (fromLine.time) {
    return {
      time: fromLine.time,
      inspector:
        fromLine.inspector ||
        inspectorRowById(assignmentsByOrderId.value[orderId]?.inspectorId ?? '')?.name?.trim() ||
        '',
    }
  }

  if (isRescheduleMode.value && selectedOrderId.value === orderId) {
    const fromCtx = originalScheduledPartsFromContext()
    if (fromCtx?.time) return fromCtx
  }

  const assignment = assignmentsByOrderId.value[orderId]
  if (assignment && assignment.lineIndex === lineIdx) {
    return {
      time: assignment.timeLabel?.trim() || '',
      inspector:
        inspectorRowById(assignment.inspectorId)?.name?.trim() ||
        lineAssignmentNameForOrder(orderId) ||
        '',
    }
  }
  return { time: '', inspector: '' }
}

function originalScheduledTimeDisplayForOrder(orderId: string): string {
  const { time, inspector } = originalScheduledPartsForOrder(orderId)
  if (time && inspector) return `${time} · ${inspector}`
  return time || inspector
}



/** 改期进入时：订单仍有预约但残留 Unassign 标记，则清掉标记以便必须先 Unassign */
function clearStaleScheduleClearedIfOrderStillBooked(orderId: string) {
  if (!isOrderScheduleCleared(orderId)) return
  const o = findOrder(orderId)
  const line = o?.services[scheduleTargetLineIndexForOrder(orderId)]
  const stillBooked =
    !!assignmentsByOrderId.value[orderId] ||
    (line != null && orderLineHasBookedSchedule(line, o ?? undefined))
  if (!stillBooked) return
  const target = o ?? ({ id: orderId } as OrderCardDTO)
  const next = new Set(scheduleClearedByUser.value)
  for (const clearedId of [...next]) {
    const cleared = findOrder(clearedId) ?? ({ id: clearedId } as OrderCardDTO)
    if (careOrderCardsSameOrder(cleared, target)) next.delete(clearedId)
  }
  scheduleClearedByUser.value = next
  persistScheduleClearedByUser()
}

/** Schedule 左栏待排：订单级 Pending Schedule / FU Pending Schedule 且无指派时，不算已排期 */
function schedulePendingPoolOrderTreatAsUnbooked(orderId: string, lineIndexOverride?: number): boolean {
  if (isRescheduleMode.value) return false
  const o = findOrder(orderId)
  if (!o) return false
  const lineIndex = lineIndexOverride ?? scheduleTargetLineIndexForOrder(orderId)
  const line = o.services[lineIndex]
  if (!line) return false
  if (!isFollowUpCareLine(line) && orderCardIsMainPendingSchedule(o)) {
    return isLineUnassignedForSchedulePool(line, o)
  }
  if (isFollowUpCareLine(line) && orderCardIsFuPendingSchedule(o)) {
    return isLineUnassignedForSchedulePool(line, o)
  }
  return false
}

/** 当前单是否仍有已确认排期（须先 Unassign 才能改时间）；Schedule / Reschedule 共用 */
function orderHasActiveBookedAssignment(orderId: string, lineIndexOverride?: number): boolean {
  if (!orderId.trim() || isOrderScheduleCleared(orderId)) return false
  if (schedulePendingPoolOrderTreatAsUnbooked(orderId, lineIndexOverride)) return false
  const o = findOrder(orderId)
  const lineIndex =
    lineIndexOverride ??
    (isRescheduleMode.value
      ? rescheduleLineIndexForOrder(orderId)
      : scheduleTargetLineIndexForOrder(orderId))
  const line = o?.services[lineIndex]
  const hasBookedLine = line != null && orderLineHasBookedSchedule(line, o ?? undefined)
  if (hasBookedLine) return true
  if (!isRescheduleMode.value) {
    const fuSession = isFollowUpScheduleSessionForOrder(orderId)
    const ctxBooked = scheduleContextInspectorOrders.value.some((row) => {
      if (!careOrderIdsReferToSameOrder(row.orderId, orderId)) return false
      if (lineIndexOverride != null && o) {
        const targetLine = o.services[lineIndex]
        const isFuLine = !!targetLine && isFollowUpCareLine(targetLine)
        if (isFuLine && row.targetType !== 'followup') return false
        if (!isFuLine && row.targetType === 'followup') return false
      } else if (fuSession && row.targetType !== 'followup') {
        return false
      }
      return !!scheduleLabelFromContextInspectorOrder(row)
    })
    if (ctxBooked) return true
  }
  if (isRescheduleMode.value) {
    if (assignmentsByOrderId.value[orderId]) return true
    const { time } = originalScheduledPartsForOrder(orderId)
    return !!time
  }
  return false
}

function hasActiveBookedAssignment(): boolean {
  const orderId = selectedOrderId.value
  if (!orderId) return false
  if (fuPageInspectorSidebarViewOnlyMode()) return false
  if (fuPageInspectorSidebarEditableActive()) {
    return orderHasActiveBookedAssignment(orderId, effectiveSidebarLineIndexForOrder(orderId))
  }
  if (isFuScheduleOrReschedulePageActive()) {
    const lineIndex = scheduleTargetLineIndexForOrder(orderId)
    if (!fuPageSidebarSelectionIsFuSchedulableLine(orderId, lineIndex)) return false
    return orderHasActiveBookedAssignment(orderId, lineIndex)
  }
  return orderHasActiveBookedAssignment(orderId)
}

/** 改期模式别名（语义与 hasActiveBookedAssignment 一致） */
function hasActiveRescheduleAssignment(): boolean {
  return isRescheduleMode.value && hasActiveBookedAssignment()
}

function applyBookedOrderContext(orderId: string, opts?: { preserveWeekAnchor?: boolean }) {
  if (applyingRescheduleContextDepth > 0) return
  applyingRescheduleContextDepth++
  try {
    if (isFollowUpSchedulePageActive()) {
      const lineIdx = effectiveSidebarLineIndexForOrder(orderId)
      syncInspectorSidebarSelectionForScheduleOrder(orderId, lineIdx)
      const allowed = serviceCycleDateForOrder(orderId)
      if (allowed && !opts?.preserveWeekAnchor) navigateCalendarToDate(allowed)
      if (!fuScheduleInspectorSidebarDrivesOrder(orderId)) {
        selectedInspectorId.value = ''
        expandedInspectorIds.value = new Set()
      } else {
        expandedInspectorIds.value = new Set([selectedInspectorId.value])
      }
      clearSelectedSlots()
      rescheduleUserPickedSlots.value = false
      rebuildInspectorAssignmentRows()
      return
    }
    clearStaleScheduleClearedIfOrderStillBooked(orderId)
    const lineIdx = isRescheduleMode.value
      ? rescheduleLineIndexForOrder(orderId)
      : scheduleTargetLineIndexForOrder(orderId)
    const stale = assignmentsByOrderId.value[orderId]
    if (stale && stale.lineIndex !== lineIdx) {
      const nextAssign = { ...assignmentsByOrderId.value }
      delete nextAssign[orderId]
      assignmentsByOrderId.value = nextAssign
    }
    syncAssignmentFromOrderCard(orderId)
    if (!assignmentForOrderId(orderId)) {
      syncAssignmentFromContextInspectorOrder(orderId, selectedInspectorId.value)
    }
    const assignment = assignmentForOrderId(orderId)
    const ctxRow = contextInspectorOrderRowFor(orderId)
    const ctxDate = ctxRow ? parseServiceCycleDate(ctxRow.dateYmd) : null
    navigateCalendarToDate(
      assignment?.date ??
        originalAppointmentDateForOrder(orderId) ??
        ctxDate ??
        rescheduleAnchorDateForOrder(orderId),
    )

    let insId = assignment?.inspectorId
    const assignName = lineAssignmentNameForOrder(orderId)
    if (assignName) {
      const found = inspectors.value.find((i) => i.name.trim() === assignName)
      if (found) insId = found.id
    }
    if (insId) {
      const insKey = String(inspectorRowById(insId)?.id ?? insId)
      selectedInspectorId.value = insKey
      expandedInspectorIds.value = new Set([insKey])
    } else {
      selectedInspectorId.value = ''
      expandedInspectorIds.value = new Set()
    }

    clearSelectedSlots()
    rescheduleUserPickedSlots.value = false
    rebuildInspectorAssignmentRows()
    if (isRescheduleMode.value) {
      captureRescheduleEntrySnapshot(orderId)
      syncInspectorSidebarSelectionForOrder(orderId)
    } else if (isFollowUpSchedulePageActive()) {
      syncInspectorSidebarSelectionForScheduleOrder(orderId, lineIdx)
    }
  } finally {
    applyingRescheduleContextDepth--
  }
}


function lineAssignmentNameForOrder(orderId: string): string | undefined {
  const o = findOrder(orderId)
  const line = o?.services[scheduleTargetLineIndexForOrder(orderId)]
  const name = (line?.assignment ?? '').trim()
  if (!name || /pending\s+assignment/i.test(name)) return undefined
  return name
}


/** 切换左侧订单时：跳到该单服务周期日所在周并选中该日（未解析周期日时不沿用上一单日期） */
function applyServiceCycleDateForOrderSwitch() {
  if (isRescheduleMode.value) {
    applyRescheduleOrderContext(selectedOrderId.value)
    return
  }
  const orderId = selectedOrderId.value
  if (orderId && orderHasActiveBookedAssignment(orderId)) {
    applyBookedOrderContext(orderId)
    return
  }
  const existing = orderId ? assignmentsByOrderId.value[orderId] : null
  if (existing) {
    applyAssignmentToUi(existing)
    return
  }
  clearSelectedSlots()
  const allowed = allowedScheduleDate.value
  if (allowed) {
    weekAnchor.value = new Date(allowed.getFullYear(), allowed.getMonth(), allowed.getDate())
    selectedDate.value = new Date(allowed.getFullYear(), allowed.getMonth(), allowed.getDate())
    rebuildInspectorAssignmentRows()
    return
  }
  const week = weekDates.value
  if (!week.length) return
  const today = new Date()
  const match = week.find((x) => isSameCalendarDay(x, today)) ?? week[0]!
  selectedDate.value = new Date(match.getFullYear(), match.getMonth(), match.getDate())
  rebuildInspectorAssignmentRows()
}

function restoreSlotsForSelectedOrderIfAssigned() {
  const orderId = selectedOrderId.value
  if (orderId && scheduleClearedByUser.value.has(orderId)) {
    clearSelectedSlots()
    return
  }
  const assignment = orderId ? assignmentsByOrderId.value[orderId] : null
  if (!assignment || !selectedDate.value) return
  if (!isSameCalendarDay(assignment.date, selectedDate.value)) return
  selectedSlots.value = new Set(pickerSlotsFromAssignment(assignment))
}

function syncSelectedDateInWeekAfterNavigate() {
  const week = weekDates.value
  if (!week.length) return
  if (selectedDate.value && week.some((x) => isSameCalendarDay(x, selectedDate.value!))) {
    if (!isRescheduleMode.value) restoreSlotsForSelectedOrderIfAssigned()
    rebuildInspectorAssignmentRows()
    return
  }
  const orderId = selectedOrderId.value
  const assignment = orderId ? assignmentsByOrderId.value[orderId] : null
  if (assignment) {
    const match = week.find((x) => isSameCalendarDay(x, assignment.date))
    if (match) {
      selectedDate.value = new Date(match.getFullYear(), match.getMonth(), match.getDate())
      if (!isRescheduleMode.value) {
        selectedSlots.value = new Set(pickerSlotsFromAssignment(assignment))
      }
      rebuildInspectorAssignmentRows()
      return
    }
  }
  const first = week[0]!
  selectedDate.value = new Date(first.getFullYear(), first.getMonth(), first.getDate())
  clearSelectedSlots()
  rebuildInspectorAssignmentRows()
}

/** 工具栏月份展示（与 week-toolbar 文案一致） */
function resolveDisplayedCalendarMonthYear(): { year: number; month: number } {
  if (selectedDate.value) {
    return { year: selectedDate.value.getFullYear(), month: selectedDate.value.getMonth() }
  }
  const week = weekDates.value
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (week.length > 0 && week.some((d) => isSameCalendarDay(d, today))) {
    const monthKeys = new Set(week.map((d) => `${d.getFullYear()}-${d.getMonth()}`))
    if (monthKeys.size > 1) {
      return { year: today.getFullYear(), month: today.getMonth() }
    }
  }
  const d = weekAnchor.value
  return { year: d.getFullYear(), month: d.getMonth() }
}

function firstSelectableDayInMonthFromWeek(
  week: Date[],
  targetYear: number,
  targetMonth: number,
): Date | null {
  const inMonth = week.filter(
    (d) =>
      d.getFullYear() === targetYear &&
      d.getMonth() === targetMonth &&
      isDateSelectable(d),
  )
  if (inMonth.length) return inMonth[0]!
  return week.find((d) => isDateSelectable(d)) ?? week[0] ?? null
}

/** 月份切换后：日期条 + slot 网格对齐到目标月内的可选日 */
function syncSelectedDateAfterMonthShift(targetYear: number, targetMonth: number) {
  weekAnchor.value = new Date(targetYear, targetMonth, 1)
  let week = weekDates.value
  let pick = firstSelectableDayInMonthFromWeek(week, targetYear, targetMonth)
  if (!pick || pick.getFullYear() !== targetYear || pick.getMonth() !== targetMonth) {
    const today = new Date()
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    const monthStart = new Date(targetYear, targetMonth, 1)
    const focusDay =
      todayMid.getFullYear() === targetYear && todayMid.getMonth() === targetMonth
        ? todayMid
        : monthStart
    weekAnchor.value = focusDay
    week = weekDates.value
    pick = firstSelectableDayInMonthFromWeek(week, targetYear, targetMonth)
  }
  if (!pick) return

  selectedDate.value = new Date(pick.getFullYear(), pick.getMonth(), pick.getDate())
  hideToast()
  if (isRescheduleMode.value) {
    rescheduleUserPickedSlots.value = false
    clearSelectedSlots()
  } else if (
    selectedOrderId.value &&
    scheduleClearedByUser.value.has(selectedOrderId.value)
  ) {
    clearSelectedSlots()
  } else if (!hasActiveBookedAssignment()) {
    clearSelectedSlots()
  } else if (!isRescheduleMode.value) {
    restoreSlotsForSelectedOrderIfAssigned()
  }
  rebuildInspectorAssignmentRows()
  void loadInspectorAvailability()
  syncScheduleSlotsForActiveInspector()
  const focus = focusPayload.value
  if (focus?.orderId) void prefetchWeekDateCardTones(focus)
}

/** 年月单行，如 December 2025 */
const calendarMonthLabel = computed(() => {
  const { year, month } = resolveDisplayedCalendarMonthYear()
  return new Date(year, month, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
})

/** 标题括号内为参考时长（保留展示），不限制用户选格 */
function durationLabelForScheduleHeader(durationLabel: string): string {
  return durationLabel.trim()
}

function buildScheduleHeaderTitle(planName: string, durationLabel: string, fullAddress: string): string {
  const verb = isRescheduleMode.value ? 'Reschedule' : 'Schedule'
  const parts = [verb]
  if (planName.trim()) parts.push(planName.trim())
  const duration = durationLabelForScheduleHeader(durationLabel)
  if (duration) parts.push(duration)
  if (fullAddress.trim()) parts.push(`for ${fullAddress.trim()}`)
  return parts.join(' ')
}

function buildFollowUpScheduleHeaderTitle(durationLabel: string, fullAddress: string): string {
  const verb = isRescheduleMode.value ? 'Reschedule' : 'Schedule'
  const parts = [verb, 'Follow-up Care']
  const duration = durationLabelForScheduleHeader(durationLabel)
  if (duration) parts.push(duration)
  if (fullAddress.trim()) parts.push(`for ${fullAddress.trim()}`)
  return parts.join(' ')
}

function orderUsesFollowUpScheduleHeader(orderId: string): boolean {
  if (isRescheduleMode.value) {
    const line = findOrder(orderId)?.services[lineIndexForOrder(orderId)]
    return line != null && isFollowUpCareLine(line)
  }
  return isFollowUpScheduleSessionForOrder(orderId)
}

function scheduleLineIndexForOrder(orderId: string): number {
  const focus = focusPayload.value
  const o = findOrder(orderId)
  if (!o) return 0
  if (focus) return lineIndexForScheduleCard(o, focus.orderId, focus.lineIndex)
  return lineIndexForOrder(orderId)
}

/** Follow-up-Pending Schedule：标题为 Schedule Follow-up Care (… hours) for … */
function isFollowUpScheduleSessionForOrder(orderId: string): boolean {
  if (isRescheduleMode.value) return false
  const o =
    findOrder(orderId) ?? focusPayload.value?.orderList?.find((x) => x.id === orderId)
  if (o) {
    const bucket =
      classifyCareOrderCardBucket(o) ?? classifyCareSidebarSubBucket(o.status ?? '')
    if (bucket === 'fu_pend_sched') return true
    if (orderStatusIsFollowUpContext(o.status)) return true
    if (orderHasSchedulableFollowUpLine(o)) {
      const main = o.services.find((l) => !isFollowUpCareLine(l))
      if (main && lineHasConfirmedScheduleTime(main, o)) return true
    }
    const focus = focusPayload.value
    if (focus?.orderId === orderId) {
      const line = o.services[focus.lineIndex]
      if (line && isFollowUpCareLine(line)) return true
    }
    return false
  }
  const focus = focusPayload.value
  const fromList = focus?.orderList?.find((x) => x.id === orderId)
  if (fromList) {
    const bucket =
      classifyCareOrderCardBucket(fromList) ?? classifyCareSidebarSubBucket(fromList.status ?? '')
    if (bucket === 'fu_pend_sched') return true
    if (orderStatusIsFollowUpContext(fromList.status)) return true
    if (focus?.orderId === orderId) {
      const line = fromList.services[focus.lineIndex]
      if (line && isFollowUpCareLine(line)) return true
    }
  }
  if (focus?.orderId === orderId) {
    const name = (focus.serviceName ?? '').trim().toLowerCase()
    if (name === 'follow-up care') return true
  }
  return false
}

function scheduleOrderBucket(o: OrderCardDTO) {
  return classifyCareOrderCardBucket(o) ?? classifyCareSidebarSubBucket(o.status ?? '')
}

function findCareTemplateById(templateId: number): CareTemplateMeta | undefined {
  const meta = careMeta.value
  if (!meta?.templates_by_plan) return undefined
  for (const templates of Object.values(meta.templates_by_plan)) {
    const hit = templates.find((t) => t.id === templateId || t.value === templateId)
    if (hit) return hit
  }
  return undefined
}

function planTemplatesForOrderDetail(detail: CareOrderListItem): CareTemplateMeta[] {
  const meta = careMeta.value
  if (!meta?.templates_by_plan) return []
  const planKey = detail.plan_key?.trim()
  if (planKey && meta.templates_by_plan[planKey]?.length) {
    return meta.templates_by_plan[planKey]!
  }
  const templateId = careTemplateIdFromListRow(detail)
  if (templateId == null) return []
  for (const templates of Object.values(meta.templates_by_plan)) {
    if (templates.some((t) => t.id === templateId || t.value === templateId)) return templates
  }
  return []
}

function serviceCycleMonthFromYmd(ymd: string | undefined): number | null {
  const hit = ymd?.trim().match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!hit) return null
  const d = new Date(`${hit[1]}-${hit[2]}-${hit[3]}T12:00:00`)
  return Number.isNaN(d.getTime()) ? null : d.getMonth() + 1
}

function isFollowUpScheduleOrder(detail: CareOrderListItem): boolean {
  const bucket = classifyCareSidebarSubBucket(detail.service_status_text ?? detail.service_status ?? '')
  return bucket === 'fu_pend_sched' || bucket === 'fu_pend_resched'
}

function durationLabelForOrderDetail(
  detail: CareOrderListItem,
  serviceCycleYmd?: string,
): string {
  const templateId = careTemplateIdFromListRow(detail)
  if (templateId == null) return ''

  const planTemplates = planTemplatesForOrderDetail(detail)
  const month =
    serviceCycleMonthFromYmd(serviceCycleYmd) ??
    serviceCycleMonthFromYmd(detail.service_date?.trim())

  if (planTemplates.length) {
    return buildScheduleDurationLabelForOrder({
      planTemplates,
      careTemplateId: templateId,
      planKey: detail.plan_key,
      serviceCycleMonth: month,
      isFollowUp: isFollowUpScheduleOrder(detail),
      baselineItemCount: countBaselineTaggedChecklistItems(detail.items),
    })
  }

  const template = findCareTemplateById(templateId)
  return template ? formatScheduleDurationLabel(template) : ''
}

function durationLabelForTemplateId(templateId: number | undefined): string {
  if (templateId == null) return ''
  const template = findCareTemplateById(templateId)
  return template ? formatScheduleDurationLabel(template) : ''
}

function careTemplateIdFromListRow(row: unknown): number | undefined {
  if (!row || typeof row !== 'object') return undefined
  const id = (row as CareOrderListItem).care_template_id
  return typeof id === 'number' && Number.isFinite(id) ? id : undefined
}

/** 打开弹层：同单只打一次 detail，合并改期快照 / 周期 / 时长 / 首选时间 */
async function hydrateFocusOrderEnrichmentOnOpen(focus: CareScheduleFocusPayload): Promise<void> {
  const orderId = focus.orderId?.trim()
  if (!orderId) return
  const o =
    focus.orderList?.find((x) => (x.id ?? '').trim() === orderId) ??
    findOrder(orderId) ??
    focus.orderList?.find((x) => careOrderIdsReferToSameOrder(x.id, orderId))
  if (!o?.orderPrimaryId) return

  await ensureCareMeta()

  try {
    const detail = await fetchOrderDetailOnce(o.orderPrimaryId)
    const card = patchOrderCardTechnicianRefs(
      careOrderListItemToCardDto(detail),
      detail,
      technicianNoLookup.value,
    )
    registerOrderCards([card])
    if (focus.mode === 'reschedule') {
      mergeOrderCardFromBackend(orderId, card)
    }

    applyDurationLabelsFromOrderDetails({ [o.id]: detail })

    if (isCustomerRequestedReschedule.value) {
      const times = preferredTimesFromCareOrderRow(detail)
      mergePreferredTimesOntoOrderCard(orderId, times)
      const snap = rescheduleEntrySnapshot.value
      if (snap?.orderId === orderId && !snap.preferredTimes.length && times.length) {
        rescheduleEntrySnapshot.value = {
          ...snap,
          preferredTimes: times.map((pt) => formatOrderScheduleLineDisplay(pt)),
        }
      }
    }
  } catch {
    /* 详情失败则保持入口快照 */
  }
}

/** 打开初始化末尾：仅补缺 availability，不再重复打 context */
async function ensureInspectorAvailabilityOnOpen(): Promise<void> {
  if (!selectedInspectorId.value?.trim() || !selectedOrderId.value?.trim()) return
  if (inspectorAvailability.value) return
  await loadInspectorAvailability()
  syncScheduleSlotsForActiveInspector()
}

async function finalizeScheduleOpenInitialization(focus: CareScheduleFocusPayload): Promise<void> {
  // detail 已在打开链路开头 hydrateFocusOrderEnrichmentOnOpen 打过（同 id 走缓存）
  ensureFocusOrderSelected(focus)
  syncAssignmentUiForSelectedOrder()
  rebuildInspectorAssignmentRows()
  if (focus.mode === 'reschedule' && focus.orderId?.trim() && !isOrderScheduleCleared(focus.orderId)) {
    syncInspectorSidebarSelectionForOrder(focus.orderId)
  }
  await ensureInspectorAvailabilityOnOpen()
}

const headerTitle = computed(() => {
  const orderId = selectedOrderId.value
  if (orderId) {
    const card = scheduleCardForOrder(orderId)
    if (card) {
      if (orderUsesFollowUpScheduleHeader(orderId)) {
        return buildFollowUpScheduleHeaderTitle(card.durationLabel, card.fullAddress)
      }
      return buildScheduleHeaderTitle(
        planDisplayNameForOrder(orderId, card.serviceName),
        card.durationLabel,
        card.fullAddress,
      )
    }
  }
  const p = focusPayload.value
  if (!p) return isRescheduleMode.value ? 'Reschedule' : 'Schedule'
  const durationLabel = p.durationLabel ?? durationLabelForOrderId(p.orderId)
  const fullAddress = p.address ?? ''
  if (orderUsesFollowUpScheduleHeader(p.orderId)) {
    return buildFollowUpScheduleHeaderTitle(durationLabel, fullAddress)
  }
  return buildScheduleHeaderTitle(
    planDisplayNameForOrder(p.orderId, p.serviceName ?? ''),
    durationLabel,
    fullAddress,
  )
})

const unscheduledPanelHeading = computed(() => {
  const focus = focusPayload.value
  if (focus?.mode === 'reschedule') {
    if (focus && isRescheduleFollowUpFocusSession(focus)) return 'Reschedule Follow-up'
    return 'Reschedule Inspections'
  }
  const orderId = selectedOrderId.value ?? focus?.orderId ?? ''
  if (orderId && isFollowUpScheduleSessionForOrder(orderId)) return 'Unscheduled Follow-up'
  return 'Unscheduled Inspections'
})

const selectedInspector = computed(() => inspectorRowById(selectedInspectorId.value))

const inspectorScheduleTitle = computed(() => {
  const name = selectedInspector.value?.name?.trim()
  return name ? `${name}'s schedule` : 'Schedule'
})

function allGridSlots(): string[] {
  return TIME_SLOT_GRID.flat().filter((s): s is string => Boolean(s))
}

function clockMatchVariants(hm: string, ap: string): string[] {
  const m = hm.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!m?.[1] || !m[2]) return [normalizeGridSlot(`${hm}${ap.toUpperCase()}`)]
  const h = parseInt(m[1], 10)
  const min = m[2]
  const mer = ap.toUpperCase()
  return [
    normalizeGridSlot(`${h}:${min}${mer}`),
    normalizeGridSlot(`${String(h).padStart(2, '0')}:${min}${mer}`),
  ]
}

function findGridSlotIndexForClock(hm: string, ap: string): number {
  const all = allGridSlots()
  const variants = clockMatchVariants(hm, ap)
  let idx = all.findIndex((s) => variants.includes(normalizeGridSlot(s)))
  if (idx >= 0) return idx
  for (const v of variants) {
    const labeled = formatSlotLabel(v)
    idx = all.findIndex((s) => formatSlotLabel(s) === labeled)
    if (idx >= 0) return idx
  }
  return -1
}

function findGridSlotIndexForDisplayTime(displayTime: string): number {
  const m = displayTime.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m?.[1] || !m[2] || !m[3]) return -1
  return findGridSlotIndexForClock(`${m[1]}:${m[2]}`, m[3])
}

const SLOT_INTERVAL_MINUTES = 30

function parseClockParts(hm: string, ap: string): { h: number; m: number; ap: string } | null {
  const m = hm.trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!m?.[1] || !m[2]) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h < 1 || h > 12 || min < 0 || min > 59) return null
  return { h, m: min, ap: ap.toUpperCase() }
}

function clockPartsToMinutes(parts: { h: number; m: number; ap: string }): number {
  let hour = parts.h % 12
  if (parts.ap === 'PM') hour += 12
  return hour * 60 + parts.m
}

function gridSlotStartMinutes(slot: string): number | null {
  const m = slot.trim().match(/^(\d{1,2}):(\d{2})(AM|PM)$/i)
  if (!m?.[1] || !m[2] || !m[3]) return null
  const parts = parseClockParts(`${m[1]}:${m[2]}`, m[3])
  return parts ? clockPartsToMinutes(parts) : null
}

function startOfToday(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function isPastCalendarDay(d: Date): boolean {
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  return day.getTime() < startOfToday().getTime()
}

function isPastTimeSlot(slot: string, date: Date): boolean {
  if (isPastCalendarDay(date)) return true
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  if (day.getTime() > startOfToday().getTime()) return false
  const slotMinutes = gridSlotStartMinutes(slot)
  if (slotMinutes == null) return false
  const now = new Date()
  const nowMinutes = now.getHours() * 60 + now.getMinutes()
  return slotMinutes < nowMinutes
}

function pastSlotsForDate(date: Date): Set<string> {
  const out = new Set<string>()
  for (const slot of allGridSlots()) {
    if (isPastTimeSlot(slot, date)) out.add(slot)
  }
  return out
}

/** 结束时刻可能不在格点上（如 01:35PM）→ 取包含该时刻的最后一个 30 分钟格 */
function findGridSlotIndexForEndClock(hm: string, ap: string): number {
  const parts = parseClockParts(hm, ap)
  if (!parts) return findGridSlotIndexForClock(hm, ap)

  const endMin = clockPartsToMinutes(parts)
  const all = allGridSlots()
  const exact = findGridSlotIndexForClock(hm, ap)
  if (exact >= 0) {
    const startMin = gridSlotStartMinutes(all[exact]!)
    if (startMin != null && endMin === startMin) return exact
  }

  for (let i = 0; i < all.length; i++) {
    const startMin = gridSlotStartMinutes(all[i]!)
    if (startMin == null) continue
    const nextStart =
      i + 1 < all.length ? gridSlotStartMinutes(all[i + 1]!) : startMin + SLOT_INTERVAL_MINUTES
    if (startMin <= endMin && endMin < nextStart!) return i
  }

  let lastIdx = -1
  for (let i = 0; i < all.length; i++) {
    const startMin = gridSlotStartMinutes(all[i]!)
    if (startMin != null && startMin <= endMin) lastIdx = i
  }
  return lastIdx >= 0 ? lastIdx : exact
}

function canonicalGridSlot(slot: string): string | null {
  const idx = slotGridIndex(slot)
  if (idx < 0) return null
  return allGridSlots()[idx] ?? null
}

function normalizeAvailabilitySlotLabel(raw: string): string | null {
  const t = raw.trim()
  if (!t) return null
  const fromGrid = canonicalGridSlot(t)
  if (fromGrid) return fromGrid
  const m24 = t.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
  if (m24?.[1] && m24[2]) {
    let hour = parseInt(m24[1], 10)
    const minute = m24[2]
    const ap = hour >= 12 ? 'PM' : 'AM'
    if (hour > 12) hour -= 12
    if (hour === 0) hour = 12
    return canonicalGridSlot(`${hour}:${minute}${ap}`)
  }
  return null
}

function technicianIdForAvailabilityApi(inspectorId: string): number {
  const row = inspectorRowById(inspectorId)
  const raw = row?.linkedIds?.[0] ?? row?.id ?? inspectorId
  const n = Number(raw)
  if (Number.isFinite(n) && n > 0) return Math.trunc(n)
  const digits = String(raw).replace(/\D/g, '')
  const parsed = Number(digits)
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : 0
}

function availabilityDisabledSlotsForDate(date: Date): Set<string> | null {
  const av = inspectorAvailability.value
  if (!av) return null
  const ymd = ymdFromDate(date)
  const all = allGridSlots()
  const availableRaw = av.availableSlotsByDate[ymd]
  if (availableRaw?.length) {
    const allow = new Set<string>()
    for (const raw of availableRaw) {
      const slot = normalizeAvailabilitySlotLabel(raw)
      if (slot) allow.add(slot)
    }
    if (allow.size) return new Set(all.filter((slot) => !allow.has(slot)))
  }
  const blockedRaw = av.blockedSlotsByDate[ymd]
  if (blockedRaw?.length) {
    const blocked = new Set<string>()
    for (const raw of blockedRaw) {
      const slot = normalizeAvailabilitySlotLabel(raw)
      if (slot) blocked.add(slot)
    }
    if (blocked.size) return blocked
  }
  return null
}

function localDisabledSlotsForPicker(
  inspectorId: string,
  date: Date,
  exclude: ScheduleBookingExclude | null,
): Set<string> {
  return collectOccupiedSlotsForInspectorDate(inspectorId, date, exclude, { forSlotDisable: true })
}

/** availability 白名单常漏标夹在两笔预约之间的短空档；释放被前后占用包围且本地无占用的格 */
function slotsInSurroundedGapsBetweenBookings(occupied: Set<string>): Set<string> {
  const gaps = new Set<string>()
  const all = allGridSlots()
  const isOccupied = (slot: string) => {
    const canonical = canonicalGridSlot(slot) ?? slot
    return occupied.has(canonical) || occupied.has(slot)
  }

  let i = 0
  while (i < all.length) {
    while (i < all.length && !isOccupied(all[i]!)) i++
    if (i >= all.length) break
    while (i < all.length && isOccupied(all[i]!)) i++
    const gapStart = i
    while (i < all.length && !isOccupied(all[i]!)) i++
    const gapEnd = i - 1
    if (gapStart > gapEnd || i >= all.length) continue
    for (let g = gapStart; g <= gapEnd; g++) {
      const slot = all[g]
      if (slot) gaps.add(slot)
    }
  }
  return gaps
}

function disabledSlotsForPicker(
  inspectorId: string,
  date: Date,
  exclude: ScheduleBookingExclude | null,
): Set<string> {
  const local = localDisabledSlotsForPicker(inspectorId, date, exclude)
  const fromApi = availabilityDisabledSlotsForDate(date)
  if (!fromApi) return local

  const authoritativeOccupied = collectOccupiedSlotsForInspectorDate(inspectorId, date, null)
  const merged = new Set(fromApi)
  // 与 API 取并集：后端 availability 漏算时，仍展示本地已排期占用（主单 / FU / Schedule / Reschedule 均计入）
  for (const slot of local) merged.add(slot)

  for (const slot of slotsInSurroundedGapsBetweenBookings(authoritativeOccupied)) {
    const canonical = canonicalGridSlot(slot) ?? slot
    if (!authoritativeOccupied.has(canonical) && !authoritativeOccupied.has(slot)) merged.delete(slot)
  }

  if (exclude) {
    const fullLocal = localDisabledSlotsForPicker(inspectorId, date, null)
    const exclLocal = localDisabledSlotsForPicker(inspectorId, date, exclude)
    for (const slot of allGridSlots()) {
      if (fullLocal.has(slot) && !exclLocal.has(slot)) merged.delete(slot)
    }
  }
  return merged
}

function occupiedSlotIndicesFromClockRange(
  startHm: string,
  startAp: string,
  endHm: string,
  endAp: string,
): { startIdx: number; endIdx: number } {
  const startParts = parseClockParts(startHm, startAp)
  const endParts = parseClockParts(endHm, endAp)
  if (!startParts || !endParts) return { startIdx: -1, endIdx: -1 }

  const apptStart = clockPartsToMinutes(startParts)
  const apptEnd = clockPartsToMinutes(endParts)
  if (apptEnd <= apptStart) {
    const startIdx = findGridSlotIndexForClock(startHm, startAp)
    return { startIdx, endIdx: startIdx }
  }

  const all = allGridSlots()
  let startIdx = -1
  let endIdx = -1
  for (let i = 0; i < all.length; i++) {
    const slotStart = gridSlotStartMinutes(all[i]!)
    if (slotStart == null) continue
    if (slotStart >= apptStart && slotStart < apptEnd) {
      if (startIdx < 0) startIdx = i
      endIdx = i
    }
  }
  if (startIdx < 0) {
    startIdx = findGridSlotIndexForClock(startHm, startAp)
    endIdx = findGridSlotIndexForEndClock(endHm, endAp)
  }
  return { startIdx, endIdx }
}

function gridSlotIndicesFromTimeLabel(
  timeLabel: string,
  anchorDate?: Date | null,
): { startIdx: number; endIdx: number } {
  if (anchorDate) {
    const scoped = clockRangeFromScheduleDisplayForDate(timeLabel, anchorDate)
    if (scoped) {
      return occupiedSlotIndicesFromClockRange(
        scoped.startHm,
        scoped.startAp,
        scoped.endHm,
        scoped.endAp,
      )
    }
  }
  const times = [...timeLabel.matchAll(/(\d{1,2}:\d{2})\s*(AM|PM)/gi)]
  if (times.length >= 2 && times[0]?.[1] && times[0]?.[2]) {
    const last = times[times.length - 1]!
    if (last[1] && last[2]) {
      return occupiedSlotIndicesFromClockRange(times[0][1], times[0][2], last[1], last[2])
    }
  }
  let startIdx = -1
  let endIdx = -1
  if (times.length >= 1 && times[0]?.[1] && times[0]?.[2]) {
    startIdx = findGridSlotIndexForClock(times[0][1], times[0][2])
    endIdx = startIdx
  }
  return { startIdx, endIdx }
}

function occupiedSlotsFromTimeLabel(
  timeLabel: string,
  opts?: { startSlot?: string; anchorDate?: Date | null },
): string[] {
  const anchor = opts?.anchorDate
  if (anchor && !Number.isNaN(anchor.getTime())) {
    const fromDisplay = displaySlotsFromBookedTimeLabel(timeLabel, anchor)
    if (fromDisplay.length) return fromDisplay
  }

  const range = clockRangeFromBookedTimeLabel(timeLabel, anchor ?? null)
  if (range) {
    const fromRange = slotsInClockRangeForDisplay(range)
    if (fromRange.length) return fromRange
  }

  const all = allGridSlots()
  const fromLabel = gridSlotIndicesFromTimeLabel(timeLabel, anchor)
  let startIdx = fromLabel.startIdx
  if (startIdx < 0 && opts?.startSlot) startIdx = slotGridIndex(opts.startSlot)
  if (startIdx < 0) {
    const only = opts?.startSlot ? canonicalGridSlot(opts.startSlot) : null
    return only ? [only] : []
  }

  let endIdx = fromLabel.endIdx >= 0 ? fromLabel.endIdx : startIdx
  if (endIdx < startIdx) endIdx = startIdx

  const from = Math.min(startIdx, endIdx)
  const to = Math.max(startIdx, endIdx)
  return all.slice(from, to + 1).filter((s): s is string => Boolean(s))
}

function buildOccupiedSlotsFromSelectedSlots(slots: string[]): string[] {
  return sortSlotsByGridOrder(slots)
    .map((s) => canonicalGridSlot(s) ?? s)
    .filter((s) => slotGridIndex(s) >= 0)
}

function occupiedSlotsForAssignment(
  a: OrderScheduleAssignment,
  opts?: { forceRecompute?: boolean },
): string[] {
  if (a.pickerSlots?.length) {
    return sortSlotsByGridOrder(
      a.pickerSlots
        .map((s) => canonicalGridSlot(s) ?? s)
        .filter((s) => slotGridIndex(s) >= 0),
    )
  }

  if (!opts?.forceRecompute && a.occupiedSlots?.length) {
    const resolved = a.occupiedSlots
      .map((s) => canonicalGridSlot(s) ?? s)
      .filter((s) => slotGridIndex(s) >= 0)
    if (resolved.length) return resolved
  }

  const fromLabel = occupiedSlotsFromTimeLabel(a.timeLabel, {
    startSlot: a.slot,
    anchorDate: a.date,
  })
  if (fromLabel.length) return fromLabel

  const only = canonicalGridSlot(a.slot)
  return only ? [only] : []
}

function gridSlotFromDisplayClock(clock: string): string | null {
  const m = clock.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m?.[1] || !m[2] || !m[3]) return null
  return canonicalGridSlot(`${m[1]}:${m[2]}${m[3].toUpperCase()}`)
}

function endBoundaryGridSlotFromTimeLabel(timeLabel: string, anchorDate?: Date | null): string | null {
  if (anchorDate) {
    const scoped = clockRangeFromScheduleDisplayForDate(timeLabel, anchorDate)
    if (scoped) return gridSlotFromDisplayClock(`${scoped.endHm} ${scoped.endAp}`)
  }
  const times = [...timeLabel.matchAll(/(\d{1,2}:\d{2})\s*(AM|PM)/gi)]
  if (times.length < 2) return null
  const last = times[times.length - 1]!
  if (!last[1] || !last[2]) return null
  return gridSlotFromDisplayClock(`${last[1]} ${last[2].toUpperCase()}`)
}

/** 网格展示区间 = 用户实际占用的格（不再追加结束边界格） */
function displaySlotsForAssignment(
  a: OrderScheduleAssignment,
  opts?: { forceRecomputeOccupied?: boolean },
): string[] {
  return sortSlotsByGridOrder(
    occupiedSlotsForAssignment(a, { forceRecompute: opts?.forceRecomputeOccupied }),
  )
}

/** 时段选择器 `selectedSlots`：仅真实占用格，不含结束展示格（避免未点击却多出 30 分钟） */
function pickerSlotsFromAssignment(
  a: OrderScheduleAssignment,
  opts?: { forceRecomputeOccupied?: boolean },
): string[] {
  if (a.pickerSlots?.length) {
    return sortSlotsByGridOrder(
      a.pickerSlots
        .map((s) => canonicalGridSlot(s) ?? s)
        .filter((s) => slotGridIndex(s) >= 0),
    )
  }
  return inferPickerSlotsFromAssignmentOccupied(a, opts)
}

function inferPickerSlotsFromAssignmentOccupied(
  a: OrderScheduleAssignment,
  opts?: { forceRecomputeOccupied?: boolean },
): string[] {
  const occupied = sortSlotsByGridOrder(
    occupiedSlotsForAssignment(a, { forceRecompute: opts?.forceRecomputeOccupied }),
  )
  if (occupied.length <= 1) return occupied

  const range = clockRangeFromBookedTimeLabel(a.timeLabel, a.date)
  if (!range) return occupied
  const startParts = parseClockParts(range.startHm, range.startAp)
  const endParts = parseClockParts(range.endHm, range.endAp)
  if (!startParts || !endParts) return occupied
  const duration = clockPartsToMinutes(endParts) - clockPartsToMinutes(startParts)
  if (duration === SLOT_INTERVAL_MINUTES) {
    return [occupied[0]!]
  }
  return occupied
}

/** 用户选中的格均为真实占用格（网格每格 30 分钟，不再剥离“结束展示格”） */
function occupancySlotsFromSelectedSlots(slots: string[]): string[] {
  return buildOccupiedSlotsFromSelectedSlots(slots)
}

/** 检查员日历占用：合并缓存、会话列表、ordersById 等，主单/跟进行时段均计入 */
function allOrdersForInspectorOccupancy(): OrderCardDTO[] {
  const byId = new Map<string, OrderCardDTO>()
  const addList = (list: OrderCardDTO[]) => {
    for (const o of list) {
      if (!o?.id) continue
      byId.set(o.id, ordersById.value[o.id] ?? o)
    }
  }
  addList(Object.values(ordersById.value))
  addList(assignedOrdersCache.value)
  addList(focusPayload.value?.orderList ?? [])
  for (const item of unscheduled.value) {
    const o = findOrder(item.orderId)
    if (o) byId.set(o.id, o)
  }
  return [...byId.values()]
}

function bookingExcludeMatches(
  exclude: ScheduleBookingExclude | null,
  orderId: string,
  lineIndex: number,
): boolean {
  if (!exclude || exclude.lineIndex !== lineIndex) return false
  if (exclude.orderId === orderId) return true
  const excludeOrder = findOrder(exclude.orderId)
  const targetOrder = findOrder(orderId)
  return !!(excludeOrder && targetOrder && careOrderCardsSameOrder(excludeOrder, targetOrder))
}

function parseOrderPrimaryKeyFromId(raw: string): number | null {
  const t = raw.trim()
  if (!t) return null
  const hm = t.match(/^HM-(\d+)$/i)
  if (hm?.[1]) {
    const n = parseInt(hm[1], 10)
    return Number.isFinite(n) && !isLikelyCareOrderBusinessNumber(n) ? n : null
  }
  if (/^\d+$/.test(t)) {
    const n = parseInt(t, 10)
    return Number.isFinite(n) && !isLikelyCareOrderBusinessNumber(n) ? n : null
  }
  return null
}

function orderIdMatchesCard(orderId: string, card: OrderCardDTO): boolean {
  const id = orderId.trim()
  if (!id) return false
  const cardId = (card.id ?? '').trim()
  if (cardId && cardId === id) return true
  const pk = card.orderPrimaryId
  if (pk == null) return false
  const pkStr = String(Math.trunc(pk))
  if (id === pkStr || id === `HM-${pkStr}`) {
    const cardIdNum = readCareOrderNumericId(cardId)
    if (cardIdNum != null && isLikelyCareOrderBusinessNumber(cardIdNum) && id !== cardId) {
      return false
    }
    return true
  }
  const idPk = parseOrderPrimaryKeyFromId(id)
  if (idPk != null && idPk === Math.trunc(pk)) {
    const cardIdNum = readCareOrderNumericId(cardId)
    if (cardIdNum != null && isLikelyCareOrderBusinessNumber(cardIdNum) && id !== cardId) {
      return false
    }
    return true
  }
  return false
}

function findOrderForContextInspectorRow(row: ScheduleContextInspectorOrder): OrderCardDTO | undefined {
  const orderId = row.orderId.trim()
  const targetId = row.targetId
  if (targetId != null && targetId > 0) {
    const byPk = Object.values(ordersById.value).find(
      (o) => o.orderPrimaryId != null && Math.trunc(o.orderPrimaryId) === Math.trunc(targetId),
    )
    if (byPk) return byPk
    const fromAssigned = assignedOrdersCache.value.find(
      (o) => o.orderPrimaryId != null && Math.trunc(o.orderPrimaryId) === Math.trunc(targetId),
    )
    if (fromAssigned) return fromAssigned
  }
  const exact = findOrder(orderId)
  if (
    exact &&
    (targetId == null ||
      exact.orderPrimaryId == null ||
      Math.trunc(exact.orderPrimaryId) === Math.trunc(targetId))
  ) {
    return exact
  }
  return undefined
}

function registerContextInspectorOrderCard(row: ScheduleContextInspectorOrder): void {
  if (row.targetType === 'followup') return
  const orderId = row.orderId.trim()
  const targetId = row.targetId
  if (!orderId || targetId == null || targetId <= 0) return
  if (findOrderForContextInspectorRow(row)) return
  const addr = (row.address ?? '').trim() || '—'
  registerOrderCards([
    {
      id: orderId,
      orderPrimaryId: targetId,
      homeId: '',
      status: '',
      placementDate: '',
      services: [
        {
          serviceName: 'Care',
          productCode: '—',
          scheduledAt: scheduleLabelFromContextInspectorOrder(row) ?? '—',
          address: addr,
          assignment: '—',
          price: '—',
          paymentMethod: '—',
          canSchedule: false,
          canReschedule: true,
          canRefund: false,
          canMessage: false,
          canNote: false,
        },
      ],
    },
  ])
}


function careOrderStatusBlobIndicatesCompleted(status?: string, code?: string): boolean {
  const blob = `${code ?? ''} ${status ?? ''}`.trim().toLowerCase()
  if (!blob) return false
  if (blob.includes('incomplete')) return false
  if (blob.includes('pending')) return false
  if (/\bcompleted\b/.test(blob) || /\bcompeted\b/.test(blob)) return true
  if (/\bfu[\s_-]*completed\b/.test(blob) || /\bfollow[\s-]*up[\s_-]*completed\b/.test(blob)) {
    return true
  }
  return /\bcomplete\b/.test(blob)
}

/** 已完成整单不再占检查员网格（避免 context 残留 + 空档释放出现「空洞」） */
function orderLineShouldNotOccupyInspectorGrid(o: OrderCardDTO, lineIndex: number): boolean {
  if (orderStatusIsCompleted(o.status)) return true
  if (careOrderStatusBlobIndicatesCompleted(o.status, o.serviceStatusCode)) return true
  return false
}

function contextInspectorOrderLineIndex(row: ScheduleContextInspectorOrder, o: OrderCardDTO): number {
  if (row.targetType === 'followup') {
    const fu = o.services.findIndex((l) => isFollowUpCareLine(l))
    return fu >= 0 ? fu : 0
  }
  const main = o.services.findIndex((l) => !isFollowUpCareLine(l))
  return main >= 0 ? main : 0
}

function contextInspectorOrderShouldSkipOccupancy(row: ScheduleContextInspectorOrder): boolean {
  const o = findOrder(row.orderId)
  if (!o) return false
  return orderLineShouldNotOccupyInspectorGrid(o, contextInspectorOrderLineIndex(row, o))
}

/** Schedule 模式：左栏待排程池订单（仅 localStorage / 缓存脏数据）不计入检查员占用 */
function orderCountsAsCommittedInspectorBooking(
  orderId: string,
  lineIndex: number,
  exclude: ScheduleBookingExclude | null,
  opts?: { forConflict?: boolean; forSlotDisable?: boolean },
): boolean {
  if (isOrderScheduleCleared(orderId)) return false
  const o = findOrder(orderId)
  if (o && orderLineShouldNotOccupyInspectorGrid(o, lineIndex)) return false
  if (
    opts?.forConflict &&
    exclude &&
    careOrderIdsReferToSameOrder(orderId, exclude.orderId) &&
    (isFollowUpScheduleSessionForOrder(exclude.orderId) ||
      isRescheduleFollowUpSessionForOrder(exclude.orderId))
  ) {
    return false
  }
  if (bookingExcludeMatches(exclude, orderId, lineIndex)) return false
  const focus = focusPayload.value
  if (!focus || focus.mode === 'reschedule') return true
  if (o && isOrderInUnscheduledPool(o, focus)) {
    const editingLine = scheduleConflictExcludeLineIndex(o.id)
    if (lineIndex !== editingLine) {
      return true
    }
    return false
  }
  return true
}

function currentScheduleBookingExclude(): ScheduleBookingExclude | null {
  const orderId = selectedOrderId.value
  if (!orderId) return null
  return {
    orderId,
    lineIndex: isRescheduleMode.value
      ? rescheduleLineIndexForOrder(orderId)
      : scheduleConflictExcludeLineIndex(orderId),
  }
}

function scheduleLabelFromContextInspectorOrder(row: ScheduleContextInspectorOrder): string {
  const text = row.timeText.trim()
  if (!text) return ''
  const formatted = formatOrderScheduleLineDisplay(text)
  if (formatted !== '—' && /[A-Za-z]{3}\s+\d{1,2},?\s*\d{4}/.test(formatted)) {
    return formatted
  }
  const d = parseServiceCycleDate(row.dateYmd)
  if (!d) return formatted !== '—' ? formatted : text
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const clock = formatted !== '—' ? formatted : text
  return `${dateStr}, ${clock}`
}

function contextInspectorOrderRowFor(
  orderId: string,
  inspectorId?: string,
  date?: Date | null,
  targetType?: 'order' | 'followup',
): ScheduleContextInspectorOrder | null {
  for (const row of scheduleContextInspectorOrders.value) {
    if (!careOrderIdsReferToSameOrder(row.orderId, orderId)) continue
    if (targetType === 'followup' && row.targetType !== 'followup') continue
    if (targetType === 'order' && row.targetType === 'followup') continue
    if (inspectorId && !isSameInspectorId(row.technicianId, inspectorId)) continue
    if (date) {
      const rowDate = parseServiceCycleDate(row.dateYmd)
      if (!rowDate || !isSameCalendarDay(rowDate, date)) continue
    }
    if (!scheduleLabelFromContextInspectorOrder(row)) continue
    return row
  }
  return null
}

function assignmentForOrderId(orderId: string): OrderScheduleAssignment | undefined {
  const id = orderId.trim()
  if (!id) return undefined
  const direct = assignmentsByOrderId.value[id]
  if (direct) return direct
  return Object.values(assignmentsByOrderId.value).find((a) => careOrderIdsReferToSameOrder(a.orderId, id))
}

function bookedTimeLabelCandidatesForPicker(orderId: string, date: Date): string[] {
  const lineIndex = scheduleTargetLineIndexForOrder(orderId)
  const o = findOrder(orderId)
  const out: string[] = []
  const push = (raw: string) => {
    const trimmed = raw.trim()
    if (!trimmed || trimmed === '—') return
    const formatted = formatOrderScheduleLineDisplay(trimmed)
    const label = formatted !== '—' ? formatted : trimmed
    if (!out.includes(label)) out.push(label)
  }

  const backendLabel = o ? backendBookedScheduleLabelForLine(o, lineIndex) : null
  if (backendLabel) return [backendLabel]

  for (const row of scheduleContextInspectorOrders.value) {
    if (!careOrderIdsReferToSameOrder(row.orderId, orderId)) continue
    const rowDate = parseServiceCycleDate(row.dateYmd)
    if (!rowDate || !isSameCalendarDay(rowDate, date)) continue
    const label = scheduleLabelFromContextInspectorOrder(row)
    if (label) push(label)
  }

  const cs = scheduleContextCurrentSchedule.value
  if (cs?.date?.trim() && careOrderIdsReferToSameOrder(selectedOrderId.value ?? '', orderId)) {
    const csDate = parseServiceCycleDate(cs.date)
    if (csDate && isSameCalendarDay(csDate, date)) {
      const ctxLabel = scheduleTimeLabelFromContext(cs)
      if (ctxLabel) push(ctxLabel)
    }
  }

  return out
}

function syncAssignmentFromContextInspectorOrder(orderId: string, inspectorId?: string): boolean {
  if (isOrderScheduleCleared(orderId)) return false
  const canonicalId = resolveCanonicalOrderId(orderId)
  if (schedulePendingPoolOrderTreatAsUnbooked(canonicalId)) return false
  const lineIndex = scheduleTargetLineIndexForOrder(canonicalId)
  const fuSession = !isRescheduleMode.value && isFollowUpScheduleSessionForOrder(canonicalId)
  const targetFilter: 'order' | 'followup' | undefined = fuSession ? 'followup' : undefined
  const row =
    (inspectorId
      ? contextInspectorOrderRowFor(canonicalId, inspectorId, null, targetFilter)
      : null) ?? contextInspectorOrderRowFor(canonicalId, undefined, null, targetFilter)
  if (!row) return false
  const label = scheduleLabelFromContextInspectorOrder(row)
  if (!label) return false
  const date = dateFromScheduleDisplay(label) ?? parseServiceCycleDate(row.dateYmd)
  const slot = slotFromScheduleDisplay(label)
  if (!date || !slot) return false
  const insId = String(inspectorRowById(row.technicianId)?.id ?? row.technicianId)
  const assignment = preserveOccupiedSlotsOnAssignment(
    assignmentForOrderId(canonicalId),
    {
      orderId: canonicalId,
      lineIndex,
      inspectorId: insId,
      date,
      slot,
      timeLabel: label,
    },
    label,
  )
  assignmentsByOrderId.value = { ...assignmentsByOrderId.value, [canonicalId]: assignment }
  syncInspectorAssignmentRow(assignment)
  return true
}

function shouldSkipContextInspectorOrderForBooking(
  row: ScheduleContextInspectorOrder,
  exclude: ScheduleBookingExclude | null,
  opts?: { forConflict?: boolean; forSlotDisable?: boolean },
): boolean {
  if (isOrderScheduleCleared(row.orderId)) return true
  if (contextInspectorOrderShouldSkipOccupancy(row)) return true
  if (!exclude) return false
  if (!careOrderIdsReferToSameOrder(row.orderId, exclude.orderId)) return false
  if (
    opts?.forConflict &&
    (isFollowUpScheduleSessionForOrder(exclude.orderId) ||
      isRescheduleFollowUpSessionForOrder(exclude.orderId))
  ) {
    return true
  }
  if (
    !opts?.forConflict &&
    !opts?.forSlotDisable &&
    row.targetType === 'followup' &&
    exclude.lineIndex !== undefined &&
    exclude.orderId === row.orderId
  ) {
    return true
  }
  return false
}

function ingestScheduleContextInspectorOrders(rawData?: Record<string, unknown>): void {
  const rows: ScheduleContextInspectorOrder[] = []
  if (!rawData) {
    scheduleContextInspectorOrders.value = rows
    return
  }
  const inspectors = rawData.inspectors ?? rawData.technicians
  if (!Array.isArray(inspectors)) {
    scheduleContextInspectorOrders.value = rows
    return
  }
  for (const ins of inspectors) {
    if (ins == null || typeof ins !== 'object') continue
    const insObj = ins as Record<string, unknown>
    const technicianId = String(insObj.id ?? '').trim()
    if (!technicianId) continue
    const orders = insObj.orders
    if (!Array.isArray(orders)) continue
    for (const ord of orders) {
      if (ord == null || typeof ord !== 'object') continue
      const o = ord as Record<string, unknown>
      const orderId = String(o.order_id ?? o.orderId ?? '').trim()
      const dateRaw =
        typeof o.date === 'string'
          ? o.date.trim()
          : typeof o.service_date === 'string'
            ? o.service_date.trim()
            : ''
      const dateYmd = dateRaw.match(/(\d{4}-\d{2}-\d{2})/)?.[1] ?? ''
      if (!orderId || !dateYmd) continue
      const timeText =
        typeof o.time_text === 'string'
          ? o.time_text.trim()
          : typeof o.timeText === 'string'
            ? o.timeText.trim()
            : ''
      const timeStart =
        typeof o.time_start === 'string'
          ? o.time_start.trim()
          : typeof o.timeStart === 'string'
            ? o.timeStart.trim()
            : ''
      const timeEnd =
        typeof o.time_end === 'string'
          ? o.time_end.trim()
          : typeof o.timeEnd === 'string'
            ? o.timeEnd.trim()
            : ''
      const resolvedTimeText =
        timeText ||
        (timeStart && timeEnd ? `${timeStart} - ${timeEnd}` : timeStart || timeEnd)
      if (!resolvedTimeText) continue
      const targetType = String(o.target_type ?? o.targetType ?? 'order')
        .trim()
        .toLowerCase()
      const targetIdRaw = o.target_id ?? o.targetId ?? o.id
      const targetId =
        typeof targetIdRaw === 'number' && Number.isFinite(targetIdRaw) && targetIdRaw > 0
          ? Math.trunc(targetIdRaw)
          : typeof targetIdRaw === 'string' && /^\d+$/.test(targetIdRaw.trim())
            ? Math.trunc(Number(targetIdRaw))
            : undefined
      const addressRaw =
        typeof o.address === 'string'
          ? o.address.trim()
          : typeof o.service_address === 'string'
            ? o.service_address.trim()
            : typeof o.short_address === 'string'
              ? o.short_address.trim()
              : ''
      rows.push({
        technicianId,
        orderId,
        targetId,
        targetType,
        dateYmd,
        timeText: resolvedTimeText,
        address: addressRaw || undefined,
      })
    }
  }
  scheduleContextInspectorOrders.value = rows
  for (const row of rows) registerContextInspectorOrderCard(row)
}

function addOccupiedSlotsFromContextInspectorOrders(
  occupied: Set<string>,
  inspectorId: string,
  date: Date,
  exclude: ScheduleBookingExclude | null,
  opts?: { forConflict?: boolean },
): void {
  for (const row of scheduleContextInspectorOrders.value) {
    if (row.dateYmd !== ymdFromDate(date)) continue
    if (!isSameInspectorId(row.technicianId, inspectorId)) continue
    if (shouldSkipContextInspectorOrderForBooking(row, exclude, opts)) continue
    const label = scheduleLabelFromContextInspectorOrder(row)
    if (!label) continue
    const lineDate = dateFromScheduleDisplay(label)
    if (!lineDate || !isSameCalendarDay(lineDate, date)) continue
    for (const slot of occupiedSlotsFromScheduledAt(label, row.orderId)) occupied.add(slot)
  }
}

/** 指定检查员、日期下已被占用的时段（主单+跟进行；仅排除当前正在编辑的那一行） */
function collectOccupiedSlotsForInspectorDate(
  inspectorId: string,
  date: Date,
  exclude: ScheduleBookingExclude | null,
  opts?: { forConflict?: boolean; forSlotDisable?: boolean },
): Set<string> {
  const occupied = new Set<string>()
  for (const a of Object.values(assignmentsByOrderId.value)) {
    if (!orderCountsAsCommittedInspectorBooking(a.orderId, a.lineIndex, exclude, opts)) continue
    if (!isSameInspectorId(a.inspectorId, inspectorId)) continue
    const effective = effectiveAssignmentForOccupancy(a)
    if (!effective || !isSameCalendarDay(effective.date, date)) continue
    for (const slot of occupiedSlotsForAssignment(effective, { forceRecompute: true })) occupied.add(slot)
  }

  addOccupiedSlotsFromAssignedOrders(occupied, inspectorId, date, exclude, allOrdersForInspectorOccupancy(), opts)
  addOccupiedSlotsFromContextInspectorOrders(occupied, inspectorId, date, exclude, opts)

  return occupied
}

interface MinuteRange {
  start: number
  endExclusive: number
}

/** 用户所选格 → [首格起点, 末格起点+30min) */
function minuteRangeFromSelectedSlots(slots: string[]): MinuteRange | null {
  const ordered = sortSlotsByGridOrder(slots)
  if (!ordered.length) return null
  const startMin = gridSlotStartMinutes(ordered[0]!)
  const lastMin = gridSlotStartMinutes(ordered[ordered.length - 1]!)
  if (startMin == null || lastMin == null) return null
  return { start: startMin, endExclusive: lastMin + SLOT_INTERVAL_MINUTES }
}

/**
 * Assign 前冲突：只看 context/订单卡/assignment 的真实占用，不含 availability 白名单。
 * Follow-up 排期时整单已有预约（如主单 8–11）不与跟进行新时段冲突。
 */
function selectedSlotsHaveScheduleConflictForSelection(
  orderId: string,
  slots: string[],
  inspectorId: string,
  date: Date,
): boolean {
  const ordered = sortSlotsByGridOrder(slots)
  if (!ordered.length) return false
  const exclude: ScheduleBookingExclude = {
    orderId,
    lineIndex: scheduleConflictExcludeLineIndex(orderId),
  }

  const occupied = collectOccupiedSlotsForInspectorDate(inspectorId, date, exclude, { forConflict: true })
  for (const slot of ordered) {
    const canonical = canonicalGridSlot(slot) ?? slot
    if (occupied.has(canonical) || occupied.has(slot)) return true
  }
  return false
}

function selectedSlotsHaveScheduleConflict(orderId: string): boolean {
  const inspectorId = selectedInspectorId.value
  const date = selectedDate.value
  if (!inspectorId || !date) return false
  return selectedSlotsHaveScheduleConflictForSelection(
    orderId,
    [...selectedSlots.value],
    inspectorId,
    date,
  )
}

/** 订单行上后端已确认的预约时段文案（含日期+时刻；不含 service_date 周期日占位） */
function backendBookedScheduleLabelForLine(o: OrderCardDTO, lineIndex: number): string | null {
  const line = o.services[lineIndex]
  if (!line || schedulePoolOrderIsMainPendingSchedule(o)) return null
  if (!lineHasConfirmedScheduleTime(line, o)) return null
  const raw = (line.scheduledAt ?? '').trim()
  if (!raw || raw === '—') return null
  const formatted = formatOrderScheduleLineDisplay(raw)
  return formatted !== '—' ? formatted : null
}

/** 网格占用：使用后端订单行已确认时段 */
function resolvedBookedScheduleLabelForLine(o: OrderCardDTO, lineIndex: number): string | null {
  return backendBookedScheduleLabelForLine(o, lineIndex)
}

function scheduleLabelsEquivalent(a: string, b: string): boolean {
  const left = formatOrderScheduleLineDisplay(a.trim())
  const right = formatOrderScheduleLineDisplay(b.trim())
  if (left !== '—' && right !== '—' && left === right) return true
  return a.trim() === b.trim()
}

function assignmentFromBookedLabel(
  a: OrderScheduleAssignment,
  label: string,
): OrderScheduleAssignment | null {
  const trimmed = label.trim()
  if (!trimmed || trimmed === '—') return null
  const date = dateFromScheduleDisplay(trimmed)
  const slot = slotFromScheduleDisplay(trimmed)
  if (!date || !slot) return null
  return {
    ...a,
    date,
    slot,
    timeLabel: trimmed,
    occupiedSlots: undefined,
  }
}

/** 占用网格：以订单后端时段为准，丢弃与后端不符的 localStorage 脏 assignment */
function effectiveAssignmentForOccupancy(a: OrderScheduleAssignment): OrderScheduleAssignment | null {
  const o = findOrder(a.orderId)
  if (!o) {
    return isBookedScheduleDisplayLabel(a.timeLabel) ? a : null
  }

  const focus = focusPayload.value
  if (
    focus &&
    !isRescheduleMode.value &&
    isOrderInUnscheduledPool(o, focus) &&
    a.lineIndex === schedulePoolLineIndex(o, focus)
  ) {
    return null
  }

  const backend = backendBookedScheduleLabelForLine(o, a.lineIndex)
  if (backend) {
    return assignmentFromBookedLabel(a, backend)
  }

  return isBookedScheduleDisplayLabel(a.timeLabel) ? a : null
}

function orderLineHasBookedSchedule(
  line: OrderServiceLineDTO | undefined,
  order?: OrderCardDTO,
): boolean {
  if (!line) return false
  return lineHasConfirmedScheduleTime(line, order)
}

function addOccupiedSlotsFromOrdersWithBookedLines(
  disabled: Set<string>,
  inspectorId: string,
  date: Date,
  exclude: ScheduleBookingExclude | null,
  orders: OrderCardDTO[],
  opts?: { forConflict?: boolean; forSlotDisable?: boolean },
) {
  const seen = new Set<string>()
  for (const o of dedupeOrdersById(orders)) {
    if (seen.has(o.id)) continue
    seen.add(o.id)
    for (let li = 0; li < o.services.length; li++) {
      if (!orderCountsAsCommittedInspectorBooking(o.id, li, exclude, opts)) continue
      const line = o.services[li]!
      if (!orderLineHasBookedSchedule(line, o)) continue
      if (!orderLineMatchesInspector(line, inspectorId, o.id, li)) continue
      const label = resolvedBookedScheduleLabelForLine(o, li)
      if (!label) continue
      const lineDate = dateFromScheduleDisplay(label)
      if (!lineDate || !isSameCalendarDay(lineDate, date)) continue
      for (const slot of occupiedSlotsFromScheduledAt(label, o.id)) disabled.add(slot)
    }
  }
}

/** 指定检查员、日期是否仍有可点击时段（与 slotPickerDisabled 规则一致） */
function inspectorDateHasPickableSlot(inspectorId: string, date: Date): boolean {
  if (isPastCalendarDay(date) || !isDateSelectable(date)) return false
  if (fuPageInspectorSidebarViewOnlyMode()) return false
  const disabled = disabledSlotsForPicker(inspectorId, date, currentScheduleBookingExclude())
  for (const slot of pastSlotsForDate(date)) disabled.add(slot)
  return allGridSlots().some((slot) => !disabled.has(slot))
}

function findFirstPickableDateFromTodayForInspector(inspectorId: string): Date | null {
  return findFirstDateWithPickableSlotFromToday(weekDates.value, (d) =>
    inspectorDateHasPickableSlot(inspectorId, d),
  )
}

/** 当前检查员、当前日期下不可选时段：优先后端 availability，否则本地占用推算 */
const slotPickerDisabled = computed(() => {
  const inspectorId = selectedInspectorId.value
  const date = selectedDate.value
  if (!inspectorId || !date) return new Set<string>()

  const merged = disabledSlotsForPicker(inspectorId, date, currentScheduleBookingExclude())
  for (const slot of pastSlotsForDate(date)) merged.add(slot)
  if (fuPageInspectorSidebarViewOnlyMode()) {
    for (const slot of allGridSlots()) merged.add(slot)
  }
  return merged
})

/** 已排程结束边界格：不再额外高亮（与用户选格一致） */
function bookingEndBoundarySlotsForAssignment(_a: OrderScheduleAssignment): string[] {
  return []
}

/** 改期原预约浅蓝格：按完整展示文案推算占用格 + 结束边界（不读 context 短区间） */
function clockRangeFromBookedTimeLabel(
  timeLabel: string,
  anchorDate?: Date | null,
): { startHm: string; startAp: string; endHm: string; endAp: string } | null {
  const label = timeLabel.trim()
  if (!label || label === '—') return null
  if (anchorDate) {
    const scoped = clockRangeFromScheduleDisplayForDate(label, anchorDate)
    if (scoped) return scoped
  }
  const formatted = formatOrderScheduleLineDisplay(label)
  const text = formatted !== '—' ? formatted : label
  if (anchorDate) {
    const scoped = clockRangeFromScheduleDisplayForDate(text, anchorDate)
    if (scoped) return scoped
  }
  const rangeRe = /(\d{1,2}:\d{2})\s*(AM|PM)\s*-\s*(\d{1,2}:\d{2})\s*(AM|PM)/gi
  const ranges = [...text.matchAll(rangeRe)]
  const hit = ranges[ranges.length - 1]
  if (!hit?.[1] || !hit[2] || !hit[3] || !hit[4]) return null
  return {
    startHm: hit[1],
    startAp: hit[2].toUpperCase(),
    endHm: hit[3],
    endAp: hit[4].toUpperCase(),
  }
}

/** 起止时刻 → 真实占用格（每格 30 分钟；结束时刻落在格点时不额外加下一格） */
function slotsInClockRangeForDisplay(range: {
  startHm: string
  startAp: string
  endHm: string
  endAp: string
}): string[] {
  const startParts = parseClockParts(range.startHm, range.startAp)
  const endParts = parseClockParts(range.endHm, range.endAp)
  if (!startParts || !endParts) return []

  const apptStart = clockPartsToMinutes(startParts)
  const apptEnd = clockPartsToMinutes(endParts)

  if (apptEnd <= apptStart) {
    const only = findGridSlotIndexForClock(range.startHm, range.startAp)
    return only >= 0 ? [allGridSlots()[only]!] : []
  }

  const picked: string[] = []
  for (const slot of allGridSlots()) {
    const slotStart = gridSlotStartMinutes(slot)
    if (slotStart == null) continue
    if (slotStart >= apptStart && slotStart < apptEnd) picked.push(slot)
  }

  if (!picked.length) {
    const startOnly = findGridSlotIndexForClock(range.startHm, range.startAp)
    if (startOnly >= 0) picked.push(allGridSlots()[startOnly]!)
  }

  // 结束时刻为区间外边界（如 10:30–12:00），不再把结束格点追加为占用起点
  return sortSlotsByGridOrder(picked)
}

function displaySlotsFromBookedTimeLabel(
  timeLabel: string,
  date: Date,
  _startSlot?: string,
): string[] {
  const range = clockRangeFromBookedTimeLabel(timeLabel, date)
  if (!range) return []
  return slotsInClockRangeForDisplay(range)
}

function bookedTimeLabelMinuteSpan(
  timeLabel: string,
  date: Date,
): number {
  const range = clockRangeFromBookedTimeLabel(timeLabel, date)
  if (!range) return 0
  const startParts = parseClockParts(range.startHm, range.startAp)
  const endParts = parseClockParts(range.endHm, range.endAp)
  if (!startParts || !endParts) return 0
  const start = clockPartsToMinutes(startParts)
  const end = clockPartsToMinutes(endParts)
  return end > start ? end - start : 0
}

function originalRescheduleTimeLabelCandidates(orderId: string, date: Date): string[] {
  return bookedTimeLabelCandidatesForPicker(orderId, date)
}

const slotPickerBookingEndBoundaries = computed(() => {
  const boundaries = new Set<string>()
  const inspectorId = selectedInspectorId.value
  const date = selectedDate.value
  if (!inspectorId || !date) return boundaries

  const addBoundaries = (a: OrderScheduleAssignment) => {
    if (isOrderScheduleCleared(a.orderId)) return
    if (!isSameInspectorId(a.inspectorId, inspectorId)) return
    if (!isSameCalendarDay(a.date, date)) return
    for (const slot of bookingEndBoundarySlotsForAssignment(a)) boundaries.add(slot)
  }

  const exclude = currentScheduleBookingExclude()
  for (const a of Object.values(assignmentsByOrderId.value)) {
    if (!orderCountsAsCommittedInspectorBooking(a.orderId, a.lineIndex, exclude)) continue
    const effective = effectiveAssignmentForOccupancy(a)
    if (!effective) continue
    addBoundaries(effective)
  }

  for (const o of dedupeOrdersById(allOrdersForInspectorOccupancy())) {
    for (let li = 0; li < o.services.length; li++) {
      if (!orderCountsAsCommittedInspectorBooking(o.id, li, exclude)) continue
      const line = o.services[li]!
      if (!orderLineHasBookedSchedule(line, o)) continue
      if (!orderLineMatchesInspector(line, inspectorId, o.id, li)) continue
      const label = resolvedBookedScheduleLabelForLine(o, li)
      if (!label) continue
      const lineDate = dateFromScheduleDisplay(label)
      if (!lineDate || !isSameCalendarDay(lineDate, date)) continue
      const slot = slotFromScheduleDisplay(label)
      if (!slot) continue
      addBoundaries({
        orderId: o.id,
        lineIndex: li,
        inspectorId,
        date: lineDate,
        slot,
        timeLabel: label,
      })
    }
  }
  return boundaries
})

/** 改期：当前打开订单原先预约的时段（与灰掉的他人占用区分，用 #D9F0FF） */
const slotPickerCurrentOrderOriginal = computed(() => {
  const slots = new Set<string>()
  const inspectorId = selectedInspectorId.value
  const date = selectedDate.value
  const orderId = selectedOrderId.value
  if (!inspectorId || !date || !orderId || isOrderScheduleCleared(orderId)) return slots
  if (fuPageInspectorSidebarViewOnlyMode()) return slots
  const lineIndexForHighlight = effectiveSidebarLineIndexForOrder(orderId)
  if (
    isFuScheduleOrReschedulePageActive() &&
    !fuPageSidebarSelectionIsFuSchedulableLine(orderId, lineIndexForHighlight)
  ) {
    return slots
  }
  if (!hasActiveBookedAssignment()) return slots

  const lineIndex = lineIndexForHighlight
  const o = findOrder(orderId)
  const line = o?.services[lineIndex]
  const ctxRow = contextInspectorOrderRowFor(orderId, inspectorId, date)
  const lineBooked = !!line && orderLineHasBookedSchedule(line, o ?? undefined)
  const inspectorMatches =
    !!ctxRow || (lineBooked && orderLineMatchesInspector(line!, inspectorId, orderId, lineIndex))
  if (!inspectorMatches) return slots

  const assignment = assignmentForOrderId(orderId)
  const effectiveAssignment =
    assignment && assignment.lineIndex === lineIndex
      ? effectiveAssignmentForOccupancy(assignment)
      : null
  if (
    effectiveAssignment &&
    isSameInspectorId(effectiveAssignment.inspectorId, inspectorId) &&
    isSameCalendarDay(effectiveAssignment.date, date)
  ) {
    for (const slot of pickerSlotsFromAssignment(effectiveAssignment, { forceRecomputeOccupied: true })) {
      const canonical = canonicalGridSlot(slot) ?? slot
      if (slotGridIndex(canonical) >= 0) slots.add(canonical)
    }
    if (slots.size) return slots
  }

  if (ctxRow) {
    const ctxLabel = scheduleLabelFromContextInspectorOrder(ctxRow)
    if (ctxLabel) {
      for (const slot of displaySlotsFromBookedTimeLabel(ctxLabel, date)) {
        const canonical = canonicalGridSlot(slot) ?? slot
        if (slotGridIndex(canonical) >= 0) slots.add(canonical)
      }
      if (slots.size) return slots
    }
  }

  let bestSpan = -1
  let bestDisplay: string[] = []

  for (const timeLabel of bookedTimeLabelCandidatesForPicker(orderId, date)) {
    const display = displaySlotsFromBookedTimeLabel(timeLabel, date)
    const span = bookedTimeLabelMinuteSpan(timeLabel, date)
    if (span > bestSpan || (span === bestSpan && display.length > bestDisplay.length)) {
      bestSpan = span
      bestDisplay = display
    }
  }

  for (const slot of bestDisplay) slots.add(slot)
  return slots
})

const reassignEnabled = computed(() => {
  if (!isRescheduleMode.value) return true
  if (hasActiveBookedAssignment()) return false
  if (!rescheduleUserPickedSlots.value) return false
  if (!selectedInspectorId.value || !selectedDate.value || !selectedOrderId.value) return false
  if (!sortSlotsByGridOrder([...selectedSlots.value]).length) return false
  return !selectedSlotsHaveScheduleConflict(selectedOrderId.value)
})

/** Schedule：已选检查员/日期/连续时段且无冲突时可 Assign（与标题参考时长无关） */
const scheduleAssignEnabled = computed(() => {
  if (isRescheduleMode.value) return false
  if (fuPageInspectorSidebarViewOnlyMode()) return false
  if (hasActiveBookedAssignment()) return false
  if (!slotGridInteractionAllowed()) return false
  if (!selectedOrderId.value || !selectedInspectorId.value || !selectedDate.value) return false
  if (!sortSlotsByGridOrder([...selectedSlots.value]).length) return false
  return !selectedSlotsHaveScheduleConflict(selectedOrderId.value)
})

const assignEnabled = computed(() => {
  if (isRescheduleMode.value) {
    if (!sortSlotsByGridOrder([...selectedSlots.value]).length) return false
    return reassignEnabled.value
  }
  return scheduleAssignEnabled.value
})

const showInspectorSchedule = computed(() => !!selectedInspectorId.value)

function occupiedSlotAddressesForPicker(): Map<string, string> {
  const map = new Map<string, string>()
  const inspectorId = selectedInspectorId.value
  const date = selectedDate.value
  if (!inspectorId || !date) return map

  const putSlots = (slots: string[], address: string) => {
    const addr = address.trim()
    if (!addr) return
    for (const slot of slots) {
      const canonical = canonicalGridSlot(slot) ?? slot
      if (!map.has(canonical)) map.set(canonical, addr)
      if (canonical !== slot && !map.has(slot)) map.set(slot, addr)
    }
  }

  const exclude = currentScheduleBookingExclude()
  for (const a of Object.values(assignmentsByOrderId.value)) {
    if (!orderCountsAsCommittedInspectorBooking(a.orderId, a.lineIndex, exclude)) continue
    if (!isSameInspectorId(a.inspectorId, inspectorId)) continue
    const effective = effectiveAssignmentForOccupancy(a)
    if (!effective || !isSameCalendarDay(effective.date, date)) continue
    const addr = orderShortAddressForAssignment(effective.orderId)
    putSlots(occupiedSlotsForAssignment(effective, { forceRecompute: true }), addr)
    putSlots(bookingEndBoundarySlotsForAssignment(effective), addr)
  }

  const orders = allOrdersForInspectorOccupancy()
  const seen = new Set<string>()
  for (const o of orders) {
    if (seen.has(o.id)) continue
    seen.add(o.id)
    for (let li = 0; li < o.services.length; li++) {
      if (!orderCountsAsCommittedInspectorBooking(o.id, li, exclude)) continue
      const line = o.services[li]!
      if (!orderLineHasBookedSchedule(line, o)) continue
      if (!orderLineMatchesInspector(line, inspectorId, o.id, li)) continue
      const label = resolvedBookedScheduleLabelForLine(o, li)
      if (!label) continue
      const lineDate = dateFromScheduleDisplay(label)
      if (!lineDate || !isSameCalendarDay(lineDate, date)) continue
      const slot = slotFromScheduleDisplay(label)
      if (!slot) continue
      const addr = (line.address ?? '').trim().split(',')[0]?.trim() || o.homeId
      const assignmentLike: OrderScheduleAssignment = {
        orderId: o.id,
        lineIndex: li,
        inspectorId,
        date: lineDate,
        slot,
        timeLabel: label,
      }
      putSlots(occupiedSlotsForAssignment(assignmentLike, { forceRecompute: true }), addr)
      putSlots(bookingEndBoundarySlotsForAssignment(assignmentLike), addr)
    }
  }

  for (const row of scheduleContextInspectorOrders.value) {
    if (row.dateYmd !== ymdFromDate(date)) continue
    if (!isSameInspectorId(row.technicianId, inspectorId)) continue
    if (shouldSkipContextInspectorOrderForBooking(row, exclude)) continue
    const label = scheduleLabelFromContextInspectorOrder(row)
    if (!label) continue
    const lineDate = dateFromScheduleDisplay(label)
    if (!lineDate || !isSameCalendarDay(lineDate, date)) continue
    let addr = orderShortAddressForAssignment(row.orderId).trim()
    if (!addr || addr === row.orderId.trim()) {
      for (const o of orders) {
        if (!careOrderIdsReferToSameOrder(row.orderId, o.id) && !orderIdMatchesCard(row.orderId, o)) {
          continue
        }
        const line = o.services[lineIndexForOrder(o.id)] ?? o.services[0]
        addr =
          (line?.address ?? '').trim().split(',')[0]?.trim() ||
          o.homeId?.trim() ||
          orderShortAddressForAssignment(o.id)
        break
      }
    }
    putSlots(occupiedSlotsFromScheduledAt(label, row.orderId), addr)
  }
  return map
}

const occupiedSlotAddresses = computed(() => occupiedSlotAddressesForPicker())

const occupiedSlotHover = ref<{
  address: string
  style: { top: string; left: string }
} | null>(null)

function onOccupiedSlotEnter(slot: string, event: MouseEvent) {
  const canonical = canonicalGridSlot(slot) ?? slot
  const address =
    occupiedSlotAddresses.value.get(slot)?.trim() ||
    occupiedSlotAddresses.value.get(canonical)?.trim()
  if (!address) return
  const el = event.currentTarget as HTMLElement | null
  if (!el) return
  const r = el.getBoundingClientRect()
  occupiedSlotHover.value = {
    address,
    style: { top: `${r.bottom + 8}px`, left: `${r.left}px` },
  }
}

function onOccupiedSlotLeave() {
  occupiedSlotHover.value = null
}

const unassignEnabled = computed(() => hasActiveBookedAssignment())

const originalScheduledTimeParts = computed(() => {
  const snap = rescheduleEntrySnapshot.value
  if (snap) {
    return { time: snap.originalTime, inspector: snap.originalInspector }
  }
  if (!isRescheduleMode.value) return { time: '', inspector: '' }
  const orderId = selectedOrderId.value
  return orderId ? originalScheduledPartsForOrder(orderId) : { time: '', inspector: '' }
})

const preferredTimeSlotLabels = computed(() => {
  const snap = rescheduleEntrySnapshot.value
  if (snap) return snap.preferredTimes
  if (!isRescheduleMode.value) return [] as string[]
  const orderId = selectedOrderId.value
  const o = orderId ? findOrder(orderId) : null
  if (!o) return []
  return orderPreferredTimesList(o).map((pt) => formatOrderScheduleLineDisplay(pt))
})

function mergePreferredTimesOntoOrderCard(orderId: string, times: string[]) {
  if (!times.length) return
  const prev = findOrder(orderId)
  if (!prev) return
  const next: OrderCardDTO = { ...prev, preferredTimes: times }
  ordersById.value = { ...ordersById.value, [orderId]: next }
  const focus = focusPayload.value
  if (focus?.orderList) {
    const idx = focus.orderList.findIndex((o) => o.id === orderId)
    if (idx >= 0) focus.orderList[idx] = next
  }
}

async function hydratePreferredTimesForOrder(orderId: string) {
  if (!isCustomerRequestedReschedule.value || !orderId) return
  const o = findOrder(orderId)
  if (!o?.orderPrimaryId) return
  if (orderPreferredTimesList(o).length) return
  try {
    const detail = await fetchOrderDetailOnce(o.orderPrimaryId)
    const times = preferredTimesFromCareOrderRow(detail)
    mergePreferredTimesOntoOrderCard(orderId, times)
    const snap = rescheduleEntrySnapshot.value
    if (snap?.orderId === orderId && !snap.preferredTimes.length && times.length) {
      rescheduleEntrySnapshot.value = {
        ...snap,
        preferredTimes: times.map((pt) => formatOrderScheduleLineDisplay(pt)),
      }
    }
  } catch {
    /* 详情无首选时间则保持空 */
  }
}

function orderFuScheduleLineAwaitingAssignment(o: OrderCardDTO, focus: CareScheduleFocusPayload): boolean {
  const bucket = scheduleOrderBucket(o)
  if (
    bucket !== 'fu_pend_sched' &&
    !orderStatusIsFollowUpContext(o.status) &&
    !orderCardIsFuPendingSchedule(o)
  ) {
    return false
  }
  const idx =
    o.id === focus.orderId && isFollowUpCareLine(o.services[focus.lineIndex]!)
      ? focus.lineIndex
      : followUpScheduleLineIndex(o)
  const line = o.services[idx]
  return !!line && line.canSchedule !== false && isLineUnassignedForSchedulePool(line, o)
}

function pruneStaleScheduleAssignmentsForFuPendingPool() {
  if (isRescheduleMode.value) return
  const focus = focusPayload.value
  if (!focus || focus.mode === 'reschedule') return
  const next = { ...assignmentsByOrderId.value }
  let changed = false
  for (const orderId of Object.keys(next)) {
    const o = findOrder(orderId) ?? focus.orderList?.find((x) => x.id === orderId)
    if (!o || !orderFuScheduleLineAwaitingAssignment(o, focus)) continue
    delete next[orderId]
    changed = true
  }
  if (changed) assignmentsByOrderId.value = next
}

/** Schedule 左栏待排订单不应保留会话 assignment（多为 localStorage / service cycle 脏数据） */
function pruneStaleScheduleAssignmentsForUnscheduledPool() {
  if (isRescheduleMode.value) return
  const focus = focusPayload.value
  if (!focus || focus.mode === 'reschedule') return
  const next = { ...assignmentsByOrderId.value }
  let changed = false
  for (const orderId of Object.keys(next)) {
    const o = findOrder(orderId) ?? focus.orderList?.find((x) => x.id === orderId)
    if (!o) continue
    if (schedulePoolOrderIsMainPendingSchedule(o)) {
      delete next[orderId]
      changed = true
      continue
    }
    if (!isOrderInUnscheduledPool(o, focus)) continue
    delete next[orderId]
    changed = true
  }
  if (changed) assignmentsByOrderId.value = next
}

/** 待排 Follow-up / 左栏池内订单：丢弃 localStorage 里 lineIndex 不符的会话 assignment */
function dropStaleSessionAssignmentForOrder(orderId: string) {
  if (!orderId.trim() || isOrderScheduleCleared(orderId) || isRescheduleMode.value) return
  const focus = focusPayload.value
  if (!focus || focus.mode === 'reschedule') return
  const stale = assignmentsByOrderId.value[orderId]
  if (!stale) return
  const o = findOrder(orderId) ?? focus.orderList?.find((x) => x.id === orderId)
  if (!o) return

  let shouldDrop = false
  if (orderFuScheduleLineAwaitingAssignment(o, focus)) shouldDrop = true
  else if (isOrderInUnscheduledPool(o, focus)) shouldDrop = true
  else if (
    isFollowUpScheduleFocusSession(focus) &&
    focus.orderId === orderId &&
    stale.lineIndex !== focus.lineIndex
  ) {
    shouldDrop = true
  } else if (isFollowUpScheduleFocusSession(focus) && careOrderIdsReferToSameOrder(focus.orderId, orderId)) {
    const staleLine = o.services[stale.lineIndex]
    if (staleLine && !isFollowUpCareLine(staleLine)) shouldDrop = true
  }
  if (!shouldDrop) return

  const next = { ...assignmentsByOrderId.value }
  delete next[orderId]
  assignmentsByOrderId.value = next
}

/** 历史版本曾把已确认排程写入 careScheduleAssignments，现已不再持久化，仅清理遗留键 */
function removeLegacyScheduleAssignmentsStorage() {
  try {
    localStorage.removeItem(CARE_SCHEDULE_ASSIGNMENTS_KEY)
    sessionStorage.removeItem(CARE_SCHEDULE_ASSIGNMENTS_KEY)
  } catch {
    /* ignore */
  }
}

function hydrateAllAssignmentsFromFocusOrderList() {
  const focusId = focusPayload.value?.orderId?.trim()
  if (!focusId || isOrderScheduleCleared(focusId)) return
  const o =
    findOrder(focusId) ??
    focusPayload.value?.orderList?.find((x) => careOrderIdsReferToSameOrder(x.id, focusId))
  if (o) {
    registerOrderCards([ordersById.value[o.id] ?? o])
  }
  tryHydrateAssignmentFromOrder(focusId)
}


function isFollowUpScheduleFocusSession(focus: CareScheduleFocusPayload): boolean {
  if (focus.mode === 'reschedule') return false
  if (isFollowUpScheduleSessionForOrder(focus.orderId)) return true
  const o =
    findOrder(focus.orderId) ??
    focus.orderList?.find((x) => careOrderIdsReferToSameOrder(x.id, focus.orderId))
  const focusLine = o?.services[focus.lineIndex]
  if (focusLine && isFollowUpCareLine(focusLine)) return true
  const name = (focus.serviceName ?? '').trim().toLowerCase()
  if (name === 'follow-up care') return true
  return false
}

/** 当前为 FU 排期页（非 Reschedule） */
function isFollowUpSchedulePageActive(): boolean {
  const focus = focusPayload.value
  return !!focus && focus.mode !== 'reschedule' && isFollowUpScheduleFocusSession(focus)
}

/** 合并卡 / FU 状态订单（含 App 新建跟进、跟进行尚未写入 services 时） */
function orderHasFollowUpCareContext(o: OrderCardDTO): boolean {
  if (o.services.some(isFollowUpCareLine)) return true
  return orderIsFuStatusForInspectorSidebar(o)
}

/** FU Schedule / Reschedule：检查员侧栏只展示跟进行（主单仅日历灰格 + hover） */
function fuInspectorSidebarShowsFollowUpLinesOnly(): boolean {
  return isFuScheduleOrRescheduleSessionActive()
}

/** 合并卡主单行：FU 页不可在侧栏点选改期 */
function inspectorAssignmentIsMainLineOnFuMergedCard(o: OrderCardDTO, lineIndex: number): boolean {
  const line = o.services[lineIndex]
  if (!line || isFollowUpCareLine(line)) return false
  return orderHasFollowUpCareContext(o) || o.services.some(isFollowUpCareLine)
}

/** FU 排期 / 改期页（合并卡跟进行可操作，主单仅灰格展示） */
function isFuScheduleOrReschedulePageActive(): boolean {
  return isFuScheduleOrRescheduleSessionActive()
}

function inspectorSidebarSelectedAssignment(): InspectorAssignment | null {
  const orderId = inspectorSidebarSelectedOrderId.value.trim()
  const inspectorId = selectedInspectorId.value
  if (!orderId || !inspectorId) return null
  const ins = inspectors.value.find((i) => String(i.id) === String(inspectorId))
  if (!ins) return null
  const pickedLine = inspectorSidebarSelectedLineIndex.value
  return (
    ins.assignments.find((a) => {
      if (resolveCanonicalOrderId(a.orderId) !== resolveCanonicalOrderId(orderId)) return false
      if (pickedLine != null && a.lineIndex != null && a.lineIndex !== pickedLine) return false
      return true
    }) ?? null
  )
}

/** 规则2 / 非 FU 行：FU 页侧栏仅查看 — 网格全锁、无浅蓝、Unassign 不亮 */
function fuPageSidebarSelectionIsFuSchedulableLine(orderId: string, lineIndex: number): boolean {
  const o = findOrder(orderId)
  if (!o) return false
  const line = o.services[lineIndex]
  if (!line || !isFollowUpCareLine(line)) return false
  if (orderCardIsFuPendingSchedule(o)) return true
  if (orderHasSchedulableFollowUpLine(o)) return true
  if (scheduleOrderBucket(o) === 'fu_pend_sched') return true
  if (isRescheduleMode.value && isRescheduleFollowUpSessionForOrder(orderId)) return true
  if (scheduleOrderBucket(o) === 'fu_pend_resched') return true
  if (orderIsFuAssignedOrder(o)) return true
  if (line.canReschedule) return true
  return false
}

function fuPageInspectorSidebarViewOnlyMode(): boolean {
  if (!isFuScheduleOrReschedulePageActive()) return false
  if (!inspectorSidebarSelectedOrderId.value.trim()) return false
  const assignment = inspectorSidebarSelectedAssignment()
  if (assignment?.readonly) return true
  const orderId = inspectorSidebarSelectedOrderId.value
  const lineIndex =
    inspectorSidebarSelectedLineIndex.value ?? scheduleTargetLineIndexForOrder(orderId)
  const o = findOrder(orderId)
  if (o && inspectorAssignmentIsMainLineOnFuMergedCard(o, lineIndex)) return true
  return !fuPageSidebarSelectionIsFuSchedulableLine(orderId, lineIndex)
}

/** @deprecated alias — use fuPageInspectorSidebarViewOnlyMode */
function fuPageInspectorSidebarViewOnlyLocked(): boolean {
  return fuPageInspectorSidebarViewOnlyMode()
}

/** 规则1：FU 页侧栏点击可编辑跟进行 */
function fuPageInspectorSidebarEditableActive(): boolean {
  if (!isFuScheduleOrReschedulePageActive()) return false
  if (!inspectorSidebarSelectedOrderId.value.trim()) return false
  return !fuPageInspectorSidebarViewOnlyMode()
}

function effectiveSidebarLineIndexForOrder(orderId: string): number {
  if (
    isFuScheduleOrReschedulePageActive() &&
    inspectorSidebarSelectedOrderId.value &&
    careOrderIdsReferToSameOrder(inspectorSidebarSelectedOrderId.value, orderId) &&
    inspectorSidebarSelectedLineIndex.value != null
  ) {
    return inspectorSidebarSelectedLineIndex.value
  }
  return scheduleTargetLineIndexForOrder(orderId)
}

/** 时段网格是否允许点选（FU 页侧栏可编辑行与左栏待排池一致） */
function slotGridInteractionAllowed(): boolean {
  if (fuPageInspectorSidebarViewOnlyMode()) return false
  if (fuPageInspectorSidebarEditableActive()) return true
  return isUnscheduledPoolEditingAllowed()
}

function applyInspectorSidebarViewOnlyContext(
  orderId: string,
  inspectorId: string,
  lineIndex: number,
) {
  const o = findOrder(orderId)
  const line = o?.services[lineIndex]
  if (o && line) {
    const label = resolvedBookedScheduleLabelForLine(o, lineIndex)
    const lineDate = label ? dateFromScheduleDisplay(label) : null
    if (lineDate && !isFuScheduleOrReschedulePageActive()) navigateCalendarToDate(lineDate)
  }
  selectInspectorForSidebarAssignment(inspectorId)
  clearSelectedSlots()
  rescheduleUserPickedSlots.value = false
  void loadInspectorAvailability()
  rebuildInspectorAssignmentRows()
}

/** FU Pending Schedule：检查员侧栏点选订单（非左侧待排池切换） */
function fuScheduleInspectorSidebarDrivesOrder(orderId: string): boolean {
  if (!isFollowUpSchedulePageActive()) return false
  const id = orderId.trim()
  const sidebarId = inspectorSidebarSelectedOrderId.value.trim()
  const inspectorId = selectedInspectorId.value.trim()
  if (!id || !sidebarId || !inspectorId) return false
  return careOrderIdsReferToSameOrder(sidebarId, id)
}

/** 新建排程（非 Reschedule）：只能从左侧待排列表选单 */
function isInitialScheduleSessionActive(): boolean {
  return !isRescheduleMode.value
}

function unscheduledPanelSelectToastMessage(): string {
  const verb = isRescheduleMode.value ? 'reschedule' : 'schedule'
  return `Select an order from ${unscheduledPanelHeading.value} to ${verb}.`
}

/** 左侧列表内订单才可改时段（Schedule / Reschedule 均适用） */
function isUnscheduledPoolEditingAllowed(): boolean {
  if (!isInitialScheduleSessionActive() && !isRescheduleMode.value) return true
  const orderId = selectedOrderId.value
  if (!orderId) return false
  return unscheduled.value.some(
    (x) => x.orderId === orderId || careOrderIdsReferToSameOrder(x.orderId, orderId),
  )
}

function orderStatusIsCompleted(status: string | undefined): boolean {
  const raw = (status ?? '').trim()
  if (!raw || raw === '—') return false
  const s = raw.toLowerCase()
  if (s.includes('incomplete')) return false
  if (s.includes('pending')) return false
  if (/\bcompleted\b/.test(s) || /\bcompeted\b/.test(s)) return true
  if (/\bfu[\s_-]*completed\b/.test(s) || /\bfollow[\s-]*up[\s_-]*completed\b/.test(s)) return true
  return /\bcomplete\b/.test(s)
}

/** 改期会话是否针对 FU 状态订单（Reschedule Follow-up 池） */
function orderIsFuAssignedOrder(order: OrderCardDTO): boolean {
  const code = (order.serviceStatusCode ?? '').trim().toLowerCase()
  if (code === 'fu_assigned' || code === 'follow_up_assigned') return true
  const n = (order.status ?? '').trim().toLowerCase()
  return /\bfollow[\s-]*up\b/.test(n) && /\bassigned\b/.test(n) && !/\bpending\b/.test(n)
}

function isRescheduleFollowUpSessionForOrder(orderId: string): boolean {
  const o =
    findOrder(orderId) ??
    focusPayload.value?.orderList?.find((x) => x.id === orderId) ??
    assignedOrdersCache.value.find((card) => card.id === orderId)
  if (!o) return false
  const focus = focusPayload.value
  if (focus?.mode === 'reschedule' && focus.orderId === orderId) {
    const line = o.services[focus.lineIndex]
    if (line && isFollowUpCareLine(line)) return true
  }
  if (orderStatusIsFollowUpContext(o.status)) return true
  if (orderIsFuAssignedOrder(o)) return true
  if (scheduleOrderBucket(o) === 'fu_pend_resched') return true
  const code = (o.serviceStatusCode ?? '').trim().toLowerCase()
  return code === 'fu_pending_reschedule'
}

function isRescheduleFollowUpFocusSession(focus: CareScheduleFocusPayload): boolean {
  if (focus.mode !== 'reschedule') return false
  if (isRescheduleFollowUpSessionForOrder(focus.orderId)) return true
  const focusLine = focus.orderList?.find((x) => x.id === focus.orderId)?.services[focus.lineIndex]
  return !!(focusLine && isFollowUpCareLine(focusLine))
}

/** Reschedule Follow-up 左栏：仅 FU 状态且跟进行可改期 */
function followUpLineEligibleForReschedulePool(
  line: OrderServiceLineDTO,
  order: OrderCardDTO,
): boolean {
  if (!isFollowUpCareLine(line)) return false
  if (line.canReschedule) return true
  return lineHasConfirmedScheduleTime(line, order)
}

function orderBelongsToRescheduleFuPool(o: OrderCardDTO): boolean {
  const bucket = scheduleOrderBucket(o)
  const code = (o.serviceStatusCode ?? '').trim().toLowerCase()
  const isFuStatus =
    orderStatusIsFollowUpContext(o.status) ||
    bucket === 'fu_pend_resched' ||
    code === 'fu_pending_reschedule' ||
    code === 'fu_assigned' ||
    code === 'follow_up_assigned'
  if (!isFuStatus || bucket === 'fu_pend_sched') return false
  return o.services.some((l) => followUpLineEligibleForReschedulePool(l, o))
}

function orderBelongsToRescheduleMainPool(o: OrderCardDTO): boolean {
  if (orderStatusIsFollowUpContext(o.status)) return false
  const bucket = scheduleOrderBucket(o)
  if (bucket === 'fu_pend_resched') return false
  return o.services.some(
    (l) => !isFollowUpCareLine(l) && (l.canReschedule || lineHasConfirmedScheduleTime(l, o)),
  )
}

function orderMatchesRescheduleSession(o: OrderCardDTO, focus: CareScheduleFocusPayload): boolean {
  if (focus.mode !== 'reschedule') return true
  if (orderStatusIsCompleted(o.status)) return false
  if (
    !o.services.some((l) => l.canReschedule || lineHasConfirmedScheduleTime(l, o))
  ) {
    return false
  }
  const fuSession = isRescheduleFollowUpFocusSession(focus)
  return fuSession ? orderBelongsToRescheduleFuPool(o) : orderBelongsToRescheduleMainPool(o)
}

function followUpScheduleLineIndex(o: OrderCardDTO): number {
  const schedulableWithPk = o.services.findIndex(
    (l) =>
      isFollowUpCareLine(l) &&
      l.followUpPrimaryId != null &&
      l.followUpPrimaryId > 0 &&
      l.canSchedule !== false,
  )
  if (schedulableWithPk >= 0) return schedulableWithPk
  const schedulable = o.services.findIndex(
    (l) => isFollowUpCareLine(l) && l.canSchedule !== false,
  )
  if (schedulable >= 0) return schedulable
  const withPk = o.services.findIndex(
    (l) => isFollowUpCareLine(l) && l.followUpPrimaryId != null && l.followUpPrimaryId > 0,
  )
  if (withPk >= 0) return withPk
  const fu = o.services.findIndex((l) => isFollowUpCareLine(l))
  return fu >= 0 ? fu : 0
}

function followUpRescheduleLineIndex(o: OrderCardDTO): number {
  const reschedulable = o.services.findIndex((l) => isFollowUpCareLine(l) && l.canReschedule)
  if (reschedulable >= 0) return reschedulable
  const fu = o.services.findIndex((l) => isFollowUpCareLine(l))
  return fu >= 0 ? fu : 0
}

function mainScheduleLineIndex(o: OrderCardDTO): number {
  const schedulable = o.services.findIndex((l) => !isFollowUpCareLine(l) && l.canSchedule)
  if (schedulable >= 0) return schedulable
  const main = o.services.findIndex((l) => !isFollowUpCareLine(l))
  return main >= 0 ? main : 0
}

function schedulePoolLineIndex(o: OrderCardDTO, focus: CareScheduleFocusPayload): number {
  if (focus.mode === 'reschedule') return lineIndexForRescheduleCard(o)
  if (isFollowUpScheduleFocusSession(focus)) {
    return followUpScheduleLineIndex(o)
  }
  if (o.id === focus.orderId) return focus.lineIndex
  const schedulable = o.services.findIndex((l) => l.canSchedule || l.canReschedule)
  return schedulable >= 0 ? schedulable : 0
}

function lineIndexForScheduleCard(o: OrderCardDTO, focusOrderId: string, focusLineIndex: number): number {
  const focus = focusPayload.value
  if (focus && focus.mode !== 'reschedule') {
    return schedulePoolLineIndex(o, focus)
  }
  if (careOrderIdsReferToSameOrder(o.id, focusOrderId)) return focusLineIndex
  const schedulable = o.services.findIndex((l) => l.canSchedule || l.canReschedule)
  return schedulable >= 0 ? schedulable : 0
}

function lineIndexForRescheduleCard(o: OrderCardDTO): number {
  const focus = focusPayload.value
  if (focus?.mode === 'reschedule' && isRescheduleFollowUpFocusSession(focus)) {
    const fuIdx = o.services.findIndex((l) => isFollowUpCareLine(l) && l.canReschedule)
    if (fuIdx >= 0) return fuIdx
    return followUpRescheduleLineIndex(o)
  }
  if (focus?.mode === 'reschedule') {
    const mainIdx = o.services.findIndex((l) => !isFollowUpCareLine(l) && l.canReschedule)
    if (mainIdx >= 0) return mainIdx
  }
  const idx = o.services.findIndex((l) => l.canReschedule)
  return idx >= 0 ? idx : 0
}

/** 改期左侧列表：与订单列表 Reschedule 入口一致；Completed 不可改期 */
function isOrderEligibleForReschedule(o: OrderCardDTO): boolean {
  if (orderStatusIsCompleted(o.status)) return false
  return o.services.some((l) => l.canReschedule || lineHasConfirmedScheduleTime(l, o))
}

/** 仅 Pending Schedule、Follow-up-Pending Schedule（FU-Pending Schedule）可进入排程页 */
function isOrderEligibleForSchedule(status: string | undefined): boolean {
  const bucket = classifyCareSidebarSubBucket(status ?? '')
  return bucket === 'pend_sched' || bucket === 'fu_pend_sched'
}

function schedulePoolMainLineIsUnassigned(main: OrderServiceLineDTO): boolean {
  if (main.technicianRef?.trim()) return false
  const assign = (main.assignment ?? '').trim()
  if (!assign || assign === '—' || assign === '-') return true
  return isPendingAssignmentLabel(assign)
}

/** Schedule 左栏：主单待排期（含新建单接口误标 Assigned、行内仍为 Pending Assignment） */
function schedulePoolOrderIsMainPendingSchedule(o: OrderCardDTO): boolean {
  if (orderCardIsMainPendingSchedule(o)) return true
  if (orderStatusIsFollowUpContext(o.status)) return false
  const main = o.services.find((l) => !isFollowUpCareLine(l)) ?? o.services[0]
  if (!main || isFollowUpCareLine(main)) return false
  return schedulePoolMainLineIsUnassigned(main)
}

/** 服务行尚未分配检查员且未占用具体时段 */
function isLineUnassignedForSchedulePool(
  line: OrderServiceLineDTO | undefined,
  order?: OrderCardDTO,
): boolean {
  if (!line) return false
  if (order && !isFollowUpCareLine(line) && schedulePoolOrderIsMainPendingSchedule(order)) return true
  const assign = (line.assignment ?? '').trim()
  if (assign && !/pending\s+assignment/i.test(assign)) return false
  const sched = (line.scheduledAt ?? '').trim()
  if (!sched || sched === '—') return true
  if (/^pending\s+(schedule|reschedule|assignment)/i.test(sched)) return true
  if (/^follow-up-pending\s+(schedule|reschedule)/i.test(sched)) return true
  return !(dateFromScheduleDisplay(sched) && slotFromScheduleDisplay(sched))
}

/** 与订单列表 FU-Pending Schedule 子目录同一套归类（`classifyCareOrderCardBucket`） */
function orderCardBelongsToFuPendingSchedulePool(o: OrderCardDTO): boolean {
  if (orderStatusIsCompleted(o.status)) return false
  if (scheduleOrderBucket(o) === 'fu_pend_sched') return true
  return orderCardIsFuPendingSchedule(o)
}

/** FU Pending Schedule 左栏：跟进行待排即入池（勿因会话 assignment 脏数据漏单） */
function fuSchedulePoolLineEligible(o: OrderCardDTO, _focus: CareScheduleFocusPayload): boolean {
  if (!orderCardBelongsToFuPendingSchedulePool(o)) return false
  const fuIdx = o.services.findIndex((l) => isFollowUpCareLine(l))
  if (fuIdx < 0) {
    const main = o.services.find((l) => !isFollowUpCareLine(l))
    return !!(main && lineHasConfirmedScheduleTime(main, o))
  }
  const line = o.services[fuIdx]!
  if (line.canSchedule === false) return false
  return isLineUnassignedForSchedulePool(line, o)
}

/** Schedule Follow-up 左栏：仅 FU-Pending Schedule 且跟进行可排期 */
function orderBelongsToScheduleFuPool(o: OrderCardDTO): boolean {
  if (!orderCardIsFuPendingSchedule(o)) return false
  if (o.services.some((l) => isFollowUpCareLine(l) && l.canSchedule !== false)) return true
  const main = o.services.find((l) => !isFollowUpCareLine(l))
  if (
    main &&
    lineHasConfirmedScheduleTime(main, o) &&
    (orderStatusIsFollowUpContext(o.status) || scheduleOrderBucket(o) === 'fu_pend_sched')
  ) {
    return true
  }
  return false
}

/** Schedule 左栏：主单 Pending Schedule 与 FU Pending Schedule 分池，互不混入 */
function orderMatchesScheduleSession(o: OrderCardDTO, focus: CareScheduleFocusPayload): boolean {
  if (isFollowUpScheduleFocusSession(focus)) {
    return orderBelongsToScheduleFuPool(o)
  }
  const bucket = scheduleOrderBucket(o)
  return (
    (bucket === 'pend_sched' || schedulePoolOrderIsMainPendingSchedule(o)) &&
    !orderStatusIsFollowUpContext(o.status)
  )
}

function isOrderInUnscheduledPool(o: OrderCardDTO, focus: CareScheduleFocusPayload): boolean {
  if (focus.mode === 'reschedule') {
    if (!isOrderEligibleForReschedule(o)) return false
    return orderMatchesRescheduleSession(o, focus)
  }
  if (!orderMatchesScheduleSession(o, focus)) return false

  const idx = schedulePoolLineIndex(o, focus)
  const line = o.services[idx] ?? o.services[0]
  if (!isLineUnassignedForSchedulePool(line, o)) return false
  if (assignmentsByOrderId.value[o.id] && !scheduleClearedByUser.value.has(o.id)) {
    if (
      !schedulePoolOrderIsMainPendingSchedule(o) &&
      !orderFuScheduleLineAwaitingAssignment(o, focus)
    ) {
      return false
    }
  }
  return true
}


function applyDurationLabelsFromOrderDetails(detailsByOrderId: Record<string, CareOrderListItem>) {
  if (!Object.keys(detailsByOrderId).length) return
  const nextCache = { ...durationLabelsByOrderId.value }
  const nextPlanCache = { ...planLabelsByOrderId.value }
  for (const [orderId, detail] of Object.entries(detailsByOrderId)) {
    const planLabel = resolvePlanDisplayName(detail.plan_key)
    if (planLabel) nextPlanCache[orderId] = planLabel
    const item = unscheduled.value.find((x) => x.orderId === orderId)
    const durationLabel = durationLabelForOrderDetail(
      detail,
      item?.serviceCycleYmd ?? serviceCycleYmdForOrderId(orderId),
    )
    if (durationLabel) nextCache[orderId] = durationLabel
  }
  durationLabelsByOrderId.value = nextCache
  planLabelsByOrderId.value = nextPlanCache
  unscheduled.value = unscheduled.value.map((item) => {
    if (item.durationLabel.trim()) return item
    const durationLabel = nextCache[item.orderId]
    return durationLabel ? { ...item, durationLabel } : item
  })
}

async function hydrateDurationLabelForOrder(orderId: string) {
  if (!orderId) return
  const hasDuration = !!durationLabelForOrderId(orderId)
  const hasPlan = !!planLabelsByOrderId.value[orderId]?.trim()
  if (hasDuration && hasPlan) return
  await ensureCareMeta()
  const o = findOrder(orderId)
  if (!o?.orderPrimaryId) return
  try {
    const detail = await fetchOrderDetailOnce(o.orderPrimaryId)
    cachePlanLabelFromDetail(orderId, detail)
    const durationLabel = durationLabelForOrderDetail(detail, serviceCycleYmdForOrderId(orderId))
    if (durationLabel) {
      durationLabelsByOrderId.value = { ...durationLabelsByOrderId.value, [orderId]: durationLabel }
      unscheduled.value = unscheduled.value.map((item) =>
        item.orderId === orderId && !item.durationLabel.trim() ? { ...item, durationLabel } : item,
      )
    }
  } catch {
    /* 详情失败则标题暂不展示套餐名/时长段 */
  }
}

async function ensureCareMeta(): Promise<void> {
  if (careMeta.value) return
  try {
    careMeta.value = await postCareOrderMeta()
  } catch {
    careMeta.value = null
  }
}


/** 确保进入排程的订单在池内（缺失则补入），但不改变已有顺序 */

function ensureFollowUpScheduleFocusLineIndex(focus: CareScheduleFocusPayload): void {
  if (focus.mode === 'reschedule') return
  if (!isFollowUpScheduleFocusSession(focus)) return
  const o = findOrder(focus.orderId) ?? focus.orderList?.find((x) => x.id === focus.orderId)
  if (!o) return
  const fuIdx = followUpScheduleLineIndex(o)
  const fuLine = o.services[fuIdx]
  if (!fuLine || !isFollowUpCareLine(fuLine)) return
  focus.lineIndex = fuIdx
  focus.serviceName = (fuLine.serviceName ?? 'Follow-up Care').trim()
  const pk = resolveFollowUpPrimaryIdFromOrderCard(o, fuLine)
  if (pk != null && pk > 0) focus.followUpPrimaryId = pk
}


/** FU Pending Schedule：分页拉取 fu_pending_schedule（与 HM 列表合并逻辑一致） */

/** FU Pending Schedule：合并接口 fu_pending_schedule 全量与入口快照，补全左栏待排池 */


async function hydrateServiceCycleForOrder(orderId: string) {
  if (!orderId || serviceCycleDateForOrder(orderId)) return
  const o = findOrder(orderId)
  if (!o?.orderPrimaryId) return
  try {
    const detail = await fetchOrderDetailOnce(o.orderPrimaryId)
    const card = careOrderListItemToCardDto(detail)
    registerOrderCards([
      patchOrderCardTechnicianRefs(card, detail, technicianNoLookup.value),
    ])
  } catch {
    /* 详情失败则保持原数据 */
  }
}

function clearInspectorSidebarSelection() {
  inspectorSidebarSelectedOrderId.value = ''
  inspectorSidebarSelectedLineIndex.value = null
}

/** Reschedule：检查员下列表高亮并展开入口订单所在行 */
function syncInspectorSidebarSelectionForOrder(orderId: string) {
  if (!isRescheduleMode.value) return
  const canonicalId = resolveCanonicalOrderId(orderId)
  if (!canonicalId) return
  const focus = focusPayload.value
  const o = findOrder(canonicalId)
  inspectorSidebarSelectedOrderId.value = canonicalId
  inspectorSidebarSelectedLineIndex.value =
    o && focus && isRescheduleFollowUpFocusSession(focus)
      ? lineIndexForRescheduleCard(o)
      : rescheduleLineIndexForOrder(canonicalId)
  expandInspectorForSidebarOrder(canonicalId, inspectorSidebarSelectedLineIndex.value)
}

/** 展开并选中侧栏中承载该订单的检查员行 */
function expandInspectorForSidebarOrder(orderId: string, lineIndex: number) {
  const focusId = focusPayload.value?.orderId?.trim()
  const canonicalId = resolveCanonicalOrderId(orderId)
  const lineMatches = (a: InspectorAssignment) =>
    a.lineIndex == null || lineIndex < 0 || a.lineIndex === lineIndex
  const matchAssignment = (a: InspectorAssignment, requireExact: boolean) => {
    if (requireExact && focusId) {
      return a.orderId.trim() === focusId && lineMatches(a)
    }
    if (!careOrderIdsReferToSameOrder(a.orderId, canonicalId)) return false
    return lineMatches(a)
  }
  for (const ins of inspectors.value) {
    let hit = focusId
      ? ins.assignments.find((a) => matchAssignment(a, true))
      : undefined
    if (!hit) hit = ins.assignments.find((a) => matchAssignment(a, false))
    if (!hit) {
      hit = ins.assignments.find((a) => careOrderIdsReferToSameOrder(a.orderId, canonicalId))
    }
    if (!hit) continue
    const key = String(ins.id)
    selectedInspectorId.value = key
    expandedInspectorIds.value = new Set([key])
    if (hit.lineIndex != null) {
      inspectorSidebarSelectedLineIndex.value = hit.lineIndex
    }
    return
  }
  const locked = lockedInspectorIdForOrder(orderId)
  if (locked) {
    const key = String(inspectorRowById(locked)?.id ?? locked)
    selectedInspectorId.value = key
    expandedInspectorIds.value = new Set([key])
  }
}

/** Schedule FU：与 Reschedule 一致，侧栏高亮并展开承载该单的检查员 */
function syncInspectorSidebarSelectionForScheduleOrder(orderId: string, lineIndex: number) {
  if (isRescheduleMode.value || !isFollowUpSchedulePageActive()) return
  const canonicalId = resolveCanonicalOrderId(orderId)
  if (!canonicalId) return
  inspectorSidebarSelectedOrderId.value = canonicalId
  inspectorSidebarSelectedLineIndex.value = lineIndex
  if (orderHasActiveBookedAssignment(canonicalId, lineIndex)) {
    expandInspectorForSidebarOrder(canonicalId, lineIndex)
  }
}

function resolveInspectorIdByName(name: string): string {
  const n = name.trim()
  if (!n) return ''
  const found = inspectors.value.find((i) => i.name.trim() === n)
  if (!found) return ''
  return String(inspectorRowById(found.id)?.id ?? found.id)
}

/** 已排期 Reschedule 或 Unassign 后 Schedule：锁定原检查员 */
function inspectorLockedForFocusOrder(): boolean {
  const orderId = selectedOrderId.value?.trim()
  if (!orderId) return false
  if (isRescheduleMode.value && hasActiveBookedAssignment()) return true
  if (!isRescheduleMode.value && scheduleClearedByUser.value.has(orderId)) return true
  return false
}

function lockedInspectorIdForOrder(orderId: string): string {
  const canonicalId = resolveCanonicalOrderId(orderId)?.trim()
  if (!canonicalId) return ''

  const selected = selectedInspectorId.value?.trim()
  if (selected && inspectorLockedForFocusOrder()) {
    return String(inspectorRowById(selected)?.id ?? selected)
  }

  const assignment = assignmentsByOrderId.value[canonicalId]
  if (assignment?.inspectorId) {
    return String(inspectorRowById(assignment.inspectorId)?.id ?? assignment.inspectorId)
  }

  const snap = rescheduleEntrySnapshot.value
  if (snap?.orderId && careOrderIdsReferToSameOrder(snap.orderId, canonicalId) && snap.originalInspector) {
    const fromSnap = resolveInspectorIdByName(snap.originalInspector)
    if (fromSnap) return fromSnap
  }

  const assignName = lineAssignmentNameForOrder(canonicalId)
  if (assignName) {
    const fromName = resolveInspectorIdByName(assignName)
    if (fromName) return fromName
  }

  for (const row of scheduleContextInspectorOrders.value) {
    if (!careOrderIdsReferToSameOrder(row.orderId, canonicalId)) continue
    if (!scheduleLabelFromContextInspectorOrder(row)) continue
    if (row.technicianId) {
      return String(inspectorRowById(row.technicianId)?.id ?? row.technicianId)
    }
  }

  return selected ?? ''
}

function isInspectorSelectable(inspectorId: string): boolean {
  if (!inspectorLockedForFocusOrder()) return true
  const orderId = selectedOrderId.value?.trim()
  if (!orderId) return true
  const locked = lockedInspectorIdForOrder(orderId)
  if (!locked) return true
  return isSameInspectorId(inspectorId, locked)
}

function syncInspectorSidebarSelectionForFocusOrder(orderId: string) {
  const canonicalId = resolveCanonicalOrderId(orderId)
  if (!canonicalId) return
  const focus = focusPayload.value
  inspectorSidebarSelectedOrderId.value = canonicalId
  if (isRescheduleMode.value && focus && isRescheduleFollowUpFocusSession(focus)) {
    const o = findOrder(canonicalId)
    inspectorSidebarSelectedLineIndex.value = o ? lineIndexForRescheduleCard(o) : rescheduleLineIndexForOrder(canonicalId)
  } else if (isRescheduleMode.value) {
    inspectorSidebarSelectedLineIndex.value = rescheduleLineIndexForOrder(canonicalId)
  } else {
    inspectorSidebarSelectedLineIndex.value = scheduleTargetLineIndexForOrder(canonicalId)
  }
  const locked = lockedInspectorIdForOrder(canonicalId)
  if (locked) ensureInspectorExpanded(locked)
}

function selectInspector(id: string) {
  if (!isInspectorSelectable(id)) return
  const key = String(inspectorRowById(id)?.id ?? id)
  expandedInspectorIds.value = new Set([key])
  selectedInspectorId.value = key
  hideToast()
  rebuildInspectorAssignmentRows()

  if (isRescheduleMode.value) {
    applyRescheduleCalendarAnchor()
    return
  }

  if (hasActiveBookedAssignment()) {
    const orderId = selectedOrderId.value
    if (orderId) {
      const assignment = assignmentForOrderId(orderId)
      let bookedInsId = assignment?.inspectorId ?? ''
      if (!bookedInsId) {
        const assignName = lineAssignmentNameForOrder(orderId)
        if (assignName) {
          const found = inspectors.value.find((i) => i.name.trim() === assignName)
          if (found) bookedInsId = found.id
        }
      }
      if (bookedInsId && isSameInspectorId(bookedInsId, key)) {
        applyBookedOrderContext(orderId)
        return
      }
    }
  }

  if (!selectedDate.value) {
    const match = findFirstPickableDateFromTodayForInspector(key)
    if (match) navigateCalendarToDate(match)
  }

  syncScheduleSlotsForActiveInspector()
}

/** Schedule：切换检查员时保留右侧已选日期，仅刷新该检查员在当日的时段选择 */
function syncScheduleSlotsForActiveInspector() {
  const orderId = selectedOrderId.value
  const assignment = orderId ? assignmentsByOrderId.value[orderId] : null
  const inspectorId = selectedInspectorId.value
  if (
    assignment &&
    orderId &&
    inspectorId &&
    !scheduleClearedByUser.value.has(orderId) &&
    isSameInspectorId(assignment.inspectorId, inspectorId) &&
    selectedDate.value &&
    isSameCalendarDay(assignment.date, selectedDate.value)
  ) {
    if (!isUnscheduledPoolEditingAllowed()) {
      clearSelectedSlots()
      return
    }
    selectedSlots.value = new Set(pickerSlotsFromAssignment(assignment))
    return
  }
  if (!hasActiveBookedAssignment()) {
    clearSelectedSlots()
  }
}

/** 已选中的检查员：展开其订单列表（与行点击展开一致） */
function ensureInspectorExpanded(inspectorId: string) {
  const key = String(inspectorRowById(inspectorId)?.id ?? inspectorId)
  if (!key) return
  expandedInspectorIds.value = new Set([key])
  selectedInspectorId.value = key
}

function selectInspectorForSidebarAssignment(inspectorId: string) {
  if (!isInspectorSelectable(inspectorId)) return
  const key = String(inspectorRowById(inspectorId)?.id ?? inspectorId)
  if (!key) return
  expandedInspectorIds.value = new Set([key])
  selectedInspectorId.value = key
  hideToast()
  rebuildInspectorAssignmentRows()
}

function isInspectorExpanded(id: string): boolean {
  const key = String(inspectorRowById(id)?.id ?? id)
  return expandedInspectorIds.value.has(key)
}

function toggleInspector(id: string) {
  if (!isInspectorSelectable(id)) return
  const key = String(inspectorRowById(id)?.id ?? id)
  clearInspectorSidebarSelection()

  if (String(selectedInspectorId.value) === key && isInspectorExpanded(id)) {
    const orderId = selectedOrderId.value?.trim()
    const locked = orderId ? lockedInspectorIdForOrder(orderId) : ''
    if (inspectorLockedForFocusOrder() && locked && isSameInspectorId(key, locked)) {
      return
    }
    const next = new Set(expandedInspectorIds.value)
    next.delete(key)
    expandedInspectorIds.value = next
    selectedInspectorId.value = ''
    hideToast()
    rebuildInspectorAssignmentRows()
    return
  }

  selectInspector(id)
}

function isInspectorRowSelected(ins: InspectorRow): boolean {
  if (!selectedInspectorId.value) return false
  return String(ins.id) === String(selectedInspectorId.value)
}

function inspectorChevronIcon(ins: InspectorRow): string {
  return isInspectorRowSelected(ins) ? imgInspectorChevronBlack : imgInspectorChevron
}

function isInspectorAssignmentSelected(ins: InspectorRow, a: InspectorAssignment): boolean {
  const picked = inspectorSidebarSelectedOrderId.value.trim()
  if (!picked || !selectedInspectorId.value) return false
  if (String(ins.id) !== String(selectedInspectorId.value)) return false
  if (!careOrderIdsReferToSameOrder(a.orderId, picked)) return false
  const pickedLine = inspectorSidebarSelectedLineIndex.value
  if (pickedLine != null && a.lineIndex != null && a.lineIndex !== pickedLine) {
    const sameOrderRows = ins.assignments.filter((row) =>
      careOrderIdsReferToSameOrder(row.orderId, picked),
    )
    if (sameOrderRows.length > 1) return false
  }
  return true
}

function selectInspectorAssignmentOrder(orderId: string, inspectorId: string, assignment?: InspectorAssignment) {
  const canonicalId = resolveCanonicalOrderId(orderId)
  const focusId = focusPayload.value?.orderId?.trim()
  if (focusId && inspectorSidebarBlocksNonFocusOrder(canonicalId, focusId, careOrderIdsReferToSameOrder)) {
    return
  }
  if (
    assignment &&
    !inspectorSidebarAssignmentClickable({
      orderId: assignment.orderId,
      readonly: assignment.readonly,
      focusOrderId: focusId,
      idsReferToSameOrder: careOrderIdsReferToSameOrder,
      allowReadonlyViewOnly: isFuScheduleOrReschedulePageActive(),
    })
  ) {
    return
  }
  hideToast()
  const lineIndex = assignment?.lineIndex ?? scheduleTargetLineIndexForOrder(canonicalId)
  const cached = assignedOrdersCache.value.find(
    (o) => o.id === canonicalId || orderIdMatchesCard(orderId, o),
  )
  if (cached) registerOrderCards([cached])

  const orderForLine = findOrder(canonicalId)
  const mainOnFuMerged =
    !!orderForLine && inspectorAssignmentIsMainLineOnFuMergedCard(orderForLine, lineIndex)

  // FU Schedule / Reschedule：已完成主单仅查看（可点进看时间，Unassign 不亮）；跟进行可编辑
  if (isFuScheduleOrReschedulePageActive()) {
    const viewOnly =
      !!assignment?.readonly ||
      mainOnFuMerged ||
      !fuPageSidebarSelectionIsFuSchedulableLine(canonicalId, lineIndex)
    inspectorSidebarSelectedOrderId.value = canonicalId
    inspectorSidebarSelectedLineIndex.value = lineIndex
    if (viewOnly) {
      void hydrateDurationLabelForOrder(canonicalId)
      applyInspectorSidebarViewOnlyContext(canonicalId, inspectorId, lineIndex)
      selectedOrderId.value = canonicalId
      return
    }
  }

  inspectorSidebarSelectedOrderId.value = canonicalId
  inspectorSidebarSelectedLineIndex.value = lineIndex
  if (isFuScheduleOrReschedulePageActive()) {
    selectInspectorForSidebarAssignment(inspectorId)
  } else {
    selectInspector(inspectorId)
  }
  void hydrateDurationLabelForOrder(canonicalId)

  if (scheduleClearedByUser.value.has(canonicalId)) {
    clearSelectedSlots()
    rescheduleUserPickedSlots.value = false
  } else if (orderHasActiveBookedAssignment(canonicalId)) {
    applyBookedOrderContext(canonicalId, {
      preserveWeekAnchor: isFuScheduleOrReschedulePageActive(),
    })
    void loadInspectorAvailability()
  } else if (!isRescheduleMode.value) {
    const targetDate = originalAppointmentDateForOrder(canonicalId)
    if (targetDate && !isFuScheduleOrReschedulePageActive()) navigateCalendarToDate(targetDate)
    const sessionAssignment = Object.values(assignmentsByOrderId.value).find((a) =>
      careOrderIdsReferToSameOrder(a.orderId, canonicalId),
    )
    if (sessionAssignment) {
      selectedSlots.value = isSameInspectorId(sessionAssignment.inspectorId, inspectorId)
        ? new Set(pickerSlotsFromAssignment(sessionAssignment))
        : new Set()
    } else {
      clearSelectedSlots()
    }
  } else {
    applyBookedOrderContext(canonicalId)
    void loadInspectorAvailability()
  }

  selectedOrderId.value = canonicalId
  rebuildInspectorAssignmentRows()
}

function applyServiceCycleWeekAnchorForOrder(orderId: string) {
  const allowed = serviceCycleDateForOrder(orderId)
  if (allowed) {
    weekAnchor.value = new Date(allowed.getFullYear(), allowed.getMonth(), allowed.getDate())
  }
}

/** 待排 Schedule：默认周锚到今天所在周，不用 service cycle 周 */
function shouldUseTodayWeekForDefaultSchedulePick(): boolean {
  if (isRescheduleMode.value) return false
  const orderId = selectedOrderId.value?.trim()
  if (!orderId) return false
  if (orderHasActiveBookedAssignment(orderId)) return false
  if (fuPageInspectorSidebarViewOnlyMode()) return false
  return true
}

function anchorWeekToToday(): void {
  weekAnchor.value = startOfWeekSunday(new Date())
}

function resetSchedulePickerForUnassignedOrder(orderId: string) {
  clearInspectorSidebarSelection()
  selectedInspectorId.value = ''
  expandedInspectorIds.value = new Set()
  selectedDate.value = null
  clearSelectedSlots()
  hideToast()
  occupiedSlotHover.value = null
  if (shouldUseTodayWeekForDefaultSchedulePick()) {
    anchorWeekToToday()
  } else {
    applyServiceCycleWeekAnchorForOrder(orderId)
  }
  rebuildInspectorAssignmentRows()
  void applyDefaultInspectorAndDateIfEmpty()
}

function selectUnscheduled(item: UnscheduledItem) {
  clearInspectorSidebarSelection()
  const wasClearedByUnassign = scheduleClearedByUser.value.has(item.orderId)
  scheduleDateLockReleased.value = wasClearedByUnassign || isRescheduleMode.value
  dropStaleSessionAssignmentForOrder(item.orderId)
  selectedOrderId.value = item.orderId
  if (item.serviceCycleYmd) {
    serviceCycleByOrderId.value = { ...serviceCycleByOrderId.value, [item.orderId]: item.serviceCycleYmd }
  }
  const assignment = assignmentsByOrderId.value[item.orderId]
  if (assignment && !scheduleClearedByUser.value.has(item.orderId)) {
    syncAssignmentUiForSelectedOrder()
    return
  }
  if (isRescheduleMode.value) {
    if (scheduleClearedByUser.value.has(item.orderId)) {
      clearSelectedSlots()
      rescheduleUserPickedSlots.value = false
      if (!selectedDate.value) navigateCalendarToDate(rescheduleAnchorDateForOrder(item.orderId))
      syncInspectorSidebarSelectionForOrder(item.orderId)
      rebuildInspectorAssignmentRows()
      return
    }
    applyRescheduleOrderContext(item.orderId)
    const focus = focusPayload.value
    if (focus && isRescheduleFollowUpFocusSession(focus)) {
      syncInspectorSidebarSelectionForOrder(item.orderId)
    }
    return
  }
  if (isFollowUpSchedulePageActive()) {
    syncInspectorSidebarSelectionForScheduleOrder(
      item.orderId,
      scheduleTargetLineIndexForOrder(item.orderId),
    )
    return
  }
  if (scheduleClearedByUser.value.has(item.orderId)) {
    syncInspectorSidebarSelectionForFocusOrder(item.orderId)
    rebuildInspectorAssignmentRows()
    return
  }
  resetSchedulePickerForUnassignedOrder(item.orderId)
}

function clearSelectedSlots() {
  selectedSlots.value = new Set()
}

function clearSelectedTimeSlots() {
  clearSelectedSlots()
  if (isRescheduleMode.value) rescheduleUserPickedSlots.value = false
}

function slotGridIndex(slot: string): number {
  const m = slot.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (m?.[1] && m[2] && m[3]) return findGridSlotIndexForClock(`${m[1]}:${m[2]}`, m[3])
  const all = allGridSlots()
  const norm = normalizeGridSlot(slot)
  return all.findIndex((s) => normalizeGridSlot(s) === norm)
}

function sortSlotsByGridOrder(slots: string[]): string[] {
  const idx = (s: string) => {
    const i = slotGridIndex(s)
    return i < 0 ? 9999 : i
  }
  return [...slots].sort((a, b) => idx(a) - idx(b))
}

/**
 * 按网格顺序将已选时段扩展为 [最小索引, 最大索引] 的连续区间（Schedule / Reschedule 共用）。
 * 可选格数不受标题参考时长限制，仅受网格连续性与占用冲突约束。
 */
function applyContiguousSlotSelection(slots: Iterable<string>): Set<string> {
  const ordered = sortSlotsByGridOrder([...slots])
  if (!ordered.length) return new Set()

  const startIdx = slotGridIndex(ordered[0]!)
  const endIdx = slotGridIndex(ordered[ordered.length - 1]!)
  if (startIdx < 0 || endIdx < 0) return new Set(ordered)

  const all = allGridSlots()
  const from = Math.min(startIdx, endIdx)
  const to = Math.max(startIdx, endIdx)
  const filled = new Set<string>()
  for (let i = from; i <= to; i++) {
    const cell = all[i]
    if (cell) filled.add(cell)
  }
  return filled
}

function gridSlotsStrictlyBetween(startIdx: number, endIdx: number): string[] {
  const all = allGridSlots()
  const from = Math.min(startIdx, endIdx)
  const to = Math.max(startIdx, endIdx)
  const middle: string[] = []
  for (let i = from + 1; i < to; i++) {
    const cell = all[i]
    if (cell) middle.push(cell)
  }
  return middle
}

/** FU 排期/改期：合并卡主单已占时段（灰色展示，不阻挡跟进行选格） */
function mergedMainLineOccupiedSlotsForSelectedOrder(): Set<string> {
  const slots = new Set<string>()
  const orderId = selectedOrderId.value
  if (
    !orderId ||
    (!isFollowUpScheduleSessionForOrder(orderId) && !isRescheduleFollowUpSessionForOrder(orderId))
  ) {
    return slots
  }
  const inspectorId = selectedInspectorId.value
  const date = selectedDate.value
  if (!inspectorId || !date) return slots
  const o = findOrder(orderId)
  if (!o) return slots
  const mainIdx = o.services.findIndex((l) => !isFollowUpCareLine(l))
  if (mainIdx < 0) return slots
  const line = o.services[mainIdx]!
  if (!orderLineHasBookedSchedule(line, o)) return slots
  if (!orderLineMatchesInspector(line, inspectorId, orderId, mainIdx)) return slots
  const label = resolvedBookedScheduleLabelForLine(o, mainIdx)
  if (!label) return slots
  const lineDate = dateFromScheduleDisplay(label)
  if (!lineDate || !isSameCalendarDay(lineDate, date)) return slots
  for (const slot of occupiedSlotsFromScheduledAt(label, orderId)) {
    const canonical = canonicalGridSlot(slot) ?? slot
    slots.add(canonical)
    if (canonical !== slot) slots.add(slot)
  }
  return slots
}

/** 连续选格时，中间是否夹有其他订单已占用的灰格（改期原预约浅蓝格除外） */
function middleSlotsHaveOtherTaskOccupancy(startIdx: number, endIdx: number): boolean {
  if (startIdx < 0 || endIdx < 0 || startIdx === endIdx) return false
  const addresses = occupiedSlotAddresses.value
  const ownOriginal = slotPickerCurrentOrderOriginal.value
  const mergedMainSlots = mergedMainLineOccupiedSlotsForSelectedOrder()
  for (const slot of gridSlotsStrictlyBetween(startIdx, endIdx)) {
    if (ownOriginal.has(slot)) continue
    if (mergedMainSlots.has(slot)) continue
    if (addresses.has(slot)) return true
  }
  return false
}

/** 中间存在不可选灰格（非其他任务占用，如 availability / 过去时段）时也禁止扩展连续区间 */
function middleSlotsHaveNonSelectableGap(startIdx: number, endIdx: number): boolean {
  if (startIdx < 0 || endIdx < 0 || startIdx === endIdx) return false
  const disabled = slotPickerDisabled.value
  const ownOriginal = slotPickerCurrentOrderOriginal.value
  const addresses = occupiedSlotAddresses.value
  const mergedMainSlots = mergedMainLineOccupiedSlotsForSelectedOrder()
  for (const slot of gridSlotsStrictlyBetween(startIdx, endIdx)) {
    if (ownOriginal.has(slot)) continue
    if (mergedMainSlots.has(slot)) continue
    if (addresses.has(slot)) continue
    if (disabled.has(slot)) return true
  }
  return false
}

function shrinkContiguousSlotSelection(current: Set<string>, slot: string): Set<string> {
  const ordered = sortSlotsByGridOrder([...current])
  if (!ordered.length) return new Set()

  const clickIdx = slotGridIndex(slot)
  const minIdx = slotGridIndex(ordered[0]!)
  const maxIdx = slotGridIndex(ordered[ordered.length - 1]!)
  if (clickIdx < 0 || minIdx < 0 || maxIdx < 0) {
    const next = new Set(current)
    next.delete(slot)
    return applyContiguousSlotSelection(next)
  }

  if (minIdx === maxIdx) return new Set()

  const all = allGridSlots()
  if (clickIdx === minIdx) {
    return applyContiguousSlotSelection(all.slice(minIdx + 1, maxIdx + 1).filter((s): s is string => Boolean(s)))
  }
  if (clickIdx === maxIdx) {
    return applyContiguousSlotSelection(all.slice(minIdx, maxIdx).filter((s): s is string => Boolean(s)))
  }
  return new Set()
}

function appendEndBoundaryToSlotSet(slots: Set<string>, timeLabel: string, date: Date): void {
  const endSlot = endBoundaryGridSlotFromTimeLabel(timeLabel, date)
  if (endSlot) slots.add(endSlot)
}

/** 当前选中区间结束时刻格（如 05:00PM），与占用格一并高亮 */
function isSlotEndBoundaryOfCurrentPicker(slot: string): boolean {
  const ordered = sortSlotsByGridOrder([...selectedSlots.value])
  if (!ordered.length) return false
  const endSlot = endBoundarySlotFromSelectedSlots(ordered)
  if (!endSlot) return false
  return normalizeGridSlot(slot) === normalizeGridSlot(endSlot)
}

function isSlotSelected(slot: string): boolean {
  const canonical = canonicalGridSlot(slot) ?? slot
  return selectedSlots.value.has(canonical) || selectedSlots.value.has(slot)
}

function selectDate(d: Date) {
  if (!isDateSelectable(d)) return

  const switchingDay =
    !!selectedDate.value && !isSameCalendarDay(selectedDate.value, d)

  if (switchingDay) {
    if (hasActiveBookedAssignment()) {
      showToast('warning', 'Please unassign the current schedule first')
      return
    }
    if (selectedSlots.value.size > 0) {
      showToast('warning', 'Please clear the selected time first.')
      return
    }
  }

  if (!slotGridInteractionAllowed()) return
  selectedDate.value = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  hideToast()
  if (isRescheduleMode.value) {
    rescheduleUserPickedSlots.value = false
    clearSelectedSlots()
    rebuildInspectorAssignmentRows()
    return
  }
  const orderId = selectedOrderId.value
  if (orderId && scheduleClearedByUser.value.has(orderId)) {
    clearSelectedSlots()
    rebuildInspectorAssignmentRows()
    return
  }
  const assignment = orderId ? assignmentsByOrderId.value[orderId] : null
  const inspectorId = selectedInspectorId.value
  if (
    assignment &&
    inspectorId &&
    isSameInspectorId(assignment.inspectorId, inspectorId) &&
    isSameCalendarDay(assignment.date, d)
  ) {
    selectedSlots.value = new Set(pickerSlotsFromAssignment(assignment))
  } else {
    clearSelectedSlots()
  }
  rebuildInspectorAssignmentRows()
}

function toggleSlotSelection(slot: string) {
  if (hasActiveBookedAssignment()) {
    showToast('warning', 'Please unassign the current schedule first')
    return
  }
  if (!slotGridInteractionAllowed()) return
  const orderId = selectedOrderId.value
  if (orderId && isFuScheduleOrReschedulePageActive()) {
    const o = findOrder(orderId)
    const li = effectiveSidebarLineIndexForOrder(orderId)
    if (o && inspectorAssignmentIsMainLineOnFuMergedCard(o, li)) return
  }
  const date = selectedDate.value
  if (date && isPastTimeSlot(slot, date)) return
  if (slotPickerDisabled.value.has(slot) || timeSlotsLockedOutsideCycleDay.value) return

  const canonical = canonicalGridSlot(slot) ?? slot
  const prev = selectedSlots.value
  let next: Set<string>

  if (prev.has(canonical) || prev.has(slot)) {
    next = shrinkContiguousSlotSelection(prev, canonical)
  } else {
    const proposed = applyContiguousSlotSelection(new Set([...prev, canonical]))
    const ordered = sortSlotsByGridOrder([...proposed])
    const startIdx = slotGridIndex(ordered[0]!)
    const endIdx = slotGridIndex(ordered[ordered.length - 1]!)
    if (middleSlotsHaveOtherTaskOccupancy(startIdx, endIdx)) {
      showToast('warning', 'There are other tasks in the middle time slot.')
      return
    }
    if (middleSlotsHaveNonSelectableGap(startIdx, endIdx)) {
      return
    }
    next = proposed
  }

  selectedSlots.value = next
  if (isRescheduleMode.value) rescheduleUserPickedSlots.value = next.size > 0
  if (orderId && isOrderScheduleCleared(orderId)) {
    syncInspectorSidebarSelectionForFocusOrder(orderId)
  }
  hideToast()
}

function pickSlot(slot: string) {
  toggleSlotSelection(slot)
}

function normalizeGridSlot(slot: string): string {
  return slot.trim().toUpperCase().replace(/\s/g, '')
}

function dateFromScheduleDisplay(scheduledAt: string): Date | null {
  const text = formatOrderScheduleLineDisplay(scheduledAt)
  const m = text.match(/^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4})/i)
  if (!m?.[1] || !m[2] || !m[3]) return null
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const mi = months.findIndex((x) => x.toLowerCase() === m[1]!.slice(0, 3).toLowerCase())
  if (mi < 0) return null
  const d = new Date(parseInt(m[3]!, 10), mi, parseInt(m[2]!, 10))
  return Number.isNaN(d.getTime()) ? null : d
}

function slotFromScheduleDisplay(scheduledAt: string): string | null {
  const text = formatOrderScheduleLineDisplay(scheduledAt)
  const times = [...text.matchAll(/(\d{1,2}:\d{2})(AM|PM)/gi)]
  if (!times.length || !times[0]?.[1] || !times[0]?.[2]) return null
  const normalized = normalizeGridSlot(`${times[0][1]}${times[0][2]}`)
  for (const row of TIME_SLOT_GRID) {
    for (const cell of row) {
      if (cell && normalizeGridSlot(cell) === normalized) return cell
    }
  }
  return `${times[0][1]}${times[0][2].toUpperCase()}`
}

function orderLineMatchesInspector(
  line: OrderServiceLineDTO,
  inspectorId: string,
  orderId?: string,
  lineIndex?: number,
): boolean {
  const ins = inspectorRowById(inspectorId)
  if (!ins) return false

  if (orderId != null && lineIndex != null) {
    const session = assignmentsByOrderId.value[orderId]
    if (
      session &&
      session.lineIndex === lineIndex &&
      isSameInspectorId(session.inspectorId, inspectorId)
    ) {
      return true
    }
  }

  const assignName = (line.assignment ?? '').trim()
  if (assignName && !/pending\s+assignment/i.test(assignName)) {
    const insName = ins.name.trim()
    if (assignName === insName) return true
    if (assignName.toLowerCase() === insName.toLowerCase()) return true
  }

  const ref = (line.technicianRef ?? '').trim()
  const staffNo = (ins.staffNo ?? '').trim()
  if (ref && staffNo) {
    const normRef = ref.replace(/^@\s*/, '').trim().toLowerCase()
    const normStaff = staffNo.replace(/^@\s*/, '').trim().toLowerCase()
    if (normRef === normStaff) return true
  }

  return false
}

function occupiedSlotsFromScheduledAt(scheduledAt: string, orderId?: string): string[] {
  const sched = scheduledAt.trim()
  if (!sched || sched === '—' || /pending\s+(schedule|reschedule)/i.test(sched)) return []
  const date = dateFromScheduleDisplay(sched)
  const slot = slotFromScheduleDisplay(sched)
  if (!date || !slot) return []
  const label = formatOrderScheduleLineDisplay(sched)
  return occupiedSlotsForAssignment({
    orderId: orderId ?? '',
    lineIndex: 0,
    inspectorId: '',
    date,
    slot,
    timeLabel: label !== '—' ? label : buildAssignmentTimeLabel(date, slot),
  })
}

function addOccupiedSlotsFromAssignedOrders(
  disabled: Set<string>,
  inspectorId: string,
  date: Date,
  exclude: ScheduleBookingExclude | null,
  orders: OrderCardDTO[],
  opts?: { forConflict?: boolean; forSlotDisable?: boolean },
) {
  addOccupiedSlotsFromOrdersWithBookedLines(disabled, inspectorId, date, exclude, orders, opts)
}

function inspectorAssignmentRowKey(a: InspectorAssignment): string {
  const id = resolveCanonicalOrderId(a.orderId)
  return `${id}::${a.lineIndex ?? -1}`
}

function inspectorAssignmentReadonlyForLine(o: OrderCardDTO, lineIndex: number): boolean {
  const focus = focusPayload.value
  if (!focus) return false
  if (inspectorSidebarBlocksNonFocusOrder(o.id, focus.orderId, careOrderIdsReferToSameOrder)) return true
  const line = o.services[lineIndex]
  if (!line || isFollowUpCareLine(line)) return false
  if (focus.mode === 'reschedule' && isRescheduleFollowUpFocusSession(focus)) return true
  if (focus.mode !== 'reschedule' && isFollowUpScheduleFocusSession(focus)) return true
  return false
}

/** 主单 Unscheduled Inspections：Follow-up 侧栏行只展示占用，不可点选编辑 */
function inspectorSidebarFollowUpReadonly(
  o: OrderCardDTO | undefined,
  lineIndex: number,
  targetType?: string,
): boolean {
  if (isRescheduleMode.value || isFuScheduleOrRescheduleSessionActive()) return false
  if (targetType === 'followup') return true
  const line = o?.services[lineIndex]
  return !!line && isFollowUpCareLine(line)
}

function inspectorAssignmentReadonlyInSidebar(
  o: OrderCardDTO | undefined,
  lineIndex: number,
  targetType?: string,
  orderId?: string,
): boolean {
  if (inspectorSidebarFollowUpReadonly(o, lineIndex, targetType)) return true
  const focus = focusPayload.value
  const candidateId = (o?.id ?? orderId ?? '').trim()
  if (
    focus?.orderId?.trim() &&
    candidateId &&
    inspectorSidebarBlocksNonFocusOrder(candidateId, focus.orderId, careOrderIdsReferToSameOrder)
  ) {
    return true
  }
  return o ? inspectorAssignmentReadonlyForLine(o, lineIndex) : false
}

/** 当前为 FU Schedule 或 FU Reschedule 会话 */
function isFuScheduleOrRescheduleSessionActive(): boolean {
  const focus = focusPayload.value
  if (!focus) return false
  if (focus.mode === 'reschedule') return isRescheduleFollowUpFocusSession(focus)
  return isFollowUpScheduleFocusSession(focus)
}

/** FU 会话检查员侧栏：订单是否带 FU 状态（合并卡 / FU 池） */
function orderIsFuStatusForInspectorSidebar(o: OrderCardDTO): boolean {
  if (orderStatusIsFollowUpContext(o.status)) return true
  const bucket = scheduleOrderBucket(o)
  if (bucket === 'fu_pend_sched' || bucket === 'fu_pend_resched') return true
  const code = (o.serviceStatusCode ?? '').trim().toLowerCase()
  if (
    code === 'fu_pending_schedule' ||
    code === 'fu_pending_reschedule' ||
    code === 'fu_assigned' ||
    code === 'follow_up_assigned'
  ) {
    return true
  }
  if (o.services.some(isFollowUpCareLine) && isFollowUpScheduleSessionForOrder(o.id)) return true
  if (o.services.some(isFollowUpCareLine) && isRescheduleFollowUpSessionForOrder(o.id)) return true
  return false
}

/** 主单 Unscheduled Inspections：Follow-up 行可在侧栏展示（Reschedule / FU 会话另有规则） */
function scheduleInspectorSidebarOmitsFuOrderLine(_o: OrderCardDTO, _lineIndex: number): boolean {
  return false
}

/** FU Schedule / Reschedule：非跟进行不进检查员侧栏，仅在日历灰格 + 悬浮地址展示 */
function fuSessionInspectorSidebarOmitsNonFuOrderLine(o: OrderCardDTO, lineIndex: number): boolean {
  if (!isFuScheduleOrRescheduleSessionActive()) return false
  if (!orderIsFuStatusForInspectorSidebar(o)) return true
  const line = o.services[lineIndex]
  if (!line) return true
  if (!isFollowUpCareLine(line) && orderHasFollowUpCareContext(o)) return true
  return false
}

/** 合并卡（含 App 跟进）：主单行不进检查员侧栏，仅日历灰格 + 悬浮地址 */
function mergedCardMainLineOmitsFromInspectorSidebar(o: OrderCardDTO, lineIndex: number): boolean {
  const line = o.services[lineIndex]
  if (!line || isFollowUpCareLine(line)) return false
  if (orderHasFollowUpCareContext(o)) return true
  const focus = focusPayload.value
  if (
    focus &&
    careOrderIdsReferToSameOrder(o.id, focus.orderId) &&
    (isFollowUpScheduleFocusSession(focus) || isRescheduleFollowUpFocusSession(focus))
  ) {
    return true
  }
  return false
}

/** FU Reschedule 侧栏：合并卡只展示跟进行，避免主单与跟进行各一行 */
function inspectorSidebarIncludesOrderLine(o: OrderCardDTO, lineIndex: number): boolean {
  const line = o.services[lineIndex]
  if (!line) return false
  if (fuInspectorSidebarShowsFollowUpLinesOnly() && !isFollowUpCareLine(line)) return false
  if (mergedCardMainLineOmitsFromInspectorSidebar(o, lineIndex)) return false
  if (scheduleInspectorSidebarOmitsFuOrderLine(o, lineIndex)) return false
  if (fuSessionInspectorSidebarOmitsNonFuOrderLine(o, lineIndex)) return false
  return true
}

function followUpLineIndexForContextRow(o: OrderCardDTO, canonicalId: string): number {
  if (isRescheduleMode.value && isRescheduleFollowUpSessionForOrder(canonicalId)) {
    return followUpRescheduleLineIndex(o)
  }
  return followUpScheduleLineIndex(o)
}

function dedupeInspectorAssignmentList(list: InspectorAssignment[]): InspectorAssignment[] {
  const byKey = new Map<string, InspectorAssignment>()
  for (const a of list) {
    const key = inspectorAssignmentRowKey(a)
    const prev = byKey.get(key)
    if (!prev) {
      byKey.set(key, a)
      continue
    }
    byKey.set(key, { ...prev, ...a, readonly: prev.readonly || a.readonly })
  }
  return [...byKey.values()]
}

/** FU Schedule / Reschedule：同一合并卡侧栏只保留跟进行一行 */
function finalizeInspectorSidebarAssignments(list: InspectorAssignment[]): InspectorAssignment[] {
  let deduped = dedupeInspectorAssignmentList(list)
  if (isFuScheduleOrRescheduleSessionActive()) {
    const byOrder = new Map<string, InspectorAssignment>()
    for (const a of deduped) {
      const id = resolveCanonicalOrderId(a.orderId)
      const prev = byOrder.get(id)
      if (!prev) {
        byOrder.set(id, a)
        continue
      }
      const o = findOrder(id)
      const isFuRow = (row: InspectorAssignment) => {
        if (row.targetType === 'followup') return true
        const li = row.lineIndex ?? -1
        const line = o?.services[li]
        return !!line && isFollowUpCareLine(line)
      }
      if (isFuRow(a) && !isFuRow(prev)) byOrder.set(id, a)
    }
    deduped = [...byOrder.values()]
  }
  return deduped.filter((a) => {
    const o =
      a.targetId != null && a.targetId > 0
        ? Object.values(ordersById.value).find(
            (card) =>
              card.orderPrimaryId != null &&
              Math.trunc(card.orderPrimaryId) === Math.trunc(a.targetId!),
          ) ?? findOrder(a.orderId)
        : findOrder(resolveCanonicalOrderId(a.orderId))
    if (!o) return true
    if (isFuScheduleOrRescheduleSessionActive() && a.targetType === 'followup') return true
    const lineIndex = a.lineIndex ?? -1
    const line = o.services[lineIndex]
    if (!line) return false
    if (isFuScheduleOrRescheduleSessionActive() && !isFollowUpCareLine(line)) return false
    return inspectorSidebarIncludesOrderLine(o, lineIndex)
  })
}

function inspectorAssignmentFromOrderLine(
  o: OrderCardDTO,
  line: OrderServiceLineDTO,
  lineIndex: number,
  bookedLabel?: string | null,
): InspectorAssignment | null {
  const label = bookedLabel?.trim()
  if (!label || !lineHasConfirmedScheduleTime(line, o)) return null
  const addr = (line.address ?? '').trim()
  const shortAddr = addr.split(',')[0]?.trim() || addr || o.homeId
  return {
    address: shortAddr,
    orderId: o.id,
    timeLabel: label,
    lineIndex,
    readonly: inspectorAssignmentReadonlyInSidebar(o, lineIndex),
  }
}

function dedupeOrdersById(orders: OrderCardDTO[]): OrderCardDTO[] {
  const byId = new Map<string, OrderCardDTO>()
  for (const o of orders) {
    byId.set(o.id, ordersById.value[o.id] ?? o)
  }
  return [...byId.values()]
}

function assignedRowsForInspector(
  inspectorId: string,
  orders: OrderCardDTO[],
  weekFilter: Date | null,
  dayFilter: Date | null,
): InspectorAssignment[] {
  const byKey = new Map<string, InspectorAssignment>()
  for (const o of dedupeOrdersById(orders)) {
    if (isOrderScheduleCleared(o.id)) continue
    for (let li = 0; li < o.services.length; li++) {
      const line = o.services[li]!
      if (!orderLineHasBookedSchedule(line, o)) continue
      if (isFuScheduleOrRescheduleSessionActive() && !isFollowUpCareLine(line)) continue
      if (!orderLineMatchesInspector(line, inspectorId, o.id, li)) continue
      const label = resolvedBookedScheduleLabelForLine(o, li)
      if (!label) continue
      const lineDate = dateFromScheduleDisplay(label)
      if (weekFilter && lineDate && !isDateInWeekRange(lineDate, weekFilter)) continue
      if (dayFilter && (!lineDate || !isSameCalendarDay(lineDate, dayFilter))) continue
      const row = inspectorAssignmentFromOrderLine(o, line, li, label)
      if (!row) continue
      if (!inspectorSidebarIncludesOrderLine(o, li)) continue
      const key = inspectorAssignmentRowKey(row)
      const prev = byKey.get(key)
      if (!prev) {
        byKey.set(key, row)
        continue
      }
      const session = assignmentsByOrderId.value[o.id]
      if (session?.lineIndex === li) byKey.set(key, row)
    }
  }
  return [...byKey.values()]
}

/** Unassign 后：context 行与 scheduleClearedByUser 别名匹配即隐藏（侧栏 + 网格占用） */
function isOrderScheduleClearedForSidebar(
  orderId: string,
  targetId?: number,
): boolean {
  if (!scheduleClearedByUser.value.size) return false
  for (const clearedId of scheduleClearedByUser.value) {
    const cleared = findOrder(clearedId)
    if (targetId != null && targetId > 0) {
      if (
        cleared?.orderPrimaryId != null &&
        Math.trunc(cleared.orderPrimaryId) === Math.trunc(targetId)
      ) {
        return true
      }
      continue
    }
    if (careOrderIdsReferToSameOrder(orderId, clearedId)) return true
  }
  return false
}

function purgeClearedOrdersFromContextInspectorRows(): void {
  if (!scheduleClearedByUser.value.size) return
  scheduleContextInspectorOrders.value = scheduleContextInspectorOrders.value.filter(
    (row) => !isOrderScheduleClearedForSidebar(row.orderId, row.targetId),
  )
}

function contextAddressForOrderId(orderId: string): string {
  const canonicalId = resolveCanonicalOrderId(orderId)
  for (const row of scheduleContextInspectorOrders.value) {
    if (!careOrderIdsReferToSameOrder(row.orderId, canonicalId)) continue
    const addr = row.address?.trim()
    if (addr) return addr.split(',')[0]?.trim() || addr
  }
  return ''
}

/** 侧栏以 context target_type 为准；本地订单卡仅用于补充 FU/合并卡等会话规则 */
function contextRowEligibleForInspectorSidebar(
  row: ScheduleContextInspectorOrder,
  o: OrderCardDTO | undefined,
  isFollowUpRow: boolean,
): boolean {
  const inFuSession = isFuScheduleOrRescheduleSessionActive()
  const canonicalId = (o?.id ?? row.orderId).trim()
  const lineIndex = o
    ? isFollowUpRow
      ? followUpLineIndexForContextRow(o, canonicalId)
      : o.services.findIndex((l) => !isFollowUpCareLine(l))
    : isFollowUpRow
      ? 1
      : 0
  const safeLineIndex = lineIndex >= 0 ? lineIndex : isFollowUpRow ? 1 : 0
  const fuLine = o?.services[safeLineIndex]

  return evaluateContextSidebarRow({
    row,
    isFollowUpRow,
    isRescheduleMode: isRescheduleMode.value,
    inFuSession,
    clearedForSidebar: isOrderScheduleClearedForSidebar(canonicalId, row.targetId),
    hasLocalOrder: !!o,
    orderHasFollowUpContext: !!o && orderHasFollowUpCareContext(o),
    orderIsFuStatus: !!o && orderIsFuStatusForInspectorSidebar(o),
    lineIsFollowUp: isFollowUpRow || (!!fuLine && isFollowUpCareLine(fuLine)),
    omitFuLineInSchedule: !!o && scheduleInspectorSidebarOmitsFuOrderLine(o, safeLineIndex),
    omitNonFuLineInFuSession: !!o && fuSessionInspectorSidebarOmitsNonFuOrderLine(o, safeLineIndex),
    omitMergedMainLine: !!o && mergedCardMainLineOmitsFromInspectorSidebar(o, safeLineIndex),
  })
}

function inspectorAssignmentsFromContextOrders(
  inspectorId: string,
  weekFilter: Date | null,
  dayFilter: Date | null,
): InspectorAssignment[] {
  const byKey = new Map<string, InspectorAssignment>()
  for (const row of scheduleContextInspectorOrders.value) {
    if (!isSameInspectorId(row.technicianId, inspectorId)) continue
    const lineDate = parseServiceCycleDate(row.dateYmd)
    if (weekFilter && lineDate && !isDateInWeekRange(lineDate, weekFilter)) continue
    if (dayFilter && (!lineDate || !isSameCalendarDay(lineDate, dayFilter))) continue
    const timeLabel = scheduleLabelFromContextInspectorOrder(row)
    if (!timeLabel) continue
    const o = findOrderForContextInspectorRow(row)
    const displayOrderId = (o?.id ?? row.orderId).trim()
    const isFollowUpRow = row.targetType === 'followup'

    if (!contextRowEligibleForInspectorSidebar(row, o, isFollowUpRow)) continue

    const lineIndex = o
      ? isFollowUpRow
        ? followUpLineIndexForContextRow(o, displayOrderId)
        : o.services.findIndex((l) => !isFollowUpCareLine(l))
      : isFollowUpRow
        ? 1
        : 0
    const safeLineIndex = lineIndex >= 0 ? lineIndex : isFollowUpRow ? 1 : 0
    const assignment: InspectorAssignment = {
      address:
        (row.address ?? '').trim().split(',')[0]?.trim() ||
        (row.address ?? '').trim() ||
        orderShortAddressForAssignment(displayOrderId),
      orderId: displayOrderId,
      targetId: row.targetId,
      timeLabel,
      lineIndex: safeLineIndex,
      targetType: row.targetType,
      readonly: inspectorAssignmentReadonlyInSidebar(o, safeLineIndex, row.targetType, displayOrderId),
    }
    byKey.set(inspectorAssignmentRowKey(assignment), assignment)
  }
  return [...byKey.values()]
}

function orderShortAddressForAssignment(orderId: string): string {
  const fromContext = contextAddressForOrderId(orderId)
  if (fromContext) return fromContext
  const item = unscheduled.value.find((x) => x.orderId === orderId)
  if (item?.address?.trim()) return item.address.trim()
  const o = findOrder(orderId)
  if (!o) return orderId
  const line = o.services[lineIndexForOrder(orderId)] ?? o.services[0]
  const addr = (line?.address ?? '').trim()
  return addr.split(',')[0]?.trim() || addr || o.homeId || orderId
}

/** 检查员侧栏统计/列表：按当前日历周过滤（与 context `week_start` 一致） */
function inspectorAssignmentFilterWeek(): Date | null {
  return weekAnchor.value
}

function sortInspectorAssignmentsByDate(assignments: InspectorAssignment[]): InspectorAssignment[] {
  const dateKey = (label: string): string => {
    const iso = label.match(/\b(\d{4}-\d{2}-\d{2})\b/)?.[1]
    if (iso) return iso
    const parts = label.split(',')
    if (parts.length >= 2) {
      const head = parts[0]?.trim()
      const yearToken = parts[1]?.trim().split(/\s/)[0]
      if (head && yearToken) {
        const d = parseServiceCycleDate(`${head},${yearToken}`)
        if (d) return ymdFromDate(d)
      }
    }
    return label
  }
  return [...assignments].sort((a, b) => dateKey(a.timeLabel).localeCompare(dateKey(b.timeLabel)))
}

function orderIdsReferencedInParsedContext(ctx: ParsedCareScheduleContext): Set<string> {
  const ids = new Set<string>()
  const add = (orderId: string) => {
    const canon = resolveCanonicalOrderId(orderId)
    if (canon) ids.add(canon)
  }
  for (const row of scheduleContextInspectorOrders.value) add(row.orderId)
  for (const o of ctx.assignedOrders) add(o.id)
  for (const o of ctx.unscheduledInspections) add(o.id)
  return ids
}

function orderStillReferencedByScheduleContext(
  orderId: string,
  ctx: ParsedCareScheduleContext,
  focus: CareScheduleFocusPayload,
  contextOrderIds: Set<string>,
): boolean {
  const canon = resolveCanonicalOrderId(orderId)
  if (!canon) return false
  if (focus.orderId && careOrderIdsReferToSameOrder(orderId, focus.orderId)) return true
  const selected = selectedOrderId.value?.trim()
  if (selected && careOrderIdsReferToSameOrder(orderId, selected)) return true
  for (const clearedId of scheduleClearedByUser.value) {
    if (careOrderIdsReferToSameOrder(orderId, clearedId)) return true
  }
  for (const id of contextOrderIds) {
    if (careOrderIdsReferToSameOrder(id, canon)) return true
  }
  return false
}

/** context 刷新后剔除已删订单的本地 assignment / 占用缓存，避免侧栏仍显示旧数据 */
function pruneLocalScheduleStateAfterContextIngest(
  ctx: ParsedCareScheduleContext,
  focus: CareScheduleFocusPayload,
): void {
  const contextOrderIds = orderIdsReferencedInParsedContext(ctx)
  const keep = (orderId: string) =>
    orderStillReferencedByScheduleContext(orderId, ctx, focus, contextOrderIds)

  const nextAssign = { ...assignmentsByOrderId.value }
  let assignChanged = false
  for (const orderId of Object.keys(nextAssign)) {
    if (keep(orderId)) continue
    delete nextAssign[orderId]
    assignChanged = true
  }
  if (assignChanged) {
    assignmentsByOrderId.value = nextAssign
  }

  assignedOrdersCache.value = assignedOrdersCache.value.filter((o) => keep(o.id))

  const nextOrders = { ...ordersById.value }
  let ordersChanged = false
  for (const orderId of Object.keys(nextOrders)) {
    if (keep(orderId)) continue
    delete nextOrders[orderId]
    ordersChanged = true
  }
  if (ordersChanged) ordersById.value = nextOrders
}

function rebuildInspectorAssignmentRows() {
  const weekFilter = inspectorAssignmentFilterWeek()
  /** 侧栏按当前周全部订单展示，不随选中日变化 */
  const sidebarDayFilter: Date | null = null

  for (const ins of inspectors.value) {
    const fromContext = inspectorAssignmentsFromContextOrders(ins.id, weekFilter, sidebarDayFilter)
    ins.assignments = sortInspectorAssignmentsByDate(
      finalizeInspectorSidebarAssignments(fromContext),
    )
    ins.orderCount = ins.assignments.length
  }
}


async function loadAssignedOrdersCache() {
  const statuses: Array<'assigned' | 'fu_assigned'> = ['assigned', 'fu_assigned']
  try {
    const pageResults = await Promise.all(
      statuses.map((serviceStatus) =>
        fetchCareOrderListAllPages({
          pageSize: 100,
          searchField: 'order_id',
          serviceStatus,
        }),
      ),
    )
    const merged: OrderCardDTO[] = []
    const seen = new Set<string>()
    const seedLists = [
      assignedOrdersCache.value,
      Object.values(ordersById.value).filter(
        (o) => o?.id && o.services.some((line) => orderLineHasBookedSchedule(line, o)),
      ),
    ]
    for (const list of seedLists) {
      for (const o of list) {
        if (!o?.id || seen.has(o.id)) continue
        seen.add(o.id)
        merged.push(o)
      }
    }
    for (const { list } of pageResults) {
      for (const o of list) {
        if (seen.has(o.id)) continue
        if (!o.services.some((line) => orderLineHasBookedSchedule(line, o))) continue
        seen.add(o.id)
        merged.push(o)
      }
    }
    assignedOrdersCache.value = merged
    registerOrderCards(merged)
  } catch {
    /* 保留 context / 会话内已有占用，避免 API 失败时网格空白 */
  }
  rebuildInspectorAssignmentRows()
}

function lineIndexForOrder(orderId: string): number {
  const focus = focusPayload.value
  const o = findOrder(orderId)
  if (!o) return focus?.lineIndex ?? 0
  // Reschedule：以入口 focus.lineIndex 为准（主单/FU 都要），勿用 === 比较 orderId
  if (
    focus?.mode === 'reschedule' &&
    focus.orderId &&
    careOrderIdsReferToSameOrder(focus.orderId, orderId)
  ) {
    const focusLine = o.services[focus.lineIndex]
    if (focusLine) return focus.lineIndex
  }
  if (focus?.mode === 'reschedule' && isRescheduleFollowUpSessionForOrder(orderId)) {
    const fuIdx = followUpRescheduleLineIndex(o)
    if (isFollowUpCareLine(o.services[fuIdx]!)) return fuIdx
  }
  if (focus?.mode !== 'reschedule' && isFollowUpScheduleSessionForOrder(orderId)) {
    const fuIdx = followUpScheduleLineIndex(o)
    if (isFollowUpCareLine(o.services[fuIdx]!)) return fuIdx
  }
  return lineIndexForScheduleCard(o, orderId, focus?.lineIndex ?? 0)
}

function currentOrderAssignment(): OrderScheduleAssignment | null {
  const orderId = selectedOrderId.value
  if (!orderId) return null
  return assignmentsByOrderId.value[orderId] ?? null
}

/** 分钟数 → 展示用 `H:MM AM/PM`（与 formatSlotLabel 一致） */
function displayClockFromMinutes(totalMinutes: number): string {
  const hour24 = Math.floor(totalMinutes / 60) % 24
  const minute = totalMinutes % 60
  const ap = hour24 >= 12 ? 'PM' : 'AM'
  let h12 = hour24 % 12
  if (h12 === 0) h12 = 12
  return `${h12}:${String(minute).padStart(2, '0')} ${ap}`
}

/** 单格点选 → 展示结束时刻（+30 分钟），网格仍只高亮所点那一格 */
function singleSlotDurationEndDisplayClock(startSlot: string): string {
  const startMin = gridSlotStartMinutes(startSlot)
  if (startMin == null) return formatSlotLabel(startSlot)
  const endMin = startMin + SLOT_INTERVAL_MINUTES
  const hour24 = Math.floor(endMin / 60) % 24
  const minute = endMin % 60
  const ap = hour24 >= 12 ? 'PM' : 'AM'
  let h12 = hour24 % 12
  if (h12 === 0) h12 = 12
  return `${h12}:${String(minute).padStart(2, '0')} ${ap}`
}

/** 选中格对应的结束展示时刻：一律末格起点 +30min（每格 30 分钟） */
function endDisplayClockFromSelectedSlots(slots: string[]): string {
  const ordered = sortSlotsByGridOrder(slots)
  if (!ordered.length) return ''
  const lastMin = gridSlotStartMinutes(ordered[ordered.length - 1]!)
  if (lastMin == null) return formatSlotLabel(ordered[ordered.length - 1]!)
  return displayClockFromMinutes(lastMin + SLOT_INTERVAL_MINUTES)
}

/** Assign API time_end：一律末格起点 +30min */
function timeEndFromSelectedSlots(slots: string[]): string {
  const ordered = sortSlotsByGridOrder(slots)
  if (!ordered.length) return ''
  return displayClockToTime24(endDisplayClockFromSelectedSlots(ordered))
}

function endBoundarySlotFromSelectedSlots(slots: string[]): string | null {
  const range = minuteRangeFromSelectedSlots(slots)
  if (!range) return null
  for (const slot of allGridSlots()) {
    if (gridSlotStartMinutes(slot) === range.endExclusive) return slot
  }
  return null
}

function buildAssignmentTimeLabel(date: Date, slot: string): string {
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${dateStr}, ${formatSlotLabel(slot)}`
}

function buildAssignmentTimeLabelFromSlots(date: Date, slots: string[]): string {
  const ordered = sortSlotsByGridOrder(slots)
  if (!ordered.length) return ''
  const start = formatSlotLabel(ordered[0]!)
  const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  const endClock = endDisplayClockFromSelectedSlots(ordered)
  const raw =
    ordered.length === 1 || endClock !== start
      ? `${dateStr}, ${start} - ${endClock}`
      : `${dateStr}, ${start}`
  const normalized = formatOrderScheduleLineDisplay(raw)
  return normalized !== '—' ? normalized : raw
}

/** Schedule Confirmation：与 assignment 时段文案相同（末格 +30） */
function buildConfirmTimeLabelFromSlots(date: Date, slots: string[]): string {
  return buildAssignmentTimeLabelFromSlots(date, slots)
}

interface UserScheduleSelection {
  date: Date
  slots: string[]
  timeLabel: string
  timeStart: string
  timeEnd: string
}

function buildUserScheduleSelection(date: Date, slots: string[]): UserScheduleSelection {
  const ordered = sortSlotsByGridOrder(slots)
  const timeLabel = buildAssignmentTimeLabelFromSlots(date, ordered)
  return {
    date,
    slots: ordered,
    timeLabel,
    timeStart: gridSlotToTimeStart(ordered[0]!),
    timeEnd: timeEndFromSelectedSlots(ordered),
  }
}

function resolveInspectorNameForAssign(orderId: string, inspectorId: string): string {
  const fromRow = inspectorRowById(inspectorId)?.name?.trim()
  if (fromRow) return fromRow
  if (
    selectedInspectorId.value &&
    isSameInspectorId(selectedInspectorId.value, inspectorId) &&
    selectedInspector.value?.name?.trim()
  ) {
    return selectedInspector.value.name.trim()
  }
  const fromLine = lineAssignmentNameForOrder(orderId)
  if (fromLine) return fromLine
  return ''
}

function applyUserScheduleSelectionToSession(
  orderId: string,
  lineIndex: number,
  inspectorId: string,
  selection: UserScheduleSelection,
  opts?: { refreshPool?: boolean },
) {
  const ins = inspectorRowById(inspectorId)
  const inspectorName = resolveInspectorNameForAssign(orderId, inspectorId)
  const resolvedInsId = String(ins?.id ?? inspectorId)
  const assignment: OrderScheduleAssignment = {
    orderId,
    lineIndex,
    inspectorId: resolvedInsId,
    date: new Date(selection.date.getFullYear(), selection.date.getMonth(), selection.date.getDate()),
    slot: selection.slots[0]!,
    timeLabel: selection.timeLabel,
    pickerSlots: [...selection.slots],
    occupiedSlots: buildOccupiedSlotsFromSelectedSlots(selection.slots),
  }
  assignmentsByOrderId.value = { ...assignmentsByOrderId.value, [orderId]: assignment }
  applyAssignmentToOrderCard(
    orderId,
    lineIndex,
    selection.timeLabel,
    inspectorName,
    resolvedInsId,
    opts,
  )
}

function syncInspectorAssignmentRow(_a: OrderScheduleAssignment) {
  rebuildInspectorAssignmentRows()
}

function removeInspectorAssignmentRow(_a: OrderScheduleAssignment) {
  rebuildInspectorAssignmentRows()
}

function pendingScheduleStatusForOrder(order: OrderCardDTO, lineIndex: number): string {
  const line = order.services[lineIndex]
  if (line && isFollowUpCareLine(line)) return 'Follow-up-Pending Schedule'
  return 'Pending Schedule'
}

function pendingRescheduleStatusForOrder(order: OrderCardDTO, lineIndex: number): string {
  const line = order.services[lineIndex]
  if (line && isFollowUpCareLine(line)) return 'Follow-up-Pending Reschedule'
  return 'Pending Reschedule'
}

/** Reschedule Unassign：仅清空该行时段与指派人员，订单状态/权限/其它字段保持不变 */
function clearScheduleTimeAndPersonnelOnOrderCard(orderId: string, lineIndex: number) {
  const order = findOrder(orderId)
  if (!order) return
  const line = order.services[lineIndex]
  if (!line) return

  const nextLine: OrderServiceLineDTO = {
    ...line,
    scheduledAt: '',
    assignment: 'Pending Assignment',
    technicianRef: undefined,
  }
  const nextServices = order.services.map((svc, i) => (i === lineIndex ? nextLine : svc))
  const nextOrder: OrderCardDTO = {
    ...order,
    services: nextServices,
  }

  const next = purgeDuplicateOrdersById(nextOrder, orderId)
  next[orderId] = nextOrder
  ordersById.value = next

  upsertFocusOrderList(orderId, nextOrder)

  syncScheduleOrderListAndNotify()
  refreshUnscheduledFromFocusOrderList()
}

/** 主动排期后 Unassign：跟进行应回到 FU Pending Schedule，而非误留 FU Pending Reschedule */
function unassignShouldRevertToPendingSchedule(
  orderBefore: OrderCardDTO,
  lineIndex: number,
  focus: CareScheduleFocusPayload | null,
  wasCustomerRequestedReschedule: boolean,
): boolean {
  if (focus?.rescheduleKind === 'proactive') return true
  const line = orderBefore.services[lineIndex]
  if (!line) return true
  const bucket = classifyCareSidebarSubBucket(orderBefore.status ?? '')
  const statusLower = (orderBefore.status ?? '').trim().toLowerCase()
  const mainAssigned =
    (orderBefore.serviceStatusCode ?? '').trim().toLowerCase() === 'assigned' ||
    (/\bassigned\b/.test(statusLower) &&
      !/\bpending\b/.test(statusLower) &&
      !/\bfollow[\s-]*up\b/.test(statusLower))
  const hadConfirmedSchedule = lineHasConfirmedScheduleTime(line, orderBefore)
  const isCustomerResched =
    focus?.rescheduleKind === 'customer' || wasCustomerRequestedReschedule

  if (hadConfirmedSchedule || mainAssigned || orderIsFuAssignedOrder(orderBefore)) return true

  if (isFollowUpCareLine(line)) {
    if (bucket === 'fu_pend_sched') return true
    if (bucket === 'fu_pend_resched' && isCustomerResched) return false
    if (
      bucket === 'fu_pend_resched' &&
      !hadConfirmedSchedule &&
      isPendingAssignmentLabel(line.assignment)
    ) {
      return true
    }
    return false
  }
  if (bucket === 'pend_sched') return true
  if (bucket === 'pend_resched' && isCustomerResched) return false
  if (
    bucket === 'pend_resched' &&
    !hadConfirmedSchedule &&
    isPendingAssignmentLabel(line.assignment)
  ) {
    return true
  }
  return false
}

/** Unassign 后未重新排期就退出：订单回到 Pending Schedule，会话切回 Schedule 模式 */
function applyPendingScheduleStateOnOrderCard(orderId: string, lineIndex: number) {
  const order = findOrder(orderId)
  if (!order) return
  const canonicalId = (order.id ?? orderId).trim()
  const line = order.services[lineIndex]
  if (!line) return

  const pendingTimeLabel = isFollowUpCareLine(line)
    ? 'Follow-up-Pending Schedule'
    : 'Pending Schedule'
  const nextLine: OrderServiceLineDTO = {
    ...line,
    scheduledAt: pendingTimeLabel,
    assignment: 'Pending Assignment',
    technicianRef: undefined,
    canSchedule: true,
    canReschedule: false,
  }
  let nextOrder: OrderCardDTO = {
    ...order,
    id: canonicalId,
    status: pendingScheduleStatusForOrder(order, lineIndex),
    serviceStatusCode: isFollowUpCareLine(line) ? 'fu_pending_schedule' : 'pending_schedule',
    services: order.services.map((svc, i) => (i === lineIndex ? nextLine : svc)),
  }
  if (
    isFollowUpCareLine(line) &&
    order.services.some((l) => isFollowUpCareLine(l)) &&
    order.services.some((l) => !isFollowUpCareLine(l))
  ) {
    nextOrder = preserveMainLineOnMergedFollowUpCard(order, nextOrder)
  }

  const next = purgeDuplicateOrdersById(nextOrder, canonicalId)
  next[canonicalId] = nextOrder
  ordersById.value = next
  upsertFocusOrderList(canonicalId, nextOrder)
  syncScheduleOrderListAndNotify()
}

/** 客户改期 Unassign：回到 Pending Reschedule（或 FU Pending Reschedule） */
function applyPendingRescheduleStateOnOrderCard(orderId: string, lineIndex: number) {
  const order = findOrder(orderId)
  if (!order) return
  const canonicalId = (order.id ?? orderId).trim()
  const line = order.services[lineIndex]
  if (!line) return

  const pendingTimeLabel = isFollowUpCareLine(line)
    ? 'Follow-up-Pending Reschedule'
    : 'Pending Reschedule'
  const nextLine: OrderServiceLineDTO = {
    ...line,
    scheduledAt: pendingTimeLabel,
    assignment: 'Pending Assignment',
    technicianRef: undefined,
    canSchedule: false,
    canReschedule: true,
  }
  const nextOrder: OrderCardDTO = {
    ...order,
    id: canonicalId,
    status: pendingRescheduleStatusForOrder(order, lineIndex),
    serviceStatusCode: isFollowUpCareLine(line) ? 'fu_pending_reschedule' : 'pending_reschedule',
    services: order.services.map((svc, i) => (i === lineIndex ? nextLine : svc)),
  }

  const next = purgeDuplicateOrdersById(nextOrder, canonicalId)
  next[canonicalId] = nextOrder
  ordersById.value = next
  upsertFocusOrderList(canonicalId, nextOrder)
  syncScheduleOrderListAndNotify()
}

/** Unassign 后锁定本地 Pending Schedule 卡片，避免 context API 延迟覆盖 */
function upsertLocalFocusOrderAfterUnassign(orderId: string, lineIndex: number): void {
  let order = findOrder(orderId)
  if (!order) {
    const focus = focusPayload.value
    order = focus?.orderList?.find((o) => careOrderIdsReferToSameOrder(o.id, orderId))
  }
  const line = order?.services[lineIndex]
  const needsPendingState =
    !order || (!!line && lineHasConfirmedScheduleTime(line, order))
  if (needsPendingState) {
    applyPendingScheduleStateOnOrderCard(orderId, lineIndex)
    order = findOrder(orderId)
  }
  if (!order) return
  upsertLocalFocusOrderCard(order)
  syncScheduleOrderListAndNotify()
}

/** 主动改期 Unassign 后：会话切回 Schedule，左栏待排池可继续选时段 */
function switchFocusToScheduleMode(orderId: string) {
  const focus = focusPayload.value
  if (!focus || focus.mode !== 'reschedule') return
  const switchId = (findOrder(orderId)?.id ?? orderId).trim()
  const line = findOrder(switchId)?.services[focus.lineIndex] ?? findOrder(switchId)?.services[lineIndexForOrder(switchId)]
  const orderList = scheduleOrderListSnapshot()
  const nextFocus: CareScheduleFocusPayload = {
    ...focus,
    mode: 'schedule',
    rescheduleKind: undefined,
    orderId: switchId,
    lineIndex: focus.lineIndex,
    orderList,
    serviceName: line?.serviceName ?? focus.serviceName,
    address: line?.address ?? focus.address,
  }
  focusPayload.value = nextFocus
  scheduleDateLockReleased.value = false
  clearRescheduleEntrySnapshot()
  selectedOrderId.value = switchId
  clearSelectedSlots()
  rescheduleUserPickedSlots.value = false
}

function careOrderCardsReferToSameOrder(a: OrderCardDTO, b: OrderCardDTO): boolean {
  return careOrderCardsSameOrder(a, b)
}

function persistScheduleClearedByUser() {
  try {
    if (!scheduleClearedByUser.value.size) {
      sessionStorage.removeItem(CARE_SCHEDULE_CLEARED_KEY)
      return
    }
    sessionStorage.setItem(
      CARE_SCHEDULE_CLEARED_KEY,
      JSON.stringify([...scheduleClearedByUser.value]),
    )
  } catch {
    /* ignore */
  }
}

function loadScheduleClearedByUser() {
  try {
    const raw = sessionStorage.getItem(CARE_SCHEDULE_CLEARED_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return
    const ids = parsed.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    if (!ids.length) return
    scheduleClearedByUser.value = new Set([...scheduleClearedByUser.value, ...ids])
  } catch {
    /* ignore */
  }
}

function markScheduleClearedByUser(orderId: string) {
  const order = findOrder(orderId) ?? ({ id: orderId } as OrderCardDTO)
  const ids = new Set([...scheduleClearedByUser.value, orderId.trim()])
  const canonical = (order.id ?? orderId).trim()
  if (canonical) ids.add(canonical)
  for (const [key, val] of Object.entries(ordersById.value)) {
    if (careOrderCardsSameOrder(val, order)) ids.add(key.trim())
  }
  scheduleClearedByUser.value = ids
  persistScheduleClearedByUser()
}

function hasLocalAssignmentForOrder(orderId: string): boolean {
  const target = findOrder(orderId) ?? ({ id: orderId } as OrderCardDTO)
  return Object.entries(assignmentsByOrderId.value).some(([key, a]) => {
    if (key === orderId || a.orderId === orderId) return true
    const other =
      findOrder(key) ?? findOrder(a.orderId) ?? ({ id: key || a.orderId } as OrderCardDTO)
    return careOrderCardsSameOrder(other, target)
  })
}

function hasPendingSlotSelectionForOrder(orderId: string): boolean {
  const target = findOrder(orderId) ?? ({ id: orderId } as OrderCardDTO)
  const selected = findOrder(selectedOrderId.value)
  const sameSelected =
    selectedOrderId.value === orderId ||
    (selected != null && careOrderCardsSameOrder(selected, target))
  return sameSelected && sortSlotsByGridOrder([...selectedSlots.value]).length > 0
}

function orderIdsPendingRescheduleExitRevert(): string[] {
  if (!scheduleClearedByUser.value.size) return []
  const pending: string[] = []
  for (const clearedId of scheduleClearedByUser.value) {
    const order = findOrder(clearedId)
    const canonicalId = (order?.id ?? clearedId).trim()
    if (!canonicalId) continue
    if (hasLocalAssignmentForOrder(canonicalId)) continue
    if (hasPendingSlotSelectionForOrder(canonicalId)) continue
    if (pending.some((id) => careOrderCardsReferToSameOrder({ id } as OrderCardDTO, order ?? ({ id: canonicalId } as OrderCardDTO)))) {
      continue
    }
    pending.push(canonicalId)
  }
  return pending
}

function removeScheduleClearedMarks(orderIds: string[]) {
  const next = new Set(scheduleClearedByUser.value)
  for (const orderId of orderIds) {
    next.delete(orderId)
    const order = findOrder(orderId)
    if (order) {
      for (const key of [...next]) {
        const other = findOrder(key)
        if (other && careOrderCardsSameOrder(other, order)) next.delete(key)
      }
    }
  }
  scheduleClearedByUser.value = next
  persistScheduleClearedByUser()
}

/** 关闭弹层时清理 Unassign 标记，不再改写订单状态/权限 */
function finalizeRescheduleUnassignOnExit(): boolean {
  const pendingIds = orderIdsPendingRescheduleExitRevert()
  if (!pendingIds.length) return false
  removeScheduleClearedMarks(pendingIds)
  return true
}

function orderIdForRescheduleExitWithoutReassign(): string | null {
  return orderIdsPendingRescheduleExitRevert()[0] ?? null
}

function switchRescheduleSessionToScheduleMode(orderId: string) {
  void orderId
  finalizeRescheduleUnassignOnExit()
}

/** 撤销排程后写入订单卡片：待排期或待改期 */
function revertAssignmentOnOrderCard(orderId: string, lineIndex: number, revertToPendingSchedule = false) {
  const order = findOrder(orderId)
  if (!order) return
  const line = order.services[lineIndex]
  if (!line) return

  const { technicianRef: _dropRef, ...lineRest } = line
  let nextLine: OrderServiceLineDTO
  let nextStatus: string
  let nextServiceStatusCode: string

  const toPendingSchedule = revertToPendingSchedule || !isRescheduleMode.value

  if (toPendingSchedule) {
    nextLine = {
      ...lineRest,
      scheduledAt: isFollowUpCareLine(line)
        ? 'Follow-up-Pending Schedule'
        : 'Pending Schedule',
      assignment: 'Pending Assignment',
      canSchedule: true,
      canReschedule: false,
    }
    nextStatus = pendingScheduleStatusForOrder(order, lineIndex)
    nextServiceStatusCode = isFollowUpCareLine(line) ? 'fu_pending_schedule' : 'pending_schedule'
  } else {
    nextLine = {
      ...lineRest,
      scheduledAt: 'Pending Reschedule',
      assignment: 'Pending Assignment',
      canSchedule: false,
      canReschedule: true,
    }
    nextStatus = pendingRescheduleStatusForOrder(order, lineIndex)
    nextServiceStatusCode = isFollowUpCareLine(line) ? 'fu_pending_reschedule' : 'pending_reschedule'
    const bucket = classifyCareSidebarSubBucket(order.status ?? '')
    if (bucket === 'pend_resched' || bucket === 'fu_pend_resched') {
      nextStatus = (order.status ?? '').trim() || nextStatus
    }
  }

  const nextServices = order.services.map((svc, i) => (i === lineIndex ? nextLine : svc))
  const nextOrder: OrderCardDTO = {
    ...order,
    status: nextStatus,
    serviceStatusCode: nextServiceStatusCode,
    services: nextServices,
  }

  const next = purgeDuplicateOrdersById(nextOrder, orderId)
  next[orderId] = nextOrder
  ordersById.value = next

  upsertFocusOrderList(orderId, nextOrder)

  syncScheduleOrderListAndNotify()
  refreshUnscheduledFromFocusOrderList()
}

function assignedStatusLabelForOrder(order: OrderCardDTO, line?: OrderServiceLineDTO): string {
  if (line && isFollowUpCareLine(line)) return 'Follow-up-Assigned'
  if (orderStatusIsFollowUpContext(order.status)) return 'Follow-up-Assigned'
  const bucket = classifyCareSidebarSubBucket(order.status ?? '')
  if (bucket === 'fu_pend_sched' || bucket === 'fu_pend_resched') return 'Follow-up-Assigned'
  return 'Assigned'
}

function applyAssignmentFieldsToOrderCard(
  order: OrderCardDTO,
  lineIndex: number,
  timeLabel: string,
  inspectorName: string,
  inspectorId: string,
): OrderCardDTO {
  const line = order.services[lineIndex]
  if (!line) return order
  const trimmed = timeLabel.trim()
  const normalized = formatOrderScheduleLineDisplay(trimmed)
  const scheduledAt = normalized !== '—' ? normalized : trimmed
  const ref = technicianRefForInspector(inspectorId, inspectorName)
  const nextServices = order.services.map((svc, i) => {
    if (i !== lineIndex) return svc
    return {
      ...svc,
      scheduledAt,
      assignment: inspectorName || svc.assignment,
      ...(ref ? { technicianRef: ref } : { technicianRef: undefined }),
    }
  })
  return promoteBookedScheduleOnPendingCard({
    ...order,
    services: nextServices,
  })
}

function technicianRefForInspector(inspectorId: string, inspectorName?: string): string | undefined {
  const ins = inspectorRowById(inspectorId)
  if (ins?.staffNo) return technicianRefFromTechnicianNo(ins.staffNo)
  const lookup = technicianNoLookup.value
  if (lookup) {
    const byId = lookup.byId.get(String(inspectorId)) ?? (ins ? lookup.byId.get(String(ins.id)) : undefined)
    if (byId) return technicianRefFromTechnicianNo(byId)
    const name = (inspectorName ?? ins?.name ?? '').trim()
    const byName = name ? technicianNoForAssignmentName(name, lookup) : undefined
    if (byName) return technicianRefFromTechnicianNo(byName)
  }
  return undefined
}

/** 将 focus.orderList 与 ordersById 中已排程订单的最新状态对齐后的快照（回传列表页用） */
function scheduleOrderListSnapshot(): OrderCardDTO[] {
  const focus = focusPayload.value
  const byId = new Map<string, OrderCardDTO>()
  const resolveListId = (order: OrderCardDTO, fallbackId?: string): string => {
    const fromFocus = focus?.orderList?.find((row) => careOrderCardsSameOrder(row, order))?.id.trim()
    return fromFocus || (order.id ?? '').trim() || (fallbackId ?? '').trim()
  }
  const upsertStorageRow = (order: OrderCardDTO, listId: string) => {
    const id = listId.trim()
    if (!id) return
    const latest =
      ordersById.value[id] ??
      Object.values(ordersById.value).find((x) => careOrderCardsSameOrder(x, order)) ??
      order
    byId.set(id, { ...latest, id })
  }

  const fuScheduleSession = !!(
    focus &&
    focus.mode !== 'reschedule' &&
    isFollowUpScheduleFocusSession(focus)
  )

  for (const o of focus?.orderList ?? []) {
    upsertStorageRow(o, o.id.trim())
  }
  if (!fuScheduleSession) {
    for (const order of Object.values(ordersById.value)) {
      upsertStorageRow(order, resolveListId(order))
    }
  }
  const focusOrderId = (focus?.orderId ?? '').trim()
  if (focusOrderId) {
    const focusOrder = findOrder(focusOrderId)
    if (focusOrder) upsertStorageRow(focusOrder, resolveListId(focusOrder, focusOrderId))
  }

  const lookup = technicianNoLookup.value
  let list = [...byId.values()]
  list = list.map((o) => normalizeOrderCardForDisplay(o) ?? o)
  if (lookup) list = list.map((o) => applyTechnicianRefsToOrderCard(o, lookup))
  return list
}

function syncScheduleOrderListAndNotify() {
  const focus = focusPayload.value
  const fuScheduleSession = !!(
    focus &&
    focus.mode !== 'reschedule' &&
    isFollowUpScheduleFocusSession(focus)
  )
  const fullList = scheduleOrderListSnapshot()
  if (!fullList.length) return
  const poolList =
    fuScheduleSession && focus
      ? fullList.filter((o) => fuSchedulePoolLineEligible(o, focus))
      : fullList
  if (focus?.orderList) {
    if (fuScheduleSession) {
      const byId = new Map<string, OrderCardDTO>()
      for (const o of focus.orderList) {
        const id = (o.id ?? '').trim()
        if (id) byId.set(id, o)
      }
      for (const o of fullList) {
        if (!orderCardBelongsToFuPendingSchedulePool(o)) continue
        const id = (o.id ?? '').trim()
        if (id) byId.set(id, o)
      }
      focus.orderList = [...byId.values()]
    } else {
      focus.orderList = poolList
    }
  }
  emit('orders-updated', fullList)
}

/** 首次 Assign 后切 Reschedule：左栏展示已指派且可改期的单（主单 + FU） */
function expandRescheduleOrderListAfterAssign(baseList: OrderCardDTO[], focusOrderId: string): OrderCardDTO[] {
  const byId = new Map<string, OrderCardDTO>()
  for (const o of baseList) {
    byId.set(o.id, ordersById.value[o.id] ?? o)
  }
  const focusOrder = ordersById.value[focusOrderId] ?? findOrder(focusOrderId)
  if (focusOrder) byId.set((focusOrder.id ?? focusOrderId).trim(), focusOrder)
  for (const o of assignedOrdersCache.value) {
    const card = ordersById.value[o.id] ?? o
    if (!isOrderEligibleForReschedule(card)) continue
    if (!byId.has(card.id)) byId.set(card.id, card)
  }
  return [...byId.values()]
}


/** 将排程结果写回订单卡片（时间、检查员、状态），并同步 session 供列表页消费 */
function applyAssignmentToOrderCard(
  orderId: string,
  lineIndex: number,
  timeLabel: string,
  inspectorName: string,
  inspectorId: string,
  opts?: { refreshPool?: boolean },
) {
  const order = findOrder(orderId)
  if (!order) return
  if (!order.services[lineIndex]) return

  const nextOrder = applyAssignmentFieldsToOrderCard(
    order,
    lineIndex,
    timeLabel,
    inspectorName,
    inspectorId,
  )

  const next = purgeDuplicateOrdersById(nextOrder, orderId)
  next[orderId] = nextOrder
  ordersById.value = next
  upsertAssignedOrdersCache(nextOrder)

  upsertFocusOrderList(orderId, nextOrder)

  syncScheduleOrderListAndNotify()
  if (opts?.refreshPool !== false) {
    refreshUnscheduledFromFocusOrderList()
  }
}

function applyAssignmentToUi(a: OrderScheduleAssignment) {
  selectedDate.value = new Date(a.date.getFullYear(), a.date.getMonth(), a.date.getDate())
  weekAnchor.value = new Date(a.date.getFullYear(), a.date.getMonth(), a.date.getDate())
  selectedSlots.value = new Set(pickerSlotsFromAssignment(a))
  const insId = inspectorRowById(a.inspectorId)?.id ?? a.inspectorId
  if (inspectors.value.length) {
    selectInspector(insId)
  } else {
    selectedInspectorId.value = String(insId)
  }
  rebuildInspectorAssignmentRows()
}

/** 以订单行 `scheduledAt`（后端详情/列表）为准，同步会话 assignment 与网格占用 */
function preserveOccupiedSlotsOnAssignment(
  existing: OrderScheduleAssignment | undefined,
  next: OrderScheduleAssignment,
  authoritativeLabel: string | null,
): OrderScheduleAssignment {
  const authoritative = (authoritativeLabel ?? '').trim()
  if (authoritative) {
    const normalized = assignmentFromBookedLabel(next, authoritative)
    if (normalized) {
      if (existing?.pickerSlots?.length && !normalized.pickerSlots?.length) {
        normalized.pickerSlots = existing.pickerSlots
      }
      normalized.occupiedSlots = normalized.pickerSlots?.length
        ? occupiedSlotsForAssignment(normalized)
        : occupiedSlotsForAssignment(normalized, { forceRecompute: true })
      return normalized
    }
  }

  if (!existing?.occupiedSlots?.length) {
    next.occupiedSlots = occupiedSlotsForAssignment(next, { forceRecompute: true })
    if (existing?.pickerSlots?.length && !next.pickerSlots?.length) {
      next.pickerSlots = existing.pickerSlots
    }
    return next
  }

  const existingLabel = (existing.timeLabel ?? '').trim()
  const nextLabel = (next.timeLabel ?? '').trim()
  const labelsMatch =
    !!existingLabel &&
    (existingLabel === nextLabel ||
      scheduleLabelsEquivalent(existingLabel, nextLabel))
  if (labelsMatch) {
    next.occupiedSlots = existing.occupiedSlots
      .map((s) => canonicalGridSlot(s) ?? s)
      .filter((s) => slotGridIndex(s) >= 0)
    if (existing.pickerSlots?.length) {
      next.pickerSlots = existing.pickerSlots
    }
    return next
  }
  next.occupiedSlots = occupiedSlotsForAssignment(next, { forceRecompute: true })
  if (existing?.pickerSlots?.length && !next.pickerSlots?.length) {
    next.pickerSlots = existing.pickerSlots
  }
  return next
}

function syncAssignmentFromOrderCard(orderId: string, opts?: { applyUi?: boolean }) {
  if (isOrderScheduleCleared(orderId)) return
  const canonicalId = resolveCanonicalOrderId(orderId)
  const o = findOrder(canonicalId)
  if (!o) {
    syncAssignmentFromContextInspectorOrder(canonicalId, selectedInspectorId.value)
    return
  }
  const focus = focusPayload.value
  if (!isRescheduleMode.value && schedulePoolOrderIsMainPendingSchedule(o)) {
    return
  }
  if (!isRescheduleMode.value && focus && isOrderInUnscheduledPool(o, focus)) {
    const editingId = selectedOrderId.value?.trim()
    const editingOrder = editingId ? findOrder(editingId) : undefined
    if (!editingOrder || !careOrderCardsSameOrder(o, editingOrder)) return
  }
  const lineIndex = scheduleTargetLineIndexForOrder(canonicalId)
  const line = o.services[lineIndex]
  const sched = (
    isRescheduleMode.value
      ? rescheduleOriginalBookedScheduleLabelForLine(o, lineIndex)
      : resolvedBookedScheduleLabelForLine(o, lineIndex)
  )?.trim() ?? ''
  if (!sched) {
    if (
      isRescheduleMode.value &&
      careOrderIdsReferToSameOrder(selectedOrderId.value ?? '', canonicalId) &&
      scheduleContextCurrentSchedule.value
    ) {
      let inspectorId =
        assignmentForOrderId(canonicalId)?.inspectorId ?? selectedInspectorId.value
      const assignName = (line?.assignment ?? '').trim()
      if (assignName && !/pending/i.test(assignName)) {
        const found = inspectors.value.find((i) => i.name.trim() === assignName)
        if (found) inspectorId = found.id
      }
      const cs = scheduleContextCurrentSchedule.value
      const ctxAssignment = buildAssignmentFromCurrentSchedule(
        cs,
        canonicalId,
        lineIndex,
        String(inspectorId),
        { requireInspectorMatch: false },
      )
      if (ctxAssignment) {
        const ctxLabel = ctxAssignment.timeLabel?.trim() || null
        assignmentsByOrderId.value = {
          ...assignmentsByOrderId.value,
          [canonicalId]: preserveOccupiedSlotsOnAssignment(
            assignmentForOrderId(canonicalId),
            ctxAssignment,
            ctxLabel,
          ),
        }
        syncInspectorAssignmentRow(ctxAssignment)
        if (opts?.applyUi && careOrderIdsReferToSameOrder(selectedOrderId.value ?? '', canonicalId)) {
          applyAssignmentToUi(assignmentsByOrderId.value[canonicalId]!)
        }
      }
    } else {
      syncAssignmentFromContextInspectorOrder(canonicalId, selectedInspectorId.value)
    }
    return
  }

  const date = dateFromScheduleDisplay(sched)
  const slot = slotFromScheduleDisplay(sched)
  if (!date || !slot) return

  let inspectorId = assignmentForOrderId(canonicalId)?.inspectorId ?? selectedInspectorId.value
  const assignName = (line?.assignment ?? '').trim()
  if (assignName && !/pending/i.test(assignName)) {
    const found = inspectors.value.find((i) => i.name.trim() === assignName)
    if (found) inspectorId = found.id
  }

  const timeLabel = sched
  const assignment: OrderScheduleAssignment = preserveOccupiedSlotsOnAssignment(
    assignmentForOrderId(canonicalId),
    {
      orderId: canonicalId,
      lineIndex,
      inspectorId: String(inspectorId),
      date,
      slot,
      timeLabel,
    },
    isRescheduleMode.value ? sched : resolvedBookedScheduleLabelForLine(o, lineIndex),
  )
  assignmentsByOrderId.value = { ...assignmentsByOrderId.value, [canonicalId]: assignment }
  syncInspectorAssignmentRow(assignment)
  if (opts?.applyUi && careOrderIdsReferToSameOrder(selectedOrderId.value ?? '', canonicalId)) {
    applyAssignmentToUi(assignment)
  }
}

function tryHydrateAssignmentFromOrder(orderId: string) {
  syncAssignmentFromOrderCard(orderId)
}

function syncAssignmentUiForSelectedOrder() {
  const orderId = selectedOrderId.value
  if (!orderId) return
  if (isOrderScheduleCleared(orderId)) {
    clearSelectedSlots()
    rescheduleUserPickedSlots.value = false
    return
  }
  if (isFollowUpSchedulePageActive()) {
    const lineIdx = scheduleTargetLineIndexForOrder(orderId)
    syncInspectorSidebarSelectionForScheduleOrder(orderId, lineIdx)
    if (fuScheduleInspectorSidebarDrivesOrder(orderId)) {
      if (!selectedDate.value) {
        const anchor = originalAppointmentDateForOrder(orderId) ?? serviceCycleDateForOrder(orderId)
        if (anchor) navigateCalendarToDate(anchor)
      }
      rebuildInspectorAssignmentRows()
      void loadInspectorAvailability()
      return
    }
    resetSchedulePickerForUnassignedOrder(orderId)
    selectedOrderId.value = orderId
    return
  }
  if (!isUnscheduledPoolEditingAllowed()) {
    clearSelectedSlots()
    return
  }
  if (isRescheduleMode.value) {
    applyBookedOrderContext(orderId)
    return
  }
  if (orderHasActiveBookedAssignment(orderId)) {
    applyBookedOrderContext(orderId)
    return
  }
  tryHydrateAssignmentFromOrder(orderId)
  const assignment = assignmentsByOrderId.value[orderId]
  if (assignment) applyAssignmentToUi(assignment)
  else clearSelectedSlots()
}

function applyUnassignLocalSession(orderId: string): {
  lineIndex: number
  keepInspectorId: string
  keepDate: Date | null
} {
  scheduleDateLockReleased.value = true
  hideToast()

  const assignment = assignmentsByOrderId.value[orderId]
  const lineIndex =
    assignment?.lineIndex ??
    (isRescheduleMode.value ? rescheduleLineIndexForOrder(orderId) : lineIndexForOrder(orderId))
  const keepInspectorId = assignment?.inspectorId ?? selectedInspectorId.value
  const keepDate =
    assignment?.date ?? selectedDate.value ?? (orderId ? rescheduleAnchorDateForOrder(orderId) : null)

  if (assignment) removeInspectorAssignmentRow(assignment)
  const target = findOrder(orderId) ?? ({ id: orderId } as OrderCardDTO)
  const nextAssign = { ...assignmentsByOrderId.value }
  for (const [key, a] of Object.entries(nextAssign)) {
    if (key === orderId || a.orderId === orderId) {
      delete nextAssign[key]
      continue
    }
    const other =
      findOrder(key) ?? findOrder(a.orderId) ?? ({ id: key || a.orderId } as OrderCardDTO)
    if (careOrderCardsSameOrder(other, target)) delete nextAssign[key]
  }
  assignmentsByOrderId.value = nextAssign
  purgeOrderFromAssignedCaches(orderId)
  markScheduleClearedByUser(orderId)

  clearSelectedSlots()
  rescheduleUserPickedSlots.value = false
  occupiedSlotHover.value = null

  return { lineIndex, keepInspectorId, keepDate }
}

function applyUnassignUiState(
  orderId: string,
  _lineIndex: number,
  keepInspectorId: string,
  keepDate: Date | null,
): void {
  selectedOrderId.value = orderId
  const preserveInspector =
    keepInspectorId &&
    (isRescheduleMode.value || isOrderScheduleCleared(orderId))
  if (preserveInspector) {
    const insKey = String(inspectorRowById(keepInspectorId)?.id ?? keepInspectorId)
    selectedInspectorId.value = insKey
    ensureInspectorExpanded(insKey)
  } else {
    selectedInspectorId.value = ''
    expandedInspectorIds.value = new Set()
    selectedDate.value = null
  }
  if (keepDate) navigateCalendarToDate(keepDate)
  syncInspectorSidebarSelectionForFocusOrder(orderId)
  rebuildInspectorAssignmentRows()
}

function clearOrderAssignment(orderId: string) {
  const { lineIndex, keepInspectorId, keepDate } = applyUnassignLocalSession(orderId)
  clearScheduleTimeAndPersonnelOnOrderCard(orderId, lineIndex)
  applyUnassignUiState(orderId, lineIndex, keepInspectorId, keepDate)
}

const scheduleConfirmModalText = computed(() => {
  const item = unscheduled.value.find((x) => x.orderId === selectedOrderId.value)
  const inspection = inspectionLabelFromServiceName(
    item?.serviceName ?? focusPayload.value?.serviceName ?? 'Care',
  )
  const inspector = selectedInspector.value?.name?.trim() || 'the inspector'
  const pending = assignPendingSelection.value
  const orderedSlots = pending
    ? pending.slots
    : sortSlotsByGridOrder([...selectedSlots.value])
  const confirmDate = pending?.date ?? selectedDate.value
  if (confirmDate && orderedSlots.length) {
    const timeLabel = buildConfirmTimeLabelFromSlots(confirmDate, orderedSlots)
    return `The ${inspection} will be assigned to ${inspector} and scheduled for ${timeLabel}.`
  }
  return `The ${inspection} will be assigned to ${inspector}.`
})

function openAssignScheduleModalWithPendingSelection(): void {
  const date = selectedDate.value
  const orderedSlots = sortSlotsByGridOrder([...selectedSlots.value])
  if (!date || !orderedSlots.length) return
  assignPendingSelection.value = buildUserScheduleSelection(date, orderedSlots)
  showAssignScheduleModal.value = true
}

/** Schedule 排期：选时段 → 蓝块 → Assign 确认（不改 reschedule 流程） */
function onScheduleAssignClick() {
  if (!selectedOrderId.value) {
    showToast('warning', 'Please select an order first.')
    return
  }
  if (hasActiveBookedAssignment()) {
    showToast('warning', 'Please unassign the current schedule first')
    return
  }
  if (!isUnscheduledPoolEditingAllowed()) {
    showToast('warning', unscheduledPanelSelectToastMessage())
    return
  }
  if (!selectedInspectorId.value) {
    showToast('warning', 'Please select an inspector first.')
    return
  }
  if (!selectedDate.value) {
    showToast('warning', 'Please select a date first.')
    return
  }
  const orderedSlots = sortSlotsByGridOrder([...selectedSlots.value])
  if (!orderedSlots.length) {
    showToast('warning', 'Please select a time slot')
    return
  }
  if (selectedSlotsHaveScheduleConflict(selectedOrderId.value)) {
    showToast('warning', 'The selected time conflicts with an existing assignment for this inspector.')
    return
  }
  hideToast()
  openAssignScheduleModalWithPendingSelection()
}

function onRescheduleAssignClick() {
  if (hasActiveBookedAssignment()) {
    showToast('warning', 'Please unassign the current schedule first')
    return
  }
  const orderedSlots = sortSlotsByGridOrder([...selectedSlots.value])
  if (!orderedSlots.length) {
    showToast('warning', 'Please select a time slot')
    return
  }
  if (!selectedOrderId.value) {
    showToast('warning', 'Please select an order first.')
    return
  }
  if (!selectedInspectorId.value) {
    showToast('warning', 'Please select an inspector first.')
    return
  }
  if (!selectedDate.value) {
    showToast('warning', 'Please select a date first.')
    return
  }
  if (!assignEnabled.value) {
    if (selectedSlotsHaveScheduleConflict(selectedOrderId.value)) {
      showToast('warning', 'The selected time conflicts with an existing assignment for this inspector.')
    }
    return
  }
  hideToast()
  openAssignScheduleModalWithPendingSelection()
}

function onAssignClick() {
  if (isRescheduleMode.value) onRescheduleAssignClick()
  else onScheduleAssignClick()
}

function closeAssignScheduleModal() {
  if (assignSubmitting.value) return
  showAssignScheduleModal.value = false
  assignPendingSelection.value = null
}

/** context / availability：主单 target_id 优先 `orderPrimaryId`（与改前一致） */
async function resolveScheduleContextTarget(
  order: OrderCardDTO,
  lineIndex: number,
): Promise<{ target_type: 'order' | 'followup'; target_id: number } | null> {
  const line = order.services[lineIndex]
  if (!line) return null

  if (isFollowUpCareLine(line)) {
    let fuPk = line.followUpPrimaryId
    if (fuPk == null || fuPk <= 0) {
      fuPk = resolveFollowUpPrimaryIdFromOrderCard(order, line)
    }
    if (fuPk == null || fuPk <= 0) {
      const focus = focusPayload.value
      if (
        focus?.followUpPrimaryId != null &&
        focus.followUpPrimaryId > 0 &&
        careOrderIdsReferToSameOrder(focus.orderId, order.id)
      ) {
        fuPk = Math.trunc(focus.followUpPrimaryId)
      }
    }
    if (fuPk == null || fuPk <= 0) {
      const detailPk = resolveMainOrderPrimaryIdFromCard(order)
      if (detailPk != null && detailPk > 0) {
        try {
          const detail = await fetchOrderDetailOnce(detailPk)
          const fuBiz = (line.productCode ?? '').trim()
          const hit =
            fuBiz && fuBiz !== '—'
              ? detail.followups?.find((f) => (f.follow_up_id ?? '').trim() === fuBiz)
              : undefined
          const resolved =
            hit ??
            (detail.followups?.length === 1
              ? detail.followups[0]
              : detail.followups?.find((f) => resolveFollowUpRowPrimaryId(f) != null))
          const pk = resolved ? resolveFollowUpRowPrimaryId(resolved) : undefined
          if (pk != null) fuPk = pk
        } catch {
          /* 详情失败则沿用行内 id */
        }
      }
    }
    if (fuPk != null && fuPk > 0) return { target_type: 'followup', target_id: Math.trunc(fuPk) }
    return null
  }

  const pk = await resolveMainOrderPrimaryIdForAssign(order)
  if (pk != null && pk > 0) return { target_type: 'order', target_id: pk }
  return null
}

/** assign 主单 target_id：卡片缺 orderPrimaryId 时按展示 order_id 拉 list 补全 DB 主键 */
async function resolveMainOrderPrimaryIdForAssign(order: OrderCardDTO): Promise<number | undefined> {
  let pk = resolveScheduleOrderTargetId(order)
  if (pk != null) return pk

  const fromFocus = focusPayload.value?.orderList?.find((o) =>
    careOrderIdsReferToSameOrder(o.id, order.id),
  )
  if (fromFocus) {
    pk = resolveScheduleOrderTargetId(fromFocus)
    if (pk != null) return pk
  }

  const displayId = (order.id ?? '').trim()
  if (!displayId || displayId === '—') return undefined
  try {
    const raw = await postCareOrderList({
      page: 1,
      pageSize: 20,
      searchField: 'order_id',
      searchQuery: displayId,
    })
    const parsed = parseCareOrderListResponse(raw)
    const hit =
      parsed?.list.find(
        (row) => (row.id ?? '').trim() === displayId || orderIdMatchesCard(displayId, row),
      ) ?? null
    pk = hit ? resolveScheduleOrderTargetId(hit) : undefined
    if (pk != null && hit) {
      mergeOrderCardFromBackend(displayId, hit)
    }
    return pk ?? undefined
  } catch {
    return undefined
  }
}

/** assign / unassign：跟进行需可靠解析 followUpPrimaryId（含主单 order_id 拉详情） */
async function resolveScheduleAssignTarget(
  order: OrderCardDTO,
  lineIndex: number,
): Promise<{ target_type: 'order' | 'followup'; target_id: number } | null> {
  const line = order.services[lineIndex]
  if (!line) return null

  if (isFollowUpCareLine(line)) {
    let fuPk = resolveFollowUpPrimaryIdFromOrderCard(order, line)
    if (fuPk == null || fuPk <= 0) {
      const mainPk = (await resolveMainOrderPrimaryIdForAssign(order)) ?? resolveMainOrderPrimaryIdFromCard(order)
      if (mainPk != null) {
        try {
          const detail = await fetchOrderDetailOnce(mainPk)
          const fuBiz = (line.productCode ?? '').trim()
          const hit =
            fuBiz && fuBiz !== '—'
              ? detail.followups?.find((f) => (f.follow_up_id ?? '').trim() === fuBiz)
              : undefined
          const resolved =
            hit ??
            (detail.followups?.length === 1
              ? detail.followups[0]
              : detail.followups?.find((f) => resolveFollowUpRowPrimaryId(f) != null))
          const pk = resolved ? resolveFollowUpRowPrimaryId(resolved) : undefined
          if (pk != null) fuPk = pk
        } catch {
          /* 详情失败则沿用行内 id */
        }
      }
    }
    if (fuPk == null || fuPk <= 0) {
      const focus = focusPayload.value
      if (
        focus?.followUpPrimaryId != null &&
        focus.followUpPrimaryId > 0 &&
        careOrderIdsReferToSameOrder(focus.orderId, order.id)
      ) {
        fuPk = Math.trunc(focus.followUpPrimaryId)
      }
    }
    if (fuPk != null && fuPk > 0) return { target_type: 'followup', target_id: fuPk }
    return null
  }

  const pk = await resolveMainOrderPrimaryIdForAssign(order)
  if (pk != null && pk > 0) return { target_type: 'order', target_id: pk }
  return null
}


/** 跟进行 assign/unassign 写回接口快照时，主单行保持本地不变（按主单/跟进行角色，不按数组下标） */

async function syncOrderCardFromBackend(orderId: string): Promise<OrderCardDTO | null> {
  const order = findOrder(orderId)
  if (!order) return null
  const pk = await resolveMainOrderPrimaryIdForAssign(order)
  if (pk == null) return null
  try {
    const detail = await fetchOrderDetailOnce(pk)
    const card = careOrderListItemToCardDto(detail)
    return mergeOrderCardFromBackend(orderId, card)
  } catch {
    return null
  }
}

async function applyBackendOrderSnapshot(
  orderId: string,
  row: CareOrderListItem | null,
): Promise<OrderCardDTO | null> {
  if (row) return mergeOrderCardFromBackend(orderId, careOrderListItemToCardDto(row))
  return syncOrderCardFromBackend(orderId)
}

function removeOrderFromUnscheduledPool(orderId: string): void {
  unscheduled.value = unscheduled.value.filter(
    (x) => !careOrderIdsReferToSameOrder(x.orderId, orderId),
  )
}

async function confirmAssignSchedule() {
  if (assignSubmitting.value) return
  const pending = assignPendingSelection.value
  const orderedSlots = pending
    ? pending.slots
    : sortSlotsByGridOrder([...selectedSlots.value])
  const assignDate = pending?.date ?? selectedDate.value
  if (!orderedSlots.length || !assignDate || !selectedInspectorId.value || !selectedOrderId.value) {
    closeAssignScheduleModal()
    return
  }
  if (!isUnscheduledPoolEditingAllowed()) {
    showToast('warning', unscheduledPanelSelectToastMessage())
    closeAssignScheduleModal()
    return
  }

  const orderId = selectedOrderId.value
  const inspectorId = selectedInspectorId.value
  if (selectedSlotsHaveScheduleConflictForSelection(orderId, orderedSlots, inspectorId, assignDate)) {
    showToast('warning', 'The selected time conflicts with an existing assignment for this inspector.')
    closeAssignScheduleModal()
    return
  }

  if (hasActiveBookedAssignment()) {
    showToast('warning', 'Please unassign the current schedule first')
    closeAssignScheduleModal()
    return
  }

  if (isRescheduleMode.value) {
    if (!rescheduleUserPickedSlots.value) {
      showToast('warning', 'Please select a new time slot after unassigning')
      closeAssignScheduleModal()
      return
    }
  }

  const enteringRescheduleMode = isRescheduleMode.value

  const canonicalId = resolveCanonicalOrderId(selectedOrderId.value)
  let order = findOrder(canonicalId)
  if (!order) {
    upsertLocalFocusOrderAfterUnassign(canonicalId, lineIndexForOrder(canonicalId))
    order = findOrder(canonicalId)
  }
  if (!order) {
    showToast('error', 'Order not found.')
    closeAssignScheduleModal()
    return
  }

  // 与 Unassign 一致：Reschedule 用 focus.lineIndex，避免 Unassign 后误落到主单行
  const lineIndex = scheduleTargetLineIndexForOrder(canonicalId)
  const target = await resolveScheduleAssignTarget(order, lineIndex)
  if (!target) {
    showToast(
      'error',
      isFollowUpCareLine(order.services[lineIndex]!)
        ? 'Cannot assign: follow-up record id is missing.'
        : 'Cannot assign: order id is missing.',
    )
    closeAssignScheduleModal()
    return
  }

  const technicianId = technicianIdForAvailabilityApi(selectedInspectorId.value)
  if (!technicianId) {
    showToast('error', 'Invalid inspector id.')
    closeAssignScheduleModal()
    return
  }

  const assignOrderId = (order.id ?? canonicalId).trim()

  assignSubmitting.value = true
  showAssignScheduleModal.value = false
  const selection =
    pending ?? buildUserScheduleSelection(assignDate, orderedSlots)
  assignPendingSelection.value = null
  const inspectorIdForAssign = selectedInspectorId.value
  try {
    const { order: assignedOrderRow } = await postCareScheduleAssign({
      target_type: target.target_type,
      target_id: target.target_id,
      technician_id: technicianId,
      service_date: formatServiceDateYmd(selection.date),
      time_start: selection.timeStart,
      time_end: selection.timeEnd,
    })

    const prev = assignmentsByOrderId.value[assignOrderId]
    if (prev) removeInspectorAssignmentRow(prev)

    const nextCleared = new Set(scheduleClearedByUser.value)
    for (const key of [...nextCleared]) {
      if (careOrderIdsReferToSameOrder(key, assignOrderId)) nextCleared.delete(key)
    }
    scheduleClearedByUser.value = nextCleared

    if (assignedOrderRow) {
      await applyBackendOrderSnapshot(assignOrderId, assignedOrderRow)
    }
    invalidateCareOrderListCache()
    // 用户确认时段为准（如 08:00–10:00），不被接口模板时长推算（如 115min→9:55）覆盖
    applyUserScheduleSelectionToSession(assignOrderId, lineIndex, inspectorIdForAssign, selection, {
      refreshPool: false,
    })
    syncScheduleOrderListAndNotify()

    removeOrderFromUnscheduledPool(assignOrderId)

    const successMessage = enteringRescheduleMode ? 'Successfully reassigned' : 'Successfully scheduled'
    // 立即关弹框；toast 走外层全局容器，关闭后仍可见（无全局注入时退回本地 toast）
    if (showGlobalToast) {
      showGlobalToast('success', successMessage)
    } else {
      showToast('success', successMessage)
    }
    assignSubmitting.value = false
    closePage()
    return
  } catch (e) {
    clearPersistedScheduleForOrder(assignOrderId)
    showToast('error', e instanceof Error ? e.message : 'Failed to assign schedule.')
    return
  } finally {
    assignSubmitting.value = false
  }
}

/** 任意订单首次 Assign 后：整页切到 Reschedule（此后仅 Reschedule，直至 Unassign） */
function switchFocusToRescheduleModeAfterAssign(orderId: string) {
  const focus = focusPayload.value
  if (!focus) return

  const switchId = (findOrder(orderId)?.id ?? orderId).trim()
  const order = findOrder(switchId)
  const focusLine = order?.services[focus.lineIndex]
  const lineIndex =
    focusLine && isFollowUpCareLine(focusLine)
      ? focus.lineIndex
      : lineIndexForOrder(switchId)
  const line = order?.services[lineIndex]
  const updatedList = expandRescheduleOrderListAfterAssign(scheduleOrderListSnapshot(), switchId)
  const nextFocus: CareScheduleFocusPayload = {
    ...focus,
    mode: 'reschedule',
    rescheduleKind: 'proactive',
    orderId: switchId,
    lineIndex,
    orderList: updatedList,
    serviceName: line?.serviceName ?? focus.serviceName,
    address: line?.address ?? focus.address,
  }
  focusPayload.value = nextFocus
  scheduleDateLockReleased.value = true

  void loadUnscheduledList(nextFocus)
  selectedOrderId.value = switchId
  applyRescheduleOrderContext(switchId)
}

function inspectionLabelFromServiceName(serviceName: string): string {
  const s = serviceName.trim() || 'Care'
  return /inspection/i.test(s) ? s : `${s} Inspection`
}

function formatSlotLabel(slot: string): string {
  const m = slot.trim().match(/^(\d{1,2}:\d{2})(AM|PM)$/i)
  if (!m?.[1] || !m[2]) return slot
  return `${m[1]} ${m[2].toUpperCase()}`
}

/** assign API 的 time_end：一律末格起点 +30min */
function resolveAssignTimeEnd(slots: string[], _date?: Date): string {
  return timeEndFromSelectedSlots(slots)
}

/** 询问框时段：09:00 AM（设计稿补零） */
function formatClockLabelPadded(clock: string): string {
  const m = clock.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (!m?.[1] || !m[2] || !m[3]) return clock.trim()
  return `${m[1].padStart(2, '0')}:${m[2]} ${m[3].toUpperCase()}`
}

function formatUnassignScheduleWhenPhrase(date: Date, slots: string[]): string {
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const ordered = sortSlotsByGridOrder(slots)
  if (!ordered.length) return dateStr
  if (ordered.length === 1) {
    const end = formatClockLabelPadded(singleSlotDurationEndDisplayClock(ordered[0]!))
    const start = formatClockLabelPadded(formatSlotLabel(ordered[0]!))
    return `${dateStr}, from ${start} to ${end}`
  }
  const start = formatClockLabelPadded(formatSlotLabel(ordered[0]!))
  const end = formatClockLabelPadded(endDisplayClockFromSelectedSlots(ordered))
  return `${dateStr}, from ${start} to ${end}`
}

/** 排程展示文案 → Unassign 询问框时段（不再二次 format，避免时间被改写） */
function scheduleAtDisplayToUnassignWhenPhrase(display: string): string {
  const label = display.trim()
  if (!label) return ''
  const dateMatch = label.match(/([A-Za-z]{3}\s+\d{1,2},\s*\d{4})/)
  const dateStr = dateMatch?.[1] ?? ''
  const times = [...label.matchAll(/(\d{1,2}:\d{2})\s*(AM|PM)/gi)]
  if (times.length >= 2 && times[0]?.[1] && times[0]?.[2] && times[0]?.[3]) {
    const last = times[times.length - 1]!
    if (last[1] && last[2] && last[3]) {
      const start = formatClockLabelPadded(`${times[0][1]}:${times[0][2]} ${times[0][3]}`)
      const end = formatClockLabelPadded(`${last[1]}:${last[2]} ${last[3]}`)
      return dateStr ? `${dateStr}, from ${start} to ${end}` : `from ${start} to ${end}`
    }
  }
  if (times.length === 1 && times[0]?.[1] && times[0]?.[2] && times[0]?.[3]) {
    const at = formatClockLabelPadded(`${times[0][1]}:${times[0][2]} ${times[0][3]}`)
    return dateStr ? `${dateStr}, ${at}` : at
  }
  return label
}

/** Unassign 询问框时段：优先 occupiedSlots（用户点选），其次 assignment 文案 */
function resolveUnassignWhenPhrase(orderId: string): string {
  const assignment = assignmentsByOrderId.value[orderId]

  const slotSource = assignment?.occupiedSlots?.length
    ? sortSlotsByGridOrder(
        assignment.occupiedSlots
          .map((s) => canonicalGridSlot(s) ?? s)
          .filter((s) => slotGridIndex(s) >= 0),
      )
    : []
  if (slotSource.length && assignment?.date) {
    return formatUnassignScheduleWhenPhrase(assignment.date, slotSource)
  }

  if (assignment?.timeLabel?.trim()) {
    return scheduleAtDisplayToUnassignWhenPhrase(assignment.timeLabel)
  }

  if (assignment?.date) {
    return formatUnassignScheduleWhenPhrase(assignment.date, occupiedSlotsForAssignment(assignment))
  }
  return ''
}

const cancelScheduleModalTextSnapshot = ref('')

function buildCancelScheduleAssignmentLine(): string {
  const orderId = selectedOrderId.value
  const order = orderId ? findOrder(orderId) : undefined
  const lineIndex = orderId ? scheduleTargetLineIndexForOrder(orderId) : 0
  const line = order?.services[lineIndex]
  const item = unscheduled.value.find((x) => x.orderId === orderId)
  const inspection = inspectionLabelFromServiceName(
    line?.serviceName ?? item?.serviceName ?? focusPayload.value?.serviceName ?? 'Care',
  )
  const existing = orderId ? assignmentsByOrderId.value[orderId] : undefined
  const inspector =
    inspectorRowById(String(existing?.inspectorId ?? selectedInspectorId.value))
      ?.name?.trim() ||
    lineAssignmentNameForOrder(orderId ?? '') ||
    selectedInspector.value?.name?.trim() ||
    'the inspector'

  let when = orderId ? resolveUnassignWhenPhrase(orderId) : ''
  if (!when) {
    const orderedSlots = sortSlotsByGridOrder([...selectedSlots.value])
    if (selectedDate.value && orderedSlots.length) {
      when = formatUnassignScheduleWhenPhrase(selectedDate.value, orderedSlots)
    }
  }

  if (when) {
    return `The ${inspection} assigned to ${inspector} and scheduled for ${when} will be unassigned.`
  }
  return `The ${inspection} assigned to ${inspector} will be unassigned.`
}

function hideToast() {
  if (toastTimer) {
    clearTimeout(toastTimer)
    toastTimer = null
  }
  toast.value.visible = false
}

function showToast(type: 'success' | 'warning' | 'error', message: string) {
  if (toastTimer) clearTimeout(toastTimer)
  toast.value.type = type
  toast.value.message = message
  toast.value.visible = true
  const duration = type === 'warning' ? TOAST_DURATION.warning : TOAST_DURATION.standard
  toastTimer = setTimeout(() => {
    toast.value.visible = false
    toastTimer = null
  }, duration)
}

/** 排程确认/取消成功：关弹窗后展示 toast */
function showScheduleActionSuccessToast(message: string, afterToast?: () => void) {
  showAssignScheduleModal.value = false
  showCancelScheduleModal.value = false
  void nextTick(() => {
    showToast('success', message)
    if (afterToast) {
      void nextTick(afterToast)
    }
  })
}

function formatAssignmentTimeDisplay(timeLabel: string): string {
  const t = timeLabel.trim()
  if (!t) return ''
  const times = [...t.matchAll(/(\d{1,2}:\d{2})\s*(AM|PM)/gi)]
  if (!times.length) return t
  const parts = times.map((m) => `${m[1]}${m[2]!.toUpperCase()}`)
  if (parts.length === 1) return parts[0]!
  return `${parts[0]} - ${parts[parts.length - 1]}`
}

function openCancelScheduleModal() {
  hideToast()
  const customerNotice = isCustomerRequestedReschedule.value
    ? 'Please make sure the user has been informed before unassigning the previously scheduled time. '
    : ''
  cancelScheduleModalTextSnapshot.value = `${customerNotice}${buildCancelScheduleAssignmentLine()}`
  showCancelScheduleModal.value = true
}

function closeCancelScheduleModal() {
  if (cancelScheduleSubmitting.value) return
  showCancelScheduleModal.value = false
}

async function confirmCancelSchedule() {
  if (cancelScheduleSubmitting.value) return
  const orderId = selectedOrderId.value
  if (!orderId) return

  const order = findOrder(orderId)
  if (!order) {
    showToast('error', 'Order not found.')
    return
  }

  const orderBeforeUnassign: OrderCardDTO = {
    ...order,
    services: order.services.map((s) => ({ ...s })),
  }

  const lineIndex = isRescheduleMode.value
    ? rescheduleLineIndexForOrder(orderId)
    : fuPageInspectorSidebarEditableActive()
      ? effectiveSidebarLineIndexForOrder(orderId)
      : lineIndexForOrder(orderId)
  const target = await resolveScheduleAssignTarget(order, lineIndex)
  if (!target) {
    showToast(
      'error',
      isFollowUpCareLine(order.services[lineIndex]!)
        ? 'Cannot unassign: follow-up record id is missing.'
        : 'Cannot unassign: order id is missing.',
    )
    return
  }

  cancelScheduleSubmitting.value = true

  const wasRescheduleSession = isRescheduleMode.value
  const wasCustomerRequestedReschedule = isCustomerRequestedReschedule.value

  clearPersistedScheduleForOrder(orderId)

  try {
    const { order: unassignedRow } = await postCareScheduleUnassign(target)
    const { lineIndex: li, keepInspectorId, keepDate } = applyUnassignLocalSession(orderId)

    if (unassignedRow) {
      await applyBackendOrderSnapshot(orderId, unassignedRow)
    }
    invalidateCareOrderListCache()

    if (wasRescheduleSession) {
      const revertToPendingSchedule = unassignShouldRevertToPendingSchedule(
        orderBeforeUnassign,
        li,
        focusPayload.value,
        wasCustomerRequestedReschedule,
      )
      if (revertToPendingSchedule) {
        applyPendingScheduleStateOnOrderCard(orderId, li)
        scheduleContextCurrentSchedule.value = null
        applyUnassignUiState(orderId, li, keepInspectorId, keepDate)
      } else {
        applyPendingRescheduleStateOnOrderCard(orderId, li)
        scheduleContextCurrentSchedule.value = null
        applyUnassignUiState(orderId, li, keepInspectorId, keepDate)
      }
      const afterUnassign = findOrder(orderId)
      if (
        afterUnassign &&
        orderBeforeUnassign.services.some((l) => isFollowUpCareLine(l)) &&
        orderBeforeUnassign.services.some((l) => !isFollowUpCareLine(l))
      ) {
        const fixed = preserveMainLineOnMergedFollowUpCard(orderBeforeUnassign, afterUnassign)
        const canon = (fixed.id ?? orderId).trim()
        ordersById.value = { ...ordersById.value, [canon]: fixed }
        upsertFocusOrderList(canon, fixed)
      }
    } else {
      applyPendingScheduleStateOnOrderCard(orderId, li)
      scheduleContextCurrentSchedule.value = null

      const afterUnassign = findOrder(orderId)
      if (
        afterUnassign &&
        orderBeforeUnassign.services.some((l) => isFollowUpCareLine(l)) &&
        orderBeforeUnassign.services.some((l) => !isFollowUpCareLine(l))
      ) {
        const fixed = preserveMainLineOnMergedFollowUpCard(orderBeforeUnassign, afterUnassign)
        const canon = (fixed.id ?? orderId).trim()
        ordersById.value = { ...ordersById.value, [canon]: fixed }
        upsertFocusOrderList(canon, fixed)
      }

      applyUnassignUiState(orderId, li, keepInspectorId, keepDate)
    }

    purgeOrderFromAssignedCaches(orderId)

    upsertLocalFocusOrderAfterUnassign(orderId, li)
    purgeClearedOrdersFromContextInspectorRows()

    rebuildUnscheduledPoolFromFocus(focusPayload.value)
    await refreshUnscheduledFromFocusOrderList()
    rebuildInspectorAssignmentRows()
    syncScheduleOrderListAndNotify()

    await loadInspectorAvailability()

    const focus = focusPayload.value
    showScheduleActionSuccessToast('Successfully canceled', () => {
      if (focus) ensureFocusOrderSelected(focus)
      syncScheduleOrderListAndNotify()
    })
  } catch (e) {
    showToast('error', e instanceof Error ? e.message : 'Failed to cancel schedule.')
  } finally {
    cancelScheduleSubmitting.value = false
  }
}

function shiftWeek(delta: number) {
  const d = new Date(weekAnchor.value)
  d.setDate(d.getDate() + delta * 7)
  weekAnchor.value = d
  syncSelectedDateInWeekAfterNavigate()
  const focus = focusPayload.value
  if (focus?.orderId) void prefetchWeekDateCardTones(focus)
}

/** 月份工具栏：切到上/下一月，并同步日期条与 slot 网格 */
function shiftMonth(delta: number) {
  const { year, month } = resolveDisplayedCalendarMonthYear()
  let targetMonth = month + delta
  let targetYear = year
  while (targetMonth < 0) {
    targetMonth += 12
    targetYear -= 1
  }
  while (targetMonth > 11) {
    targetMonth -= 12
    targetYear += 1
  }
  syncSelectedDateAfterMonthShift(targetYear, targetMonth)
}

function restoreSchedulePageUi() {
  const focus = focusPayload.value
  if (!focus?.orderId) return
  if (focus.mode === 'reschedule') {
    void loadUnscheduledList(focus)
  } else {
    void loadScheduleContext(focus)
  }
  ensureFocusOrderSelected(focus)
  syncAssignmentUiForSelectedOrder()
}

function applyInspectorRowsFromTechnicians(list: ScheduleTechnician[]): void {
  inspectors.value = list.map((t, i) => {
    const { dotIcon, pinIcon, visualTone } = inspectorVisualsForIndex(i)
    return {
      id: t.id,
      name: t.name,
      staffNo: stripTechnicianNoPrefix(t.staffNo),
      linkedIds: t.linkedIds ?? [t.id],
      visualTone,
      orderCount: 0,
      dotIcon,
      pinIcon,
      assignments: [],
    }
  })
}

function finishInspectorSelectionAfterLoad(): void {
  if (!inspectors.value.length) return
  if (isRescheduleMode.value) {
    if (selectedOrderId.value) applyRescheduleOrderContext(selectedOrderId.value)
    return
  }
  if (isFollowUpSchedulePageActive()) {
    const orderId = selectedOrderId.value
    if (orderId) {
      resetSchedulePickerForUnassignedOrder(orderId)
      selectedOrderId.value = orderId
      syncInspectorSidebarSelectionForScheduleOrder(orderId, scheduleTargetLineIndexForOrder(orderId))
    }
    return
  }
  const orderId = selectedOrderId.value
  const assignment = orderId ? assignmentsByOrderId.value[orderId] : null
  if (assignment && !scheduleClearedByUser.value.has(orderId)) {
    selectInspector(
      String(inspectorRowById(assignment.inspectorId)?.id ?? assignment.inspectorId),
    )
    syncAssignmentUiForSelectedOrder()
    return
  }
  if (orderId) {
    if (scheduleClearedByUser.value.has(orderId)) {
      syncInspectorSidebarSelectionForFocusOrder(orderId)
    } else {
      resetSchedulePickerForUnassignedOrder(orderId)
    }
  }
}

/** 进入页 / 重置选格后：默认第一个检查员 + 从今天起当前周内首个仍有可点击时段的日期 */
async function applyDefaultInspectorAndDateIfEmpty(): Promise<void> {
  if (!inspectors.value.length) return
  if (selectedInspectorId.value?.trim()) return
  if (!selectedOrderId.value?.trim()) return

  if (isRescheduleMode.value && !isOrderScheduleCleared(selectedOrderId.value)) {
    syncInspectorSidebarSelectionForOrder(selectedOrderId.value)
    if (selectedInspectorId.value?.trim()) {
      rebuildInspectorAssignmentRows()
      await loadInspectorAvailability()
      syncScheduleSlotsForActiveInspector()
      return
    }
  }

  if (shouldUseTodayWeekForDefaultSchedulePick()) {
    anchorWeekToToday()
  }

  const first = inspectors.value[0]!
  const key = String(inspectorRowById(first.id)?.id ?? first.id)
  expandedInspectorIds.value = new Set([key])
  selectedInspectorId.value = key
  rebuildInspectorAssignmentRows()

  await loadInspectorAvailability()

  if (!selectedDate.value) {
    if (shouldUseTodayWeekForDefaultSchedulePick()) {
      const match = findFirstPickableDateFromTodayForInspector(key)
      if (match) navigateCalendarToDate(match)
    } else {
      const match = findFirstGreenOrBlueDateInWeek(weekDates.value, weekDateAvailabilityTone)
      if (match) navigateCalendarToDate(match)
    }
  }

  syncScheduleSlotsForActiveInspector()
}

function hydrateScheduleContextCurrentSchedule(
  ctx: ParsedCareScheduleContext,
  focus: CareScheduleFocusPayload,
  rawData?: Record<string, unknown>,
) {
  scheduleContextCurrentSchedule.value = null

  const order = focus.orderId?.trim() ? findOrder(focus.orderId) : undefined
  const line = order?.services[focus.lineIndex]
  const isFuTarget = line != null && isFollowUpCareLine(line)
  const isReschedule = focus.mode === 'reschedule'

  if (rawData && isReschedule && focus.orderId?.trim()) {
    const followUpId = isFuTarget ? (line!.productCode ?? '').trim() : ''
    const followUpPrimaryId = isFuTarget ? line!.followUpPrimaryId : undefined
    const derived = deriveCurrentScheduleFromInspectorOrders(rawData, {
      targetType: isFuTarget ? 'followup' : 'order',
      orderId: focus.orderId,
      followUpId: followUpId && followUpId !== '—' ? followUpId : undefined,
      followUpPrimaryId,
    })
    if (derived) {
      scheduleContextCurrentSchedule.value = derived
    }
  }

  // 跟进行改期：根级 current_schedule 常为主单时段，禁止误用；与主单改期 ctx 回退对称，用 target 回退
  if (
    !scheduleContextCurrentSchedule.value &&
    isFuTarget &&
    isReschedule &&
    rawData?.target
  ) {
    const fromTarget = currentScheduleFromContextTarget(rawData.target)
    if (fromTarget) scheduleContextCurrentSchedule.value = fromTarget
  }

  if (!scheduleContextCurrentSchedule.value && !isFuTarget && ctx.currentSchedule) {
    scheduleContextCurrentSchedule.value = ctx.currentSchedule
  }

  if (isReschedule && order && focus.orderId?.trim() && !scheduleContextCurrentSchedule.value) {
    const lineIndex = focus.lineIndex ?? 0
    const backendLabel = backendBookedScheduleLabelForLine(order, lineIndex)
    if (backendLabel) {
      const fromBackend = currentScheduleFromBookedDisplayLabel(backendLabel)
      if (fromBackend) scheduleContextCurrentSchedule.value = fromBackend
    }
  }
}

function applyParsedScheduleContext(
  ctx: ParsedCareScheduleContext,
  focus: CareScheduleFocusPayload,
  opts?: {
    assignmentsOnly?: boolean
    rawData?: Record<string, unknown>
    skipDayStatusUpdate?: boolean
  },
): void {
  if (ctx.meta) careMeta.value = ctx.meta
  // 点击单日 week-date-card 时不改日期条数据；仅打开预取 / 切周预取会更新
  if (!opts?.skipDayStatusUpdate && opts?.rawData) {
    const requestDay = contextWeekStartYmd()
    scheduleContextBusyInspectorsByDate.value = {
      ...scheduleContextBusyInspectorsByDate.value,
      [requestDay]: extractBusyInspectorIdsForDate(opts.rawData, requestDay),
    }
  }
  ingestScheduleContextInspectorOrders(opts?.rawData)
  purgeClearedOrdersFromContextInspectorRows()
  pruneLocalScheduleStateAfterContextIngest(ctx, focus)
  hydrateScheduleContextCurrentSchedule(ctx, focus, opts?.rawData)

  const mergedCards = [...ctx.pendingOrders, ...ctx.assignedOrders].filter(
    (o) => !isOrderScheduleCleared(o.id),
  )
  if (mergedCards.length) registerOrderCards(mergedCards)

  const cleared = scheduleClearedByUser.value
  assignedOrdersCache.value = ctx.assignedOrders.filter(
    (o) => !cleared.has(o.id) && !isOrderScheduleCleared(o.id),
  )

  if (ctx.weekStart) {
    const focusOrderId = focus.orderId?.trim()
    const useCycleWeek =
      focus.mode === 'reschedule' ||
      (!!focusOrderId && orderHasActiveBookedAssignment(focusOrderId))
    if (useCycleWeek) {
      const anchor = parseServiceCycleDate(ctx.weekStart)
      if (anchor) {
        const nextAnchor = startOfWeekSunday(anchor)
        const curAnchor = startOfWeekSunday(weekAnchor.value)
        if (nextAnchor.getTime() !== curAnchor.getTime()) {
          weekAnchor.value = nextAnchor
        }
      }
    }
  }

  rebuildInspectorAssignmentRows()

  if (isRescheduleMode.value && selectedOrderId.value) {
    syncAssignmentFromOrderCard(selectedOrderId.value)
    captureRescheduleEntrySnapshot(selectedOrderId.value, { force: true })
  }

  scheduleContextUnscheduledInspections.value = ctx.unscheduledInspections
  if (focus.mode !== 'reschedule') {
    applyScheduleUnscheduledFromContextOrders(focus, ctx.unscheduledInspections)
  }

  if (focus.mode === 'schedule' && focus.orderId && isOrderScheduleCleared(focus.orderId)) {
    upsertLocalFocusOrderAfterUnassign(
      focus.orderId,
      focus.lineIndex ?? lineIndexForOrder(focus.orderId),
    )
    purgeClearedOrdersFromContextInspectorRows()
    rebuildInspectorAssignmentRows()
  }

  if (opts?.assignmentsOnly) return
}

async function resolveContextTargetForFocus(
  focus: CareScheduleFocusPayload,
): Promise<{ target_type: 'order' | 'followup'; target_id: number } | null> {
  const orderId = focus.orderId?.trim()
  if (!orderId) return null
  const o =
    focus.orderList?.find((x) => (x.id ?? '').trim() === orderId) ??
    findOrder(orderId) ??
    focus.orderList?.find((x) => x.id === orderId)
  if (!o) return null
  let lineIndex = focus.lineIndex ?? lineIndexForOrder(orderId)
  if (focus.mode !== 'reschedule' && isFollowUpScheduleFocusSession(focus)) {
    lineIndex = followUpScheduleLineIndex(o)
  }
  return resolveScheduleContextTarget(o, lineIndex)
}



async function loadInspectors() {
  const { lookup, rows } = await getScheduleInspectorData()
  technicianNoLookup.value = lookup

  const fromList = dedupeTechniciansByNameAndStaffNo(rows)
  if (fromList.length) {
    applyInspectorRowsFromTechnicians(fromList)
    finishInspectorSelectionAfterLoad()
    return
  }

  try {
    careMeta.value = await postCareOrderMeta()
    const fromMeta = dedupeTechniciansByNameAndStaffNo(
      (careMeta.value?.technicians ?? [])
        .filter((t) => Number(t.value) !== 0)
        .map((t) => ({
          id: String(t.value),
          name: String(t.label),
          staffNo:
            stripTechnicianNoPrefix(lookup?.byId.get(String(t.value))) ??
            stripTechnicianNoPrefix(t.staff_no),
        })),
    )
    applyInspectorRowsFromTechnicians(fromMeta.length ? fromMeta : [])
  } catch {
    careMeta.value = null
    inspectors.value = []
  }

  finishInspectorSelectionAfterLoad()
}

function closePage() {
  if (assignSubmitting.value) return
  finalizeRescheduleUnassignOnExit()
  const list = scheduleOrderListSnapshot()
  syncScheduleOrderListAndNotify()
  if (props.embedded) {
    emit('close', list)
    return
  }
  void router.push({ name: 'Home' })
}

onMounted(() => {
  // 打开初始化期间会多次改 weekAnchor；先挡住 watch，避免 context 连打 5～6 次
  scheduleOpenInitializing = true
  schedulePageLoading.value = true
  const focus = resolveInitialFocus()
  ensureFollowUpScheduleFocusLineIndex(focus)
  focusPayload.value = focus
  if (focus.mode === 'reschedule') {
    scheduleDateLockReleased.value = true
  }
  removeLegacyScheduleAssignmentsStorage()
  loadScheduleClearedByUser()
  registerOrderCards(focus.orderList ?? [])
  ensureFocusOrderSelected(focus)
  if (focus.orderId && focus.mode !== 'reschedule') {
    if (isFollowUpScheduleFocusSession(focus)) {
      resetSchedulePickerForUnassignedOrder(focus.orderId)
      selectedOrderId.value = focus.orderId
      syncInspectorSidebarSelectionForScheduleOrder(
        focus.orderId,
        scheduleTargetLineIndexForOrder(focus.orderId),
      )
    } else if (assignmentsByOrderId.value[focus.orderId]) {
      syncAssignmentUiForSelectedOrder()
    } else {
      resetSchedulePickerForUnassignedOrder(focus.orderId)
    }
  }
  void (async () => {
    try {
      // 打开弹层：detail×1（缓存复用）→ context → inspectors → assigned list
      await hydrateFocusOrderEnrichmentOnOpen(focus)
      if (focus.orderId && focus.mode === 'reschedule') {
        clearStaleScheduleClearedIfOrderStillBooked(focus.orderId)
      }
      const ctxOk = await loadScheduleContext(focus)
      if (focus.orderId && focus.mode === 'reschedule') {
        applyRescheduleOrderContext(focus.orderId)
        captureRescheduleEntrySnapshot(focus.orderId, { force: true })
        applyRescheduleCalendarAnchor()
      }
      if (ctxOk) {
        await loadInspectors()
        if (focus.mode === 'reschedule') {
          await loadUnscheduledList(focus)
        }
        await loadAssignedOrdersCache()
      } else {
        await Promise.all([loadInspectors(), loadUnscheduledList(focus)])
        await loadAssignedOrdersCache()
      }
      pruneStaleScheduleAssignmentsForFuPendingPool()
      pruneStaleScheduleAssignmentsForUnscheduledPool()
      hydrateAllAssignmentsFromFocusOrderList()
      pruneStaleScheduleAssignmentsForFuPendingPool()
      pruneStaleScheduleAssignmentsForUnscheduledPool()
      ensureFocusOrderSelected(focus)
      syncAssignmentUiForSelectedOrder()
      finishInspectorSelectionAfterLoad()
      await applyDefaultInspectorAndDateIfEmpty()
      await finalizeScheduleOpenInitialization(focus)

      // 初始化选日后若 week_start 与已加载不一致，只补打 1 次
      const finalContextDayYmd = contextWeekStartYmd()
      if (finalContextDayYmd !== lastLoadedContextWeekStartYmd) {
        await loadScheduleContext(focus)
      }
      // 预拉本周未过期日期的 context，回显 week-date-card（有任务 → 蓝）
      await prefetchWeekDateCardTones(focus)
    } finally {
      scheduleOpenInitializing = false
      schedulePageLoading.value = false
    }
  })()
})

watch(weekAnchor, () => {
  rebuildInspectorAssignmentRows()
})

watch(
  () => props.externalRefreshNonce,
  (nonce) => {
    if (!nonce) return
    const focus = focusPayload.value
    if (!focus?.orderId) return
    void (async () => {
      invalidateCareOrderListCache()
      await loadScheduleContext(focus)
      await loadAssignedOrdersCache()
      rebuildInspectorAssignmentRows()
    })()
  },
)

watch(selectedInspectorId, (inspectorId) => {
  clearSelectedTimeSlots()
  if (inspectorId && selectedOrderId.value && focusPayload.value?.orderId) {
    void loadInspectorAvailability()
  }
})

watch(selectedDate, (date, prev) => {
  rebuildInspectorAssignmentRows()
  if (!date) return
  if (scheduleOpenInitializing) return
  const focus = focusPayload.value
  if (!focus?.orderId) return
  const dayYmd = ymdFromDate(date)
  if (prev && ymdFromDate(prev) === dayYmd) return
  if (dayYmd === lastLoadedContextWeekStartYmd) return
  const seq = ++inspectorSidebarLoadSeq
  inspectorSidebarLoading.value = true
  void refreshSchedulePageData({
    assignmentsOnly: false,
    skipDayStatusUpdate: true,
  }).finally(() => {
    if (seq === inspectorSidebarLoadSeq) {
      inspectorSidebarLoading.value = false
    }
  })
})

watch(
  () => props.initialFocus?.orderId,
  (orderId) => {
    if (!orderId || !props.initialFocus) return
    const focus = props.initialFocus
    ensureFollowUpScheduleFocusLineIndex(focus)
    focusPayload.value = focus
    ensureFocusOrderSelected(focus, { forceFocusOrder: true })
  },
)

watch(selectedOrderId, (orderId) => {
  if (!orderId) return
  // 打开初始化中：不打 detail，统一由 hydrateFocusOrderEnrichmentOnOpen 请求一次
  if (scheduleOpenInitializing) {
    if (!isRescheduleMode.value) {
      scheduleDateLockReleased.value = false
    }
    if (isRescheduleMode.value) {
      applyBookedOrderContext(orderId)
      return
    }
    if (isFollowUpSchedulePageActive()) {
      dropStaleSessionAssignmentForOrder(orderId)
      syncInspectorSidebarSelectionForScheduleOrder(
        orderId,
        scheduleTargetLineIndexForOrder(orderId),
      )
    }
    return
  }
  if (fuPageInspectorSidebarViewOnlyMode()) {
    void hydrateDurationLabelForOrder(orderId)
    return
  }
  if (!isRescheduleMode.value) {
    scheduleDateLockReleased.value = false
  }
  if (isFollowUpSchedulePageActive()) {
    dropStaleSessionAssignmentForOrder(orderId)
    void hydrateDurationLabelForOrder(orderId)
    const lineIdx = scheduleTargetLineIndexForOrder(orderId)
    syncInspectorSidebarSelectionForScheduleOrder(orderId, lineIdx)
    if (fuScheduleInspectorSidebarDrivesOrder(orderId)) {
      rebuildInspectorAssignmentRows()
      void loadInspectorAvailability()
      void hydrateServiceCycleForOrder(orderId)
      return
    }
    resetSchedulePickerForUnassignedOrder(orderId)
    selectedOrderId.value = orderId
    void hydrateServiceCycleForOrder(orderId).then(() => {
      applyServiceCycleWeekAnchorForOrder(orderId)
    })
    return
  }
  if (isRescheduleMode.value) {
    applyBookedOrderContext(orderId)
    void Promise.all([
      hydrateServiceCycleForOrder(orderId),
      hydratePreferredTimesForOrder(orderId),
      hydrateDurationLabelForOrder(orderId),
    ])
    return
  }
  dropStaleSessionAssignmentForOrder(orderId)
  void hydrateDurationLabelForOrder(orderId)
  if (!isUnscheduledPoolEditingAllowed()) {
    if (orderHasActiveBookedAssignment(orderId)) {
      applyBookedOrderContext(orderId)
      void loadInspectorAvailability()
      void hydrateServiceCycleForOrder(orderId).then(() => {
        syncAssignmentUiForSelectedOrder()
      })
      return
    }
    clearSelectedSlots()
    return
  }
  if (orderHasActiveBookedAssignment(orderId)) {
    applyBookedOrderContext(orderId)
    void loadInspectorAvailability()
    void hydrateServiceCycleForOrder(orderId).then(() => {
      syncAssignmentUiForSelectedOrder()
    })
    return
  }
  tryHydrateAssignmentFromOrder(orderId)
  const assignment = assignmentsByOrderId.value[orderId]
  if (assignment && !scheduleClearedByUser.value.has(orderId)) {
    applyAssignmentToUi(assignment)
  } else {
    resetSchedulePickerForUnassignedOrder(orderId)
  }
  void hydrateServiceCycleForOrder(orderId).then(() => {
    if (!assignmentsByOrderId.value[orderId] || scheduleClearedByUser.value.has(orderId)) {
      applyServiceCycleWeekAnchorForOrder(orderId)
    }
    syncAssignmentUiForSelectedOrder()
  })
})


function weekDateAvailabilityTone(d: Date): 'red' | 'blue' | 'green' | 'gray' {
  if (isPastCalendarDay(d)) return 'gray'
  if (!isDateSelectable(d)) return 'red'

  const ymd = ymdFromDate(d)
  const inspectorId = selectedInspectorId.value.trim()

  // 优先：预取 context 中「当前选中 Inspectors 用户」当日是否有任务
  if (inspectorId) {
    // 无可点时段（含当日剩余时段已全部过期）→ 灰掉，且须在 busyIds 短路之前判断
    if (!inspectorDateHasPickableSlot(inspectorId, d)) return 'gray'
    const busyIds = scheduleContextBusyInspectorsByDate.value[ymd]
    if (busyIds) {
      const selectedBusy = busyIds.some((id) => isSameInspectorId(id, inspectorId))
      return selectedBusy ? 'blue' : 'green'
    }
    const total = allGridSlots().length
    const occupied = collectOccupiedSlotsForInspectorDate(inspectorId, d, null).size
    if (occupied > 0) {
      if (occupied >= total) return 'red'
      return 'blue'
    }
  }

  const backendTone = inspectorAvailability.value?.dayStatusByDate[ymd]
  if (backendTone) return backendTone

  return 'green'
}

function isWeekDateCardSelectable(d: Date): boolean {
  if (!isDateSelectable(d)) return false
  return weekDateAvailabilityTone(d) !== 'gray'
}

function weekDateBarClass(d: Date): string {
  const tone = weekDateAvailabilityTone(d)
  if (tone === 'gray') return 'bar-gray'
  if (tone === 'red') return 'bar-red'
  if (tone === 'blue') return 'bar-blue'
  return 'bar-green'
}

function weekDateCardClass(d: Date): Record<string, boolean> {
  const selected = !!selectedDate.value && isSameCalendarDay(d, selectedDate.value)
  const tone = weekDateAvailabilityTone(d)
  const selectable = isWeekDateCardSelectable(d)
  return {
    'week-date-card--selected': selected,
    'week-date-card--selected-red': selected && tone === 'red',
    'week-date-card--selected-blue': selected && tone === 'blue',
    'week-date-card--selected-green': selected && tone === 'green',
    'week-date-card--past': !selectable,
    'week-date-card--disabled': !selectable && tone !== 'gray',
  }
}

const inspectorRowViews = computed((): InspectorRowView[] =>
  inspectors.value.map((ins) => ({
    inspector: ins,
    rowActive: isInspectorRowSelected(ins),
    expanded: isInspectorExpanded(ins.id),
    chevronIcon: inspectorChevronIcon(ins),
    selectable: isInspectorSelectable(ins.id),
    assignments: ins.assignments.map((a, ai) => ({
      assignment: a,
      index: ai,
      active: isInspectorAssignmentSelected(ins, a),
      formattedTime: formatAssignmentTimeDisplay(a.timeLabel),
      clickable: inspectorSidebarAssignmentClickable({
        orderId: a.orderId,
        readonly: a.readonly,
        focusOrderId: focusPayload.value?.orderId,
        idsReferToSameOrder: careOrderIdsReferToSameOrder,
        allowReadonlyViewOnly: isFuScheduleOrReschedulePageActive(),
      }),
    })),
  }))
)

const weekDayViews = computed((): WeekDateView[] =>
  weekDates.value.map((d) => {
    const barClassName = weekDateBarClass(d)
    return {
      key: d.toISOString(),
      date: d,
      cardClass: weekDateCardClass(d),
      barClass: { [barClassName]: true },
      selectable: isWeekDateCardSelectable(d),
    }
  }),
)

const slotVisualStates = computed((): Record<string, SlotVisualState> => {
  const states: Record<string, SlotVisualState> = {}
  const locked = timeSlotsLockedOutsideCycleDay.value
  const interactionAllowed = slotGridInteractionAllowed()
  const viewOnly = fuPageInspectorSidebarViewOnlyMode()

  for (const row of TIME_SLOT_GRID) {
    for (const slot of row) {
      if (!slot) continue
      const pickerDisabled = slotPickerDisabled.value.has(slot)
      const ownOrder = slotPickerCurrentOrderOriginal.value.has(slot)
      const selected = isSlotSelected(slot) && !locked
      states[slot] = {
        disabled:
          (pickerDisabled && !ownOrder) || locked || !interactionAllowed,
        buttonDisabled:
          locked || (pickerDisabled && !ownOrder) || !interactionAllowed,
        selected,
        ownOrder: ownOrder && !locked && !viewOnly,
        bookingEnd:
          slotPickerBookingEndBoundaries.value.has(slot) &&
          !pickerDisabled &&
          !ownOrder &&
          !selected &&
          !locked,
        ariaDisabled:
          ((pickerDisabled && !ownOrder) || locked || !interactionAllowed) || undefined,
        showHover:
          (pickerDisabled || slotPickerBookingEndBoundaries.value.has(slot)) && !ownOrder,
      }
    }
  }
  return states
})

onActivated(() => {
  restoreSchedulePageUi()
})

onBeforeUnmount(() => {
  finalizeRescheduleUnassignOnExit()
  syncScheduleOrderListAndNotify()
  orderDetailByPrimaryId.clear()
  orderDetailInflight.clear()
})

  return {
    embedded: computed(() => props.embedded ?? false),
    isRescheduleMode,
    showRescheduleMetaPanel,
    schedulePageLoading,
    inspectorSidebarLoading,
    closePage,
    headerTitle,
    unscheduledPanelHeading,
    unscheduled,
    selectedOrderId,
    selectUnscheduled,
    inspectorRowViews,
    toggleInspector,
    selectInspectorAssignmentOrder,
    showInspectorSchedule,
    inspectorScheduleTitle,
    calendarMonthLabel,
    weekDayViews,
    shiftWeek,
    shiftMonth,
    selectDate,
    slotVisualStates,
    pickSlot,
    onOccupiedSlotEnter,
    onOccupiedSlotLeave,
    originalScheduledTimeParts,
    preferredTimeSlotLabels,
    unassignEnabled,
    assignEnabled,
    openCancelScheduleModal,
    onAssignClick,
    occupiedSlotHover,
    showAssignScheduleModal,
    scheduleConfirmModalText,
    closeAssignScheduleModal,
    confirmAssignSchedule,
    showCancelScheduleModal,
    cancelScheduleModalTextSnapshot,
    cancelScheduleSubmitting,
    closeCancelScheduleModal,
    confirmCancelSchedule,
    toast,
  }
}
